/**
 * ElecBid — QuoteDraftReview (Phase A1: editable backend quote page)
 *
 * Route: /quote/:id  (where :id is a smart_quotes UUID)
 *
 * When the quote was generated by the new quote-brain playbook system, this
 * page renders an editable Log Book-style layout: Scope · Materials · Notes ·
 * Exclusions · Labour. Every field is editable; totals recompute live.
 *
 * Legacy quote_json shapes (no .meta.playbook_version) still render in a
 * read-only "legacy" view — kept for existing assessments that pre-date the
 * playbook system. Once we've rolled the playbook out to every service family,
 * we can rip that fallback.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/apiFetch";
import { RichText } from "../lib/richText";
import { initialThinkPref } from "../lib/chatPrefs";
import { cleanCatalogDescription } from "../lib/materialText";
import { useCellRange, rangeToText, copyToClipboard, RangePill } from "../lib/useCellRange";
import { siblingLinesFor, siblingReason, pickPatch, pickPriceSource, normDesc } from "../lib/propagatePick";
import { patchMaterialRow } from "../lib/materialRowPatch";
import { aiEstimateSource, isUnverifiedPriceSource } from "../lib/priceProvenance";
import { cleanQuoteIdentities } from "../lib/priceIdentity";
import { optionsShareBaseScope, isMirrorCloneId, optionMirrorsOut, optionMirrorOutMode, mirrorDefaultFor } from "../lib/quoteMirror";
import { buildBackendCsv, buildBackendHtml, exportFilename } from "../lib/quoteExport";
import {
  CLIPBOARD_KEY, DND_MIME, buildPayload, serializePayload, parsePayload,
  prepareOptionsForPaste, describePayload,
} from "../lib/quoteClipboard";
import FlowStrip from "./FlowStrip";
import SupplierQuoteImport from "./SupplierQuoteImport";
import { ActivityTrail, startStep, argStep, finishStep, stepNote } from "./ChatActivity";

// Jobber request gid ("gid://Jobber/Request/31356378" base64-encoded) → web URL.
function jobberRequestUrl(gid) {
  try {
    const decoded = atob(String(gid || ""));
    const m = decoded.match(/\/Request\/(\d+)$/);
    return m ? `https://secure.getjobber.com/work_requests/${m[1]}` : null;
  } catch { return null; }
}

// ── Colors (match existing app palette) ──────────────────────────────────────
const C = {
  bg:        "#0d0f12",
  surface:   "#161a20",
  surface2:  "#1e2328",
  border:    "#2a3140",
  accent:    "#a5ce3a",
  accentDim: "rgba(165,206,58,.12)",
  text:      "#e8eaf0",
  muted:     "#8892a4",
  warn:      "#f59e0b",
  danger:    "#ef4444",
  good:      "#10b981",
};

const fmt$ = n => "$" + Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
// Per-line COST helpers. These MUST mirror recomputeTotals (and its server twin
// api/quote-brain.js applyTotals) exactly — including the 2dp round of the
// PST-baked each-cost BEFORE the qty multiply — or the materials footer would
// report a different "our cost" than the margin the quote reports, and there'd
// be no way to tell which one lied. PST is real money out the door to the
// supplier, so it belongs in cost (Browning convention: PST baked into cost,
// never a separate client line).
const ourEachPst  = (m) => +((Number(m.our_cost || 0)) * (1 + (Number(m.pst_pct || 0) / 100))).toFixed(2);
const ourTotalPst = (m) => ourEachPst(m) * Number(m.qty || 1);
// EX-TAX line total — qty x the price as typed. This is what a supplier quote or
// invoice LINE reads, so it is the DEFAULT the table shows: you can compare it
// straight across without mentally stripping tax off our number first.
// 🚨 Do NOT round the each-cost before multiplying. A price carried to more
// than two decimals (a per-each figure derived from a per-1000 cable price, for
// one) got rounded to the cent first, so 1000 × $0.335 displayed as $340.00
// instead of $335.00 — a $5 error in the DEFAULT view. The PST path rounds
// first ON PURPOSE, because that is what the quote engine does and the two must
// agree to the cent; the ex-tax path has no such partner and must be exact.
const ourTotalEx  = (m) => Number(m.our_cost || 0) * Number(m.qty || 1);
// BC charges GST and PST each on the PRE-TAX amount — they do not compound.
const GST_PCT = 5;
// One key for the quote table AND the takeoff — the two screens must never
// disagree about which tax basis you are looking at.
const MAT_TAX_KEY = "elecbid-mat-tax";
// Column count of the materials table — drag handle, tick box, Qty, Unit,
// Description, Supplier, Our cost, Our total, Markup %, PST %, Client each,
// Client total, delete. Every full-width sub-row (section heading, price
// source, the save-price panels, the footer) spans this, and MaterialsFooter
// lives outside MaterialsEditor's scope, so it has to be module-level. Adding a
// column is this number plus the three cell lists — nothing else.
const MAT_COLSPAN = 13;
// What the "Our total" column and the footer DISPLAY, per the tax toggles.
// The PST path deliberately routes through ourTotalPst so a PST-in display is
// bit-identical to the quote's own margin basis; GST always rides on the ex-tax
// base. GST is an input tax credit — it comes back to us, so it NEVER touches
// profit or margin, it only makes the figure match what the invoice will read.
const ourTotalShown = (m, basis) => {
  const ex = ourTotalEx(m);
  return (basis && basis.pst ? ourTotalPst(m) : ex) + (basis && basis.gst ? ex * (GST_PCT / 100) : 0);
};
const taxBasisLabel = (basis) => (basis && basis.pst
  ? (basis.gst ? "PST + GST in" : "PST in")
  : (basis && basis.gst ? "GST in" : "ex tax"));

// Blended labour COST per van-hour ($80/vh wages, measured Jun–Jul 2026, vs the
// $305/vh client SELL). Margin subtracts labour COST, not sell. Mirrors
// src/lib/quoteBrain and api/quote-brain.js.
const LABOUR_COST_PER_VH = 80;

// Last-resort van rate for a block that has NO rate anywhere to inherit from.
// ⚠ This is NOT where the real rate lives: the standard rate reaches a draft
// through a quote_brain_memory default_adjust rule ("Browning's van rate is
// $305/hour (updated from $280)"), which only fires when the AI builds the
// draft. Anything minted in code has to inherit from the quote it is being
// added to — see addOptionUI. This constant should only ever be reached by a
// draft with no labour block at all.
// TODO(multi-tenant): a per-company rate belongs in company settings, not in
// source and not in a prompt-level memory rule.
const DEFAULT_VAN_RATE = 305;

// ── Price provenance display (the tiers from B1 + allowance/logbook) ────────
// Phase A2 stored _price_source as a string; Phase B1 stores it as an object
// with { tier, supplier_name, date_recorded, confidence, flag }. We support
// both shapes for backwards compatibility.
function priceSourceTier(ps) {
  if (!ps) return null;
  if (typeof ps === "string") return ps;            // legacy string form
  return ps.tier || null;
}
function priceSourceStyle(ps) {
  const tier = priceSourceTier(ps);
  switch (tier) {
    case "supplier_prices":  return { color: C.good };                         // 🟢
    case "supplier_quote":   return { color: "#22d3ee" };                      // 🧾 a supplier's quote for this job
    case "tech_set":         return { color: C.danger };                       // supplier amount unverified
    case "manual_pin":       return { color: "#3b82f6" };                      // 🔵
    case "memory_override":  return { color: "#a78bfa" };                      // 🟣
    case "org_price_list":   return { color: "#fbbf24" };                      // 🟡
    case "logbook_history":  return { color: "#2dd4bf" };                      // 📗 teal — from a won quote
    case "allowance":        return { color: "#94a3b8" };                      // ⚪ neutral — judgment line
    case "breaker_package":  return { color: C.warn };                         // 🤖 AI estimate — count × book avg per pole
    case "template_default": return { color: C.warn };                         // 🟠
    case "web_placeholder":  return { color: C.danger };                       // 🔴
    // legacy strings
    case "playbook_placeholder_no_match": return { color: C.warn };
    case "playbook_placeholder":          return { color: C.warn };
    default:                              return { color: C.muted };
  }
}
function priceSourceLabel(ps, m) {
  const tier = priceSourceTier(ps);
  const supplier = typeof ps === "object" ? ps.supplier_name : m?._supplier_name;
  const date     = typeof ps === "object" ? ps.date_recorded : m?._price_date;
  const flag     = typeof ps === "object" ? ps.flag : null;
  // Routine markers render neutral; ⚠ is reserved for flags that need a look
  // (band warnings, stale prices) or the glyph stops meaning anything.
  const flagText = (f) => {
    if (!f) return "";
    if (f === "matched_by_description") return " — auto-matched by description";
    if (f === "stale_price") return " — ⚠ price may be stale, confirm";
    return ` — ⚠ ${f}`;
  };
  // What EXACTLY did it match? Part # + the row's own description, up front —
  // "Q120 · SIE BREAKER, PUSH IN, 1P 20A" exposes a wrong-product match
  // (push-in vs bolt-on, 1P vs 2P) without opening the price book.
  const sku = typeof ps === "object" && ps.supplier_sku ? ` ${ps.supplier_sku}` : "";
  const matchedDesc = typeof ps === "object" && ps.supplier_description
    ? ` · "${String(ps.supplier_description).slice(0, 70)}"` : "";
  // Breaker/panel brand — makes "Q vs QO" visible so a tech spots a cross-brand
  // match without decoding the part #. ⚠ if it doesn't match the panel's brand.
  const brand = typeof ps === "object" ? ps.brand : null;
  const reqBrand = typeof ps === "object" ? ps.req_brand : null;
  const brandText = brand
    ? (reqBrand && reqBrand !== brand ? ` · ⚠ ${brand} (panel is ${reqBrand})` : ` · ${brand}`)
    : "";
  switch (tier) {
    case "supplier_prices":  return `🟢${sku}${matchedDesc}${brandText} — live price from ${supplier || "supplier"}${date ? ` (${date})` : ""}${flagText(flag)}`;
    case "supplier_quote":   return `🧾${sku}${matchedDesc} — ${supplier || "supplier"} quote${ps?.quote_number ? ` #${ps.quote_number}` : ""}${ps?.quote_date ? ` (${ps.quote_date})` : ""}${ps?.valid_until ? ` · valid to ${ps.valid_until}` : ""}${ps?.unit_note ? ` · ${ps.unit_note}` : ""}${flagText(flag)}`;
    case "tech_set":         return `🔴 ${ps?.origin === "manual" ? "Manual price" : "Price source unverified"} — supplier amount not verified${ps?.book_match ? `; book reference only: $${ps.book_match.unit_price} (${ps.book_match.supplier_name})` : ""}`;
    case "manual_pin":       return `🔵 pinned to ${supplier || "specific product"}`;
    case "memory_override":  return `🟣 memory override (learned rule)`;
    case "org_price_list":   return `🟡 from your Price List`;
    case "logbook_history":  return `📗 from won quote #${ps?.quote_num || "?"}${ps?.client_name ? ` ${ps.client_name}` : ""}${ps?.matched_line ? ` · matched "${String(ps.matched_line).slice(0, 60)}"` : ""}${flagText(flag)}`;
    case "allowance":        return `⚪ allowance — judgment line, not a SKU`;
    // Breaker package: never a supplier price — the flag carries the count and
    // the per-pole arithmetic, which is the thing the tech has to check.
    case "breaker_package":  return `🤖 ${flag || "AI estimate — breaker package, check the count"}`;
    case "template_default": return `🟠 template default — confirm/upload your price for "${(m?.description || "").slice(0, 40)}"`;
    case "web_placeholder":  return `🔴 ${ps?.origin === "unknown" ? "Price source unverified" : "AI estimate"} — confirm supplier price before sending`;
    case "playbook_placeholder_no_match": return `🟠 placeholder — no supplier match for "${(m?.description || "").slice(0, 40)}"`;
    default:                              return `↪ ${tier || "unknown source"}`;
  }
}

// ── Permit live-watch (value-bracket families only) ──────────────────────────
// Mirrors api/quote-brain.js permitOtherValue + computePermitFee so the editor
// re-derives the permit fee live as the tech tweaks the quote, and can show the
// value bracket it's landing in. Flat/amperage families (service, alt-energy)
// carry mode !== "value" and are never auto-recomputed — just displayed/edited.
function permitDeclaredValue(materials, labourTotal, valueExcludes) {
  const excl = Array.isArray(valueExcludes) && valueExcludes.length
    ? new RegExp(valueExcludes.join("|"), "i") : null;
  const matSell = (materials || []).reduce((s, m) => {
    if (excl && excl.test(String(m.description || ""))) return s;
    return s + (Number(m.client_total) || 0);
  }, 0);
  return matSell + (Number(labourTotal) || 0);
}
function permitBracketFor(brackets, value) {
  if (!Array.isArray(brackets) || !brackets.length) return null;
  const v = Number(value) || 0;
  const i = brackets.findIndex(x => x.max == null || v <= Number(x.max));
  const idx = i < 0 ? brackets.length - 1 : i;
  const b = brackets[idx];
  const min = idx === 0 ? 0 : Number(brackets[idx - 1].max) || 0;
  return { fee: Number(b.fee), plus_pct: Number(b.plus_pct) || 0, min, max: b.max == null ? null : Number(b.max) };
}
function computePermitFromBrackets(value, brackets) {
  const b = permitBracketFor(brackets, value);
  if (!b) return null;
  const v = Number(value) || 0;
  return Math.round(b.fee + (b.plus_pct ? v * b.plus_pct / 100 : 0));
}
// Mirrors quote-brain feeByAmps — picks the first tier whose max_amps covers the
// service rating. Used by the permit-type picker for service-upgrade/new-service.
function permitFeeByAmps(table, amps) {
  if (!Array.isArray(table) || !table.length) return null;
  const a = Number(amps) || 0;
  const t = table.find(x => x.max_amps == null || a <= Number(x.max_amps)) || table[table.length - 1];
  return Number(t.fee);
}
// Build the { permit_fee?, permit_basis, _permit_basis_override } patch for an
// explicitly-chosen permit category, using the fetched TSBC schedule. "value"
// leaves the fee to recomputeTotals (live value-bracket watch); flat/amps set it
// directly. Always clears the fixed-$ override so the category drives the fee.
function permitTypePatch(schedule, mode, amps) {
  if (!schedule) return null;
  const base = { _permit_fee_override: null, _permit_basis_override: { mode } };
  if (mode === "value") {
    return { ...base, permit_basis: { category: "Other — by value of work (excl. fixtures/heaters)", mode: "value", brackets: schedule.brackets || null, value_excludes: schedule.value_excludes || null } };
  }
  if (mode === "alt_energy" && schedule.alternative_energy) {
    // Explicit tech pick = "this is an alt-energy job" → default to the system-
    // only flat ($219 res). If other work is bundled, the server recomputes to
    // the with-other tier ($139 + the other permit) on the next reprice.
    const tiers = schedule.alternative_energy.tiers || {};
    const fee = Number(tiers.res_only ?? schedule.alternative_energy.fee ?? 219);
    return { ...base, permit_fee: fee, permit_basis: { category: "Alternative Energy (residential) — system only", mode: "flat", fee } };
  }
  if (mode === "silver_label" && schedule.silver_label) {
    const fee = Number(schedule.silver_label.fee);
    return { ...base, permit_fee: fee, permit_basis: { category: "Silver Label (used home/RV approval)", mode: "flat", fee } };
  }
  if (mode === "service_upgrade" && schedule.service_upgrade_by_amps) {
    const a = Number(amps) || 200;
    const fee = permitFeeByAmps(schedule.service_upgrade_by_amps, a);
    return { ...base, permit_fee: fee, permit_basis: { category: `Service Upgrade — ${a}A`, mode: "amps", amps: a, fee }, _permit_basis_override: { mode, amps: a } };
  }
  if (mode === "new_service" && schedule.new_service_by_amps) {
    const a = Number(amps) || 200;
    const fee = permitFeeByAmps(schedule.new_service_by_amps, a);
    return { ...base, permit_fee: fee, permit_basis: { category: `New Service — ${a}A`, mode: "amps", amps: a, fee }, _permit_basis_override: { mode, amps: a } };
  }
  return null;
}
// Which permit-type option the picker shows as selected for the current draft.
function permitTypeOf(draft) {
  if (!draft) return "value";
  if (draft._permit_fee_override && Number.isFinite(Number(draft._permit_fee_override.value))) return "custom";
  const bov = draft._permit_basis_override;
  if (bov && bov.mode) return bov.mode;
  const pb = draft.permit_basis || {};
  if (pb.mode === "value") return "value";
  if (pb.mode === "amps") return /new service/i.test(pb.category || "") ? "new_service" : "service_upgrade";
  if (pb.mode === "flat") return /silver/i.test(pb.category || "") ? "silver_label" : "alt_energy";
  return "value";
}

// ── Recompute totals client-side after every edit ────────────────────────────
// Mirrors api/quote-brain.js applyTotals so the UI never lies about totals
// between Save calls.
function recomputeTotals(draft, opts = {}) {
  let mat_subtotal = 0, mat_cost = 0;
  const materials = (draft.materials || []).map(m => {
    const qty       = Number(m.qty || 1);
    const our_cost  = Number(m.our_cost || 0);
    const markup    = Number(m.markup_pct || 0) / 100;
    const pstPct    = Number(m.pst_pct || 0) / 100;
    const cost_with_pst = +(our_cost * (1 + pstPct)).toFixed(2); // PST baked into cost
    const client_each   = +(cost_with_pst * (1 + markup)).toFixed(2);
    const client_total  = +(client_each * qty).toFixed(2);
    mat_subtotal += client_total;
    mat_cost     += cost_with_pst * qty;
    return { ...m, cost_with_pst, client_each, client_total, pst: 0 };
  });
  // WYSIWYG labour: charged hours are ALWAYS the Base-hours box + the visible
  // adjustment rows. Engine-side totals (e.g. calibration labour_factor) used
  // to carry phantom hours no box showed — rebuild from the parts instead.
  let labour = draft.labour || null;
  if (labour) {
    const base    = Number(labour.base_hours ?? labour.total_hours ?? labour.hours ?? 0);
    const adjSum  = (labour.adjustments || []).reduce((s, a) => s + Number(a.hours || 0), 0);
    const t_hours = +(base + adjSum).toFixed(2);
    const rate    = Number(labour.rate ?? 280);
    labour = { ...labour, base_hours: base, rate, total_hours: t_hours, total: +(t_hours * rate).toFixed(2) };
  }
  const labour_total = Number(labour?.total || 0);
  const labour_cost  = +(Number(labour?.total_hours || 0) * LABOUR_COST_PER_VH).toFixed(2);
  // 0 is a REAL fee (tech removed the permit — e.g. we hold the operating
  // permit); only null/undefined fall back to the $811 default. `||` here
  // made $0 snap back to the old fee, forcing the $1 workaround.
  // Permit fee: a tech override (typed value) or an explicit opts.permit_fee is
  // honoured verbatim; otherwise a value-bracket permit is re-derived LIVE from
  // the current declared value so tweaking the quote updates the fee + shows the
  // bracket. Flat/amperage families just carry their stored fee.
  const basis        = draft.permit_basis || {};
  const overridden   = draft._permit_fee_override && Number.isFinite(Number(draft._permit_fee_override.value));
  let liveBracket    = null;
  let permit_fee;
  if (opts.permit_fee != null) {
    permit_fee = Number(opts.permit_fee) || 0;
  } else if (!overridden && basis.mode === "value" && Array.isArray(basis.brackets) && basis.brackets.length) {
    const value = permitDeclaredValue(materials, labour_total, basis.value_excludes);
    const auto  = computePermitFromBrackets(value, basis.brackets);
    if (auto != null) {
      permit_fee  = auto;
      liveBracket = { value: Math.round(value), ...permitBracketFor(basis.brackets, value) };
    } else {
      permit_fee = Number(draft.permit_fee ?? 811) || 0;
    }
  } else {
    permit_fee = Number(draft.permit_fee ?? 811) || 0;
  }
  // Permit markup folds into the work line: the permit line stays at the real
  // fee; the markup dollars ride in the work bundle as margin (owner choice).
  // Defaults 35% so it can't be missed; an explicit tech value (including the
  // 0 the UI sends when the field is cleared) always wins.
  const pmRaw = draft.permit_markup_pct;
  const permit_markup_pct    = (pmRaw == null || pmRaw === "") ? 35 : (Number(pmRaw) || 0);
  const permit_markup_amount = +(permit_fee * permit_markup_pct / 100).toFixed(2);
  const subtotal     = +(mat_subtotal + labour_total + permit_fee + permit_markup_amount).toFixed(2);
  const grand_total  = subtotal; // pre-GST; Jobber adds 5% GST on push
  return {
    ...draft,
    materials,
    ...(labour ? { labour } : {}),
    permit_fee,
    // Refresh the live declared value + bracket for the editor readout without
    // clobbering the schedule data the server attached (brackets/excludes/mode).
    ...(liveBracket ? { permit_basis: { ...basis, value: liveBracket.value, _live_bracket: liveBracket } } : {}),
    totals: {
      materials_subtotal:   +mat_subtotal.toFixed(2),
      labour_total:         +labour_total.toFixed(2),
      permit_fee,
      permit_markup_pct,
      permit_markup_amount,
      pst:                  0, // baked into material prices (Browning convention)
      gst_note:             "GST 5% added by Jobber at checkout",
      subtotal,
      grand_total,
      _internal_cost_total:  +mat_cost.toFixed(2),
      _internal_labour_cost: labour_cost,
      _internal_margin_pct: subtotal > 0
        ? +(((subtotal - mat_cost - labour_cost - permit_fee) / subtotal) * 100).toFixed(1)
        : 0,
    },
  };
}

// ── Multi-option helpers ─────────────────────────────────────────────────────
// A multi-option quote_json carries options[] (each { key, label, recommended,
// draft }). The top-level fields mirror the recommended option for back-compat.
function isMultiDraft(d) {
  return !!(d && d.is_multi_option && Array.isArray(d.options) && d.options.length > 1);
}
// ── Sectioned (additive-phase) helpers ───────────────────────────────────────
// Sections (rough-in + finishing) are ADDITIVE, not pick-one: they edit like
// options (own scope/materials/labour per phase) but the top level is the SUM
// of the phases, not a mirror of one. Editing per phase keeps the server's
// mirror invariant true by construction, so the push always sends one firm
// Jobber line per phase.
function isSectionedDraft(d) {
  return !!(d && d.is_sectioned && !d.is_multi_option && Array.isArray(d.sections) && d.sections.length > 1);
}
// Same math as api/quote-brain.js sectionsMirrorTopLevel — sections are only
// safe to ADOPT for editing while they still sum to the top level. A legacy
// quote hand-edited on the flat top level stays flat (the server's tag-regroup
// still pushes it per-phase); rebuilding its top from stale sections here
// would discard those edits (the 2542 failure).
function sectionsMirrorClient(qj) {
  const secs = Array.isArray(qj?.sections) ? qj.sections : [];
  if (!qj?.is_sectioned || secs.length < 2) return false;
  const near = (a, b, eps) => Math.abs(Number(a || 0) - Number(b || 0)) <= eps;
  const drafts = secs.map(s => s?.draft || {});
  const sumHours = drafts.reduce((t, d) => t + Number(d.labour?.total_hours || 0), 0);
  const sumMats  = drafts.reduce((t, d) => t + Number(d.totals?.materials_subtotal || 0), 0);
  const sumCount = drafts.reduce((t, d) => t + (Array.isArray(d.materials) ? d.materials.length : 0), 0);
  return near(sumHours, qj.labour?.total_hours, 0.01)
    && near(sumMats, qj.totals?.materials_subtotal, 0.01 * secs.length)
    && sumCount === (Array.isArray(qj.materials) ? qj.materials.length : 0);
}
// Rebuild the merged top level as the SUM of the (just-edited) phases, using
// the server's line conventions: materials get mat_<phase>_<n> ids + the
// phase's `section` label, scope gets scope_<phase>_<n> ids; notes/exclusions
// concat + dedupe by text. Permit stays a TOP-LEVEL concern (section drafts
// carry permit_fee 0 from Convert's noPermit).
function mergeSectionsToTop(qj) {
  const secs = qj.sections;
  const textOf = (x) => String(x?.text ?? x ?? "").trim();
  const materials = secs.flatMap((s, si) => (s.draft?.materials || []).map((m, n) => (
    { ...m, id: `mat_${si + 1}_${n + 1}`, section: s.label || `Section ${si + 1}` }
  )));
  const scope = secs.flatMap((s, si) => (s.draft?.scope || []).map((x, n) => (
    { ...(typeof x === "string" ? { text: x } : x), id: `scope_${si + 1}_${n + 1}`, section: s.label || `Section ${si + 1}` }
  )));
  const dedupeList = (key, prefix) => {
    const seen = new Set(); const out = [];
    secs.forEach(s => (s.draft?.[key] || []).forEach(x => {
      const t = textOf(x);
      if (t && !seen.has(t.toLowerCase())) { seen.add(t.toLowerCase()); out.push({ ...(typeof x === "string" ? {} : x), id: `${prefix}_${out.length + 1}`, text: t }); }
    }));
    return out;
  };
  const hours = secs.reduce((t, s) => t + Number(s.draft?.labour?.total_hours || 0), 0);
  const rate = Number(qj.labour?.rate) || 280;
  const merged = recomputeTotals({
    ...qj,
    scope, materials,
    notes: dedupeList("notes", "note"),
    exclusions: dedupeList("exclusions", "exc"),
    labour: { ...(qj.labour || {}), base_hours: hours, adjustments: [], total_hours: hours, rate, total: +(hours * rate).toFixed(2) },
  });
  return { ...merged, is_sectioned: true, sections: qj.sections };
}
// Patch keys that live on a PHASE; everything else (permit, meta, warranty…)
// belongs to the merged top level.
const SECTION_PATCH_KEYS = new Set(["scope", "notes", "exclusions", "materials", "labour", "review_flags", "load_shedding"]);

// Recompute every option's sub-draft (and the top-level mirror = recommended).
function normalizeLoadedDraft(qj) {
  // Sectioned + still mirroring → adopt per-phase editing (_section_edit flags
  // the mode for applyPatchToDraft). A broken-mirror legacy quote stays flat.
  if (isSectionedDraft(qj) && sectionsMirrorClient(qj)) {
    const sections = qj.sections.map(s => {
      const nd = recomputeTotals(s.draft || {});
      return { ...s, draft: nd, subtotal: nd.totals?.subtotal, grand_total: nd.totals?.grand_total, margin_pct: nd.totals?._internal_margin_pct };
    });
    return { ...mergeSectionsToTop({ ...qj, sections }), _section_edit: true };
  }
  if (qj && qj.is_multi_option && Array.isArray(qj.options) && qj.options.length) {
    const options = qj.options.map(o => {
      const nd = recomputeTotals(o.draft || {});
      return { ...o, draft: nd, subtotal: nd.totals?.subtotal, grand_total: nd.totals?.grand_total, margin_pct: nd.totals?._internal_margin_pct };
    });
    const primary = options.find(o => o.recommended) || options[0];
    // Root-level provenance (packages_applied, template ids) lives on the
    // QUOTE's meta, not on any option's draft.meta — the mirror spread was
    // silently discarding it, so multi-option drafts never showed 📦 chips.
    return {
      ...qj, ...primary.draft, is_multi_option: true, options,
      meta: {
        ...primary.draft.meta,
        ...(qj.meta?.packages_applied ? { packages_applied: qj.meta.packages_applied } : {}),
        ...(qj.meta?.template_id ? { template_id: qj.meta.template_id, template_name: qj.meta.template_name } : {}),
      },
    };
  }
  return recomputeTotals(qj);
}
// Apply an edit to the active option's sub-draft (multi) or the draft (single),
// recompute its totals, and keep the top-level mirror = recommended option.
function applyPatchToDraft(fullDraft, patch, optIdx) {
  // Sectioned per-phase editing: phase-owned keys land on the ACTIVE section's
  // draft, everything else (permit, meta, warranty) on the merged top. Either
  // way the top is rebuilt as the SUM of phases, so the mirror never breaks.
  if (fullDraft && fullDraft._section_edit && isSectionedDraft(fullDraft)) {
    const i = Math.min(optIdx, fullDraft.sections.length - 1);
    const secPatch = {}, topPatch = {};
    for (const k of Object.keys(patch)) (SECTION_PATCH_KEYS.has(k) ? secPatch : topPatch)[k] = patch[k];
    let sections = fullDraft.sections;
    if (Object.keys(secPatch).length) {
      sections = fullDraft.sections.map((s, idx) => {
        if (idx !== i) return s;
        const nd = recomputeTotals({ ...s.draft, ...secPatch });
        return { ...s, draft: nd, subtotal: nd.totals?.subtotal, grand_total: nd.totals?.grand_total, margin_pct: nd.totals?._internal_margin_pct };
      });
    }
    const next = mergeSectionsToTop({ ...fullDraft, ...topPatch, sections });
    return { ...next, is_sectioned: true, sections, _section_edit: true };
  }
  if (fullDraft && fullDraft.is_multi_option && Array.isArray(fullDraft.options)) {
    const i = Math.min(optIdx, fullDraft.options.length - 1);
    const options = fullDraft.options.map((o, idx) => {
      if (idx !== i) return o;
      const nd = recomputeTotals({ ...o.draft, ...patch });
      return { ...o, draft: nd, subtotal: nd.totals?.subtotal, grand_total: nd.totals?.grand_total, margin_pct: nd.totals?._internal_margin_pct };
    });
    const primary = options.find(o => o.recommended) || options[0];
    return {
      ...fullDraft, ...primary.draft, is_multi_option: true, options,
      meta: {
        ...primary.draft.meta,
        ...(fullDraft.meta?.packages_applied ? { packages_applied: fullDraft.meta.packages_applied } : {}),
        ...(fullDraft.meta?.template_id ? { template_id: fullDraft.meta.template_id, template_name: fullDraft.meta.template_name } : {}),
      },
    };
  }
  return recomputeTotals({ ...fullDraft, ...patch });
}

// ── Cross-option mirroring ───────────────────────────────────────────────────
// Cookie-cutter multi-option quotes carry the SAME base scope in every option;
// editing one line used to mean re-editing it in every option by hand (or
// "copy to option", which appends at the bottom AND leaves the old line).
// This mirrors an edit/remove/add of a shared line into every other option
// that carries the identical line, IN PLACE. Guards: a target only mirrors an
// edit when its current value matches the source's PRE-edit value (if the
// options had already diverged on that line, it is left alone), and the whole
// thing is one Undo (the toast keeps the prior lists).
const MIRROR_SECTIONS = ["scope", "notes", "exclusions", "materials"];
const mirrorTextOf = (x) => normDesc(typeof x === "string" ? x : String(x?.text ?? ""));
const MIRROR_MAT_KEYS = ["description", "qty", "unit", "our_cost", "markup_pct", "pst_pct", "purpose", "master_product_id", "_price_source", "_pinCost", "_tech_edit"];

function mirrorPatchAcrossOptions(prevDraft, patch, srcIdx) {
  const none = { mirrored: [], restore: [], options: null };
  if (!isMultiDraft(prevDraft)) return none;
  const srcOpt = prevDraft.options[srcIdx];
  const src = srcOpt?.draft;
  if (!src) return none;
  // A freshly DUPLICATED option (or one set to local-only) never mirrors OUT:
  // its edits are the differentiation it was copied for. The Huhn #2656 copy
  // (2026-09-15) shared 100% of its as-sent source, so every edit to it came
  // through here and rewrote both sent options. It still RECEIVES mirrors.
  if (!optionMirrorsOut(srcOpt)) return none;
  const sections = Object.keys(patch).filter(k => MIRROR_SECTIONS.includes(k));
  if (!sections.length) return none;

  const mirroredIdx = new Set();
  const restore = [];
  const options = prevDraft.options.map(o => ({ ...o }));

  for (const section of sections) {
    const isMat = section === "materials";
    const keyOf = isMat ? (m) => normDesc(m?.description) : mirrorTextOf;
    const prevList = Array.isArray(src[section]) ? src[section] : [];
    const nextList = Array.isArray(patch[section]) ? patch[section] : [];
    const prevById = new Map(prevList.map(l => [l.id, l]).filter(([id]) => id));
    const nextById = new Map(nextList.map(l => [l.id, l]).filter(([id]) => id));

    const edits = [], removes = [], adds = [];
    for (const [id, p] of prevById) {
      const n = nextById.get(id);
      // A line whose id carries the mirror-clone prefix got INTO this option by
      // mirroring. Deleting it means "doesn't belong here" — a local correction
      // that must never delete the original out of the source option (the Chris
      // Gorrigan Generac quote lost its 8c#14 line exactly this way).
      if (!n) { if (!isMirrorCloneId(p.id)) removes.push(p); }
      else if (JSON.stringify(p) !== JSON.stringify(n)) edits.push({ p, n });
    }
    for (const [id, n] of nextById) if (!prevById.has(id)) adds.push({ n, at: nextList.findIndex(l => l.id === id) });
    if (!edits.length && !removes.length && !adds.length) continue;

    for (let oi = 0; oi < options.length; oi++) {
      if (oi === srcIdx) continue;
      const od = options[oi].draft || {};
      // Mirror only between cookie-cutter siblings. An add-on/allowance option
      // ("Add-On — 2 Concrete Pads") shares no base scope with the package
      // option — nothing may mirror into it OR out of it.
      if (!optionsShareBaseScope(src, od)) continue;
      let list = Array.isArray(od[section]) ? od[section].slice() : [];
      let touched = false;
      const claimed = new Set();
      const findTarget = (probe) => {
        const k = keyOf(probe);
        if (!k) return -1;
        // fast path: shared id (duplicate-created options) with matching wording
        let ti = list.findIndex((t, i) => !claimed.has(i) && t.id === probe.id && keyOf(t) === k);
        if (ti < 0) ti = list.findIndex((t, i) => !claimed.has(i) && keyOf(t) === k);
        return ti;
      };
      for (const { p, n } of edits) {
        const ti = findTarget(p);
        if (ti < 0) continue;
        const t = list[ti];
        if (isMat) {
          // copy only the keys the edit changed, and only where the target still
          // agrees with the pre-edit value (diverged targets are left alone).
          const changed = MIRROR_MAT_KEYS.filter(k => JSON.stringify(p[k]) !== JSON.stringify(n[k]));
          const applied = {};
          for (const k of changed) if (JSON.stringify(t[k]) === JSON.stringify(p[k]) || t[k] == null) applied[k] = n[k];
          if (!Object.keys(applied).length) continue;
          list[ti] = { ...t, ...applied };
        } else {
          list[ti] = { ...t, text: String(n.text ?? "") };
        }
        claimed.add(ti); touched = true;
      }
      for (const p of removes) {
        const ti = findTarget(p);
        if (ti < 0) continue;
        list.splice(ti, 1); touched = true;
      }
      for (const { n, at } of adds) {
        if (findTarget(n) >= 0) continue; // already there
        // insert after the same neighbour it follows in the source, else append
        const above = at > 0 ? nextList[at - 1] : null;
        const ai = above ? findTarget(above) : -1;
        const prefix = isMat ? "mat" : section === "scope" ? "scope" : section === "notes" ? "note" : "exc";
        const clone = { ...n, id: `${prefix}_mir_${Date.now()}_${oi}_${at}` };
        list.splice(ai >= 0 ? ai + 1 : list.length, 0, clone);
        touched = true;
      }
      if (touched) {
        restore.push({ idx: oi, section, list: od[section] });
        const nd = recomputeTotals({ ...od, [section]: list });
        options[oi] = { ...options[oi], draft: nd, subtotal: nd.totals?.subtotal, grand_total: nd.totals?.grand_total, margin_pct: nd.totals?._internal_margin_pct };
        mirroredIdx.add(oi);
      }
    }
  }
  if (!mirroredIdx.size) return none;
  return { mirrored: [...mirroredIdx].map(i => ({ idx: i, label: options[i].label || `Option ${i + 1}` })), restore, options };
}

// ── Log Book export ──────────────────────────────────────────────────────────
// Builds a TAB-separated text block matching the house format of the Google
// Sheets Log Book estimates tab, so a draft pastes straight into the grid:
//   Qty | Unit | Description | (2 spare cols) | Unit Cost | Markup
// Labour rows carry the $80/vh cost with the markup that reproduces the sell
// rate (e.g. $280 → 250%); total hours ride on the FIRST scope bullet only.
function buildLogBookText(draft) {
  const d = draft || {};
  // PST is its own sheet column — omitting it made every pasted material
  // derive our-cost/sell ~7% low in the Log Book.
  const HEADER = "Qty\tUnit\tDescription\t\t\tUnit Cost\tMarkup\tPST";
  const rate   = Number(d.labour?.rate || 280);
  const mkPct  = Math.round((rate / LABOUR_COST_PER_VH - 1) * 100); // 280 → 250
  const hours  = Number(d.labour?.total_hours || 0);
  const lines  = [];

  lines.push(String(d.meta?.tier_label || "QUOTE").toUpperCase());

  // Scope — every bullet is a labour row; the first one carries the hours.
  // No scope bullets but real hours → still emit the hours row, or the sheet
  // loses the labour entirely.
  lines.push("Scope of Work");
  lines.push(HEADER);
  const scopeRows = (d.scope || []).length ? d.scope : (hours > 0 ? [{ text: "Labour" }] : []);
  scopeRows.forEach((s, i) => {
    lines.push(`${i === 0 ? hours : ""}\th\t- ${s.text || ""}\t\t\t${LABOUR_COST_PER_VH}\t${mkPct}%\t`);
  });

  // Materials — every line including MISC/GAS allowances (they're part of the
  // house framework and counted in the app totals); only the permit is skipped
  // because it gets its own section below.
  lines.push("Materials");
  lines.push(HEADER);
  (d.materials || []).forEach(m => {
    if (/(^|\W)permit\s*(fee|allowance)?\b|^\W*tsbc\b/i.test(String(m.description || ""))) return;
    lines.push(`${m.qty ?? ""}\t${m.unit || "ea"}\t- ${m.description || ""}\t\t\t${m.our_cost ?? 0}\t${Number(m.markup_pct || 0)}%\t${Number(m.pst_pct ?? 7)}%`);
  });

  // Permit / Other — skipped entirely at $0 (tech removed it, e.g. jobs
  // covered by an operating permit), matching the Jobber push behaviour.
  const permit = d.permit_fee ?? d.totals?.permit_fee;
  if (permit != null && Number(permit) > 0) {
    // Permit markup folds into the work line in the app; in the itemized Log Book
    // sheet it has no work line, so carry it in the permit row's Markup column to
    // keep the pasted total matching the app subtotal.
    const permitMk = Number(d.permit_markup_pct ?? d.totals?.permit_markup_pct ?? 35);
    lines.push("Permit / Other");
    lines.push(HEADER);
    lines.push(`1\tea\t- TSBC electrical permit\t\t\t${permit}\t${permitMk}%\t`);
  }

  // Notes — client-visible notes first, then exclusions, one bullet per line.
  const noteLines = [...(d.notes || []), ...(d.exclusions || [])];
  if (noteLines.length) {
    lines.push("Notes");
    noteLines.forEach(n => lines.push(`- ${n.text || ""}`));
  }

  return lines.join("\n");
}

/**
 * The quote as plain, readable text — scope, materials (qty / unit /
 * description, a part # only when the line carries one), labour hours, notes
 * and exclusions. This is the copyable form: every editor row is an <input>,
 * and a browser selection cannot span inputs, so dragging across the editor
 * only ever grabbed one field. No costs — those are internal; the Log Book
 * copy is the one that carries pricing.
 *   blocks = [{ label, draft }] — one per option/phase, or a single block.
 */
function buildQuotePlainText(blocks, { quoteNumber = null, title = null } = {}) {
  const L = [];
  const head = [quoteNumber ? `Quote #${quoteNumber}` : null, title || null].filter(Boolean).join(" — ");
  if (head) L.push(head);
  const many = blocks.length > 1;
  blocks.forEach((b, bi) => {
    const d = b.draft || {};
    if (many) L.push("", `=== ${b.label || `Option ${bi + 1}`} ===`);
    else if (b.label && b.label !== title) L.push(b.label);
    const scope = (d.scope || []).filter(s => String(s?.text || "").trim());
    if (scope.length) {
      L.push("", "Scope of Work");
      let sec = null;
      scope.forEach(s => { if (s.section && s.section !== sec) { sec = s.section; L.push(`  [${sec}]`); } L.push(`- ${String(s.text).trim()}`); });
    }
    const mats = (d.materials || []).filter(m => String(m?.description || "").trim());
    if (mats.length) {
      L.push("", "Materials");
      let sec = null;
      mats.forEach(m => {
        if (m.section && m.section !== sec) { sec = m.section; L.push(`  [${sec}]`); }
        const part = m.part_number ? String(m.part_number).trim() : "";
        L.push(`- ${m.qty ?? ""} ${m.unit || "ea"} — ${String(m.description).trim()}${part ? ` [${part}]` : ""}${m.purpose ? ` (${String(m.purpose).trim()})` : ""}`.replace(/\s+/g, " "));
      });
    }
    const hours = Number(d.labour?.total_hours || 0);
    if (hours > 0) L.push("", `Labour: ${hours} h`);
    const notes = (d.notes || []).filter(n => String(n?.text || "").trim());
    if (notes.length) { L.push("", "Notes"); notes.forEach(n => L.push(`- ${String(n.text).trim()}`)); }
    const ex = (d.exclusions || []).filter(n => String(n?.text || "").trim());
    if (ex.length) { L.push("", "Exclusions"); ex.forEach(n => L.push(`- ${String(n.text).trim()}`)); }
  });
  return L.join("\n").replace(/\n{3,}/g, "\n\n").trim();
}

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export default function QuoteDraftReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [assessment, setAssessment] = useState(null); // client name + Jobber request link
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Local edit buffer — distinct from the persisted quote_json
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [optIdx, setOptIdx] = useState(0); // which option is being edited (multi-option)

  // Async action states
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [showReviewPush, setShowReviewPush] = useState(false); // the consolidated convert→push modal

  // Diff-learn flow
  const [diffs, setDiffs] = useState(null);          // null = no dialog, [] = shown but empty
  const [diffChecked, setDiffChecked] = useState({}); // index → bool

  // ── Load quote ─────────────────────────────────────────────────────────
  // rethrow: callers that surface failures themselves (the refine chat) get
  // the error back instead of the whole page swapping to the error branch.
  const loadQuote = useCallback(async ({ quiet, rethrow } = {}) => {
      try {
        if (!quiet) setLoading(true);
        const { data, error: fetchErr } = await supabase
          .from("smart_quotes")
          .select("*")
          .eq("id", id)
          .single();
        if (fetchErr) throw fetchErr;
        if (!data) throw new Error("Quote not found");
        setQuote(data);
        // The saved project_title is the truth after a reload. A title typed
        // earlier this session is held in singleTitle and re-sent on every
        // autosave — left set, it would quietly revert a title the refine chat
        // just corrected (20A → 40A) on the next save.
        setSingleTitle(null);
        if (data.assessment_id) {
          supabase.from("smart_assessments")
            .select("id,client_name,phone,email,address,jobber_request_id,jobber_client_id")
            .eq("id", data.assessment_id).single()
            .then(({ data: a }) => setAssessment(a || null))
            .catch(() => {});
        }
        // Initialize edit buffer with persisted quote_json (recomputed). For
        // multi-option quotes, recompute every option + open on the recommended.
        if (data.quote_json) {
          // cleanQuoteIdentities: a line the chat renamed to a different part
          // used to keep the OLD part's supplier/sku ("book: TECK 3C8" under an
          // in-use box) — drop that claim on load; the price is untouched.
          const norm = normalizeLoadedDraft(cleanQuoteIdentities(data.quote_json));
          setDraft(norm);
          setDirty(false);
          if (isMultiDraft(norm)) setOptIdx(Math.max(0, norm.options.findIndex(o => o.recommended)));
          else if (norm._section_edit) setOptIdx(0);
        }
      } catch (err) {
        if (rethrow) throw err;
        setError(err.message);
      } finally {
        setLoading(false);
      }
  }, [id]);

  useEffect(() => {
    (async () => {
      await loadQuote();
      // Load the server's restore points (saved before each AI refine) for
      // the ⟲ Restore picker. NOT auto-seeded into the undo stack anymore —
      // a whole-quote snapshot as silent "Undo step 1" would wipe options
      // added since (e.g. a new Option C); the picker makes the blast radius
      // explicit: just the active option, or the entire quote.
      try {
        const r = await apiFetch("/api/quote-brain", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ action: "restorePoints", quote_id: id, limit: 5 }),
        });
        const j = await r.json();
        if (r.ok && j.ok !== false && Array.isArray(j.points)) {
          setRestorePoints(j.points.filter(p => p.quote_json));
        }
      } catch { /* best-effort */ }
    })();
  }, [id]);

  // ── Multi-option: which option is being edited ─────────────────────────
  const multi = isMultiDraft(draft);
  // Signature the Second-look card uses to grey stale results — an edit to any
  // line means the last review no longer describes this quote.
  const secondLookSig = JSON.stringify(
    (isMultiDraft(draft) ? draft.options.map(o => o?.draft) : [draft]).flatMap(d =>
      (d?.materials || []).map(m => [m?.qty, m?.unit, m?.description, m?.our_cost])
    )
  );
  // Sectioned per-phase editing (adopted at load when the phases still mirror
  // the top level) — reuses the option tabs/edit paths with ADDITIVE semantics.
  const sectionedEdit = !multi && !!draft?._section_edit && isSectionedDraft(draft);
  const blocks = multi ? draft.options : sectionedEdit ? draft.sections : null;
  const activeIdx = blocks ? Math.min(optIdx, blocks.length - 1) : 0;
  const activeDraft = multi ? draft.options[activeIdx]?.draft : sectionedEdit ? draft.sections[activeIdx]?.draft : draft;

  // Other options/phases a list/lines can be copied INTO (never the one on screen).
  const copyTargets = blocks
    ? blocks.map((o, i) => ({ label: o.label || `${sectionedEdit ? "Phase" : "Option"} ${i + 1}`, idx: i })).filter(t => t.idx !== activeIdx)
    : [];

  // ── Detect playbook vs legacy shape ────────────────────────────────────
  // brain_path==="MANUAL_OPTION" is the recovery arm: options added with the ＋
  // button before the playbook_version stamp existed are playbook-shaped but
  // lack the version string, and without this the whole page renders the
  // "Legacy Quote Format" stub the moment such an option is selected. Matching
  // on the explicit marker (not a shape guess) keeps real legacy quotes routed
  // to the legacy view.
  const isPlaybook = !!(activeDraft?.meta?.playbook_version || activeDraft?.meta?.brain_path === "MANUAL_OPTION");

  // ── Undo / redo ─────────────────────────────────────────────────────────
  // Every edit snapshots the pre-change draft (coalesced: one entry per
  // 900ms typing burst so keystrokes don't flood the stack; capped at 50).
  // AI refines push a labelled entry, and on load the stack is seeded with
  // the server's latest restore point so a refine from a PREVIOUS session
  // is still one Undo away. Undo/redo mark the draft dirty → autosave
  // persists the reverted state like any other edit.
  const draftRef = useRef(null);
  const historyRef  = useRef([]); // [{ draft, label, ts }]
  const futureRef   = useRef([]);
  const lastPushRef = useRef(0);
  const [, setHistVer] = useState(0); // bump to re-render the undo/redo buttons
  const pushHistory = useCallback((label = "edit") => {
    const cur = draftRef.current;
    if (!cur) return;
    const now = Date.now();
    if (label === "edit" && now - lastPushRef.current < 900 && historyRef.current.length) {
      lastPushRef.current = now; // still in the same burst — keep its pre-state
      return;
    }
    lastPushRef.current = now;
    historyRef.current.push({ draft: cur, label, ts: now });
    if (historyRef.current.length > 50) historyRef.current.shift();
    futureRef.current = [];
    setHistVer(v => v + 1);
  }, []);
  const handleUndo = useCallback(() => {
    const entry = historyRef.current.pop();
    if (!entry || !draftRef.current) return;
    futureRef.current.push({ draft: draftRef.current, label: entry.label, ts: Date.now() });
    setDraft(entry.draft);
    setDirty(true);
    lastPushRef.current = 0; // the next edit starts a fresh history entry
    setHistVer(v => v + 1);
  }, []);
  const handleRedo = useCallback(() => {
    const entry = futureRef.current.pop();
    if (!entry || !draftRef.current) return;
    historyRef.current.push({ draft: draftRef.current, label: entry.label, ts: Date.now() });
    setDraft(entry.draft);
    setDirty(true);
    lastPushRef.current = 0;
    setHistVer(v => v + 1);
  }, []);

  // ── Restore points (server snapshots saved before each AI refine) ───────
  // The picker reverts EITHER just the active option OR the whole quote —
  // both push an undo entry first, so a restore is itself one Undo away.
  const [restorePoints, setRestorePoints] = useState([]);
  const [showRestore, setShowRestore] = useState(false);
  // Save-as-Template card (saved quote templates — the Convert-time picker's
  // library; NOT the proposal-framing saveAsTemplate in the ReviewPush modal).
  const [showSaveTpl, setShowSaveTpl] = useState(false);
  // Plain-text view of the quote (selectable — the editors' inputs are not)
  const [textView, setTextView] = useState(false);
  const [textViewAll, setTextViewAll] = useState(false);   // every option/phase, not just the active one
  const [copiedNote, setCopiedNote] = useState(null);
  const normOptLabel = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  // The snapshot option matching the CURRENT active option (key → label →
  // index). Null when it didn't exist at that point (e.g. a new Option C).
  const snapshotOptionFor = useCallback((pt, cur, ai) => {
    const snapOpts = pt?.quote_json?.options || [];
    if (!snapOpts.length || !isMultiDraft(cur)) return null;
    const curOpt = cur.options[ai];
    return snapOpts.find(o => o.key && curOpt.key && o.key === curOpt.key)
      || snapOpts.find(o => normOptLabel(o.label) === normOptLabel(curOpt.label))
      || snapOpts[ai]
      || null;
  }, []);
  const restoreWholeQuote = useCallback((pt) => {
    if (!pt?.quote_json || !draftRef.current) return;
    pushHistory(`restore whole quote — ${pt.label}`);
    setDraft(normalizeLoadedDraft(pt.quote_json));
    setDirty(true);
    setShowRestore(false);
  }, [pushHistory]);
  const restoreActiveOption = useCallback((pt) => {
    const cur = draftRef.current;
    if (!cur || !isMultiDraft(cur)) return;
    const ai = Math.min(optIdx, cur.options.length - 1);
    const snap = snapshotOptionFor(pt, cur, ai);
    if (!snap?.draft) return;
    pushHistory(`restore option: ${cur.options[ai].label}`);
    // Only the active option's backend is replaced — label/key/recommended
    // stay as they are NOW, so stars and any renames don't flip.
    const options = cur.options.map((o, i) => (i === ai ? { ...o, draft: snap.draft } : o));
    setDraft(normalizeLoadedDraft({ ...cur, options }));
    setDirty(true);
    setShowRestore(false);
  }, [optIdx, pushHistory, snapshotOptionFor]);

  // ── Edit helpers (route to the active option when multi-option) ─────────
  // ── Cross-option mirroring state ─────────────────────────────────────────
  // Default ON for multi-option quotes: edits to a line that exists identically
  // in other options apply there too, with a toast + one-click Undo (the undo
  // reverts only the mirrored copies, keeping the edit you made).
  const [mirrorOn, setMirrorOn] = useState(true);
  // …except on a quote already pushed to Jobber: its options are client-facing
  // prices, so there the mirror starts OFF (the toggle still works). Re-evaluated
  // only when the quote id or its sent-state changes, so a quiet reload never
  // overrides a toggle the user set by hand.
  const quoteSent = !mirrorDefaultFor(quote);
  useEffect(() => { setMirrorOn(!quoteSent); }, [id, quoteSent]);
  const [mirrorUndo, setMirrorUndo] = useState(null); // { labels, restore }
  // The INVERSE of mirrorUndo: an edit that would have mirrored but didn't,
  // because the toggle is off (a pushed quote starts off, by design, after the
  // Huhn #2656 clobber) or because this option is a guarded copy. Silence was
  // costing Dylan a pass through every package by hand on every edit — the
  // guard is right, being quiet about it is not. Holds the PATCH, not a
  // computed result, so the apply re-mirrors against the current draft rather
  // than grafting a stale snapshot over later edits.
  const [mirrorOffer, setMirrorOffer] = useState(null); // { labels, patch, srcIdx }
  const mirrorOfferTimerRef = useRef(null);
  const mirrorTimerRef = useRef(null);

  const undoMirror = useCallback(() => {
    const mu = mirrorUndo;
    if (!mu) return;
    setDraft(cur => {
      if (!isMultiDraft(cur)) return cur;
      const options = cur.options.map((o, i) => {
        const rs = mu.restore.filter(r => r.idx === i);
        if (!rs.length) return o;
        let d = { ...o.draft };
        for (const r of rs) d[r.section] = r.list;
        const nd = recomputeTotals(d);
        return { ...o, draft: nd, subtotal: nd.totals?.subtotal, grand_total: nd.totals?.grand_total, margin_pct: nd.totals?._internal_margin_pct };
      });
      const primary = options.find(o => o.recommended) || options[0];
      return {
        ...cur, ...primary.draft, is_multi_option: true, options,
        meta: {
          ...primary.draft.meta,
          ...(cur.meta?.packages_applied ? { packages_applied: cur.meta.packages_applied } : {}),
          ...(cur.meta?.template_id ? { template_id: cur.meta.template_id, template_name: cur.meta.template_name } : {}),
        },
      };
    });
    setMirrorUndo(null);
    setDirty(true);
  }, [mirrorUndo]);

  const update = useCallback((patch) => {
    pushHistory();
    const prev = draftRef.current;
    const srcIdx = prev && isMultiDraft(prev) ? Math.min(optIdx, prev.options.length - 1) : 0;
    // Computed whether or not the toggle is on: with it off we do not APPLY the
    // result, we offer it. mirrorPatchAcrossOptions is pure and keeps its own
    // guards (shared-base-scope, copied-from), so this is exactly "what would
    // mirror if the toggle were on" — never a way around a guard.
    const would = (prev && isMultiDraft(prev))
      ? mirrorPatchAcrossOptions(prev, patch, srcIdx)
      : { mirrored: [], restore: [], options: null };
    const mir = mirrorOn ? would : { mirrored: [], restore: [], options: null };
    setDraft(cur => {
      const base = applyPatchToDraft(cur, patch, optIdx);
      if (!mir.options || !isMultiDraft(base)) return base;
      // graft the mirrored options in (source option keeps base's version)
      const options = base.options.map((o, i) => (i !== srcIdx && mir.mirrored.some(m => m.idx === i)) ? mir.options[i] : o);
      const primary = options.find(o => o.recommended) || options[0];
      return {
        ...base, ...primary.draft, is_multi_option: true, options,
        meta: {
          ...primary.draft.meta,
          ...(base.meta?.packages_applied ? { packages_applied: base.meta.packages_applied } : {}),
          ...(base.meta?.template_id ? { template_id: base.meta.template_id, template_name: base.meta.template_name } : {}),
        },
      };
    });
    if (mir.mirrored.length) {
      if (mirrorTimerRef.current) clearTimeout(mirrorTimerRef.current);
      setMirrorUndo({ labels: mir.mirrored.map(m => m.label), restore: mir.restore });
      mirrorTimerRef.current = setTimeout(() => setMirrorUndo(null), 12000);
    } else if (!mirrorOn && would.mirrored.length) {
      if (mirrorOfferTimerRef.current) clearTimeout(mirrorOfferTimerRef.current);
      setMirrorOffer({ labels: would.mirrored.map(m => m.label), patch, srcIdx });
      mirrorOfferTimerRef.current = setTimeout(() => setMirrorOffer(null), 14000);
    }
    setDirty(true);
  }, [optIdx, pushHistory, mirrorOn]);

  // Apply the offered mirror. Re-runs against the CURRENT draft (not a snapshot
  // taken when the offer appeared) so an edit made in between is not clobbered.
  const applyMirrorOffer = useCallback(() => {
    const offer = mirrorOffer;
    if (!offer) return;
    pushHistory();
    const cur = draftRef.current;
    const mir = (cur && isMultiDraft(cur)) ? mirrorPatchAcrossOptions(cur, offer.patch, offer.srcIdx) : null;
    if (!mir || !mir.options || !mir.mirrored.length) { setMirrorOffer(null); return; }
    setDraft(base => {
      if (!isMultiDraft(base)) return base;
      const options = base.options.map((o, i) => (i !== offer.srcIdx && mir.mirrored.some(m => m.idx === i)) ? mir.options[i] : o);
      const primary = options.find(o => o.recommended) || options[0];
      return {
        ...base, ...primary.draft, is_multi_option: true, options,
        meta: {
          ...primary.draft.meta,
          ...(base.meta?.packages_applied ? { packages_applied: base.meta.packages_applied } : {}),
          ...(base.meta?.template_id ? { template_id: base.meta.template_id, template_name: base.meta.template_name } : {}),
        },
      };
    });
    setMirrorOffer(null);
    if (mirrorTimerRef.current) clearTimeout(mirrorTimerRef.current);
    setMirrorUndo({ labels: mir.mirrored.map(m => m.label), restore: mir.restore });
    mirrorTimerRef.current = setTimeout(() => setMirrorUndo(null), 12000);
    setDirty(true);
  }, [mirrorOffer, pushHistory]);

  // ── Editable headers ─────────────────────────────────────────────────────
  // The quote's headers ARE the client-facing section titles and the Jobber
  // line-item names: for a multi-option quote each option's `label`, for a
  // single-option quote the project title. Both were read-only. Renaming an
  // option only touches quote_json.options[].label (saveDraft persists it, and
  // both the client-facing render and the Jobber push read it). The single title
  // also has to reach the project_title COLUMN — tracked via singleTitle so the
  // save payload knows to send it (see the autosave/handleSave effects).
  const [singleTitle, setSingleTitle] = useState(null);
  const relabelOption = useCallback((idx, label) => {
    const v = String(label || "").trim();
    if (!v) return;
    pushHistory("rename option");
    setDraft(prev => {
      if (!isMultiDraft(prev) || !prev.options?.[idx]) return prev;
      return { ...prev, options: prev.options.map((o, i) => (i === idx ? { ...o, label: v } : o)) };
    });
    setDirty(true);
  }, [pushHistory]);
  // Rename a PHASE (sectioned quotes) — the label is the Jobber line name and
  // the section tag on every merged line, so re-merge restamps everything.
  const relabelSection = useCallback((idx, label) => {
    const v = String(label || "").trim();
    if (!v) return;
    pushHistory("rename phase");
    setDraft(prev => {
      if (!prev?._section_edit || !isSectionedDraft(prev) || !prev.sections?.[idx]) return prev;
      const sections = prev.sections.map((s, i) => (i === idx
        ? { ...s, label: v, draft: { ...s.draft, meta: { ...(s.draft?.meta || {}), tier_label: v } } }
        : s));
      return { ...mergeSectionsToTop({ ...prev, sections }), _section_edit: true };
    });
    setDirty(true);
  }, [pushHistory]);
  const setQuoteTitle = useCallback((title) => {
    const v = String(title || "").trim();
    if (!v) return;
    pushHistory("rename quote");
    setSingleTitle(v);
    setDraft(prev => ({ ...prev, meta: { ...(prev.meta || {}), tier_label: v } }));
    setDirty(true);
  }, [pushHistory]);

  // ── Option structure (add / duplicate / reorder) ─────────────────────────
  // The options array IS the pricing ladder — reordering it changes the order
  // the client sees and the Jobber lines push. Add/duplicate/reorder persist via
  // saveDraft (quote_json.options); on save the server re-mirrors the top level
  // from the recommended option, so a stale mirror here self-corrects.
  const newOptKey = () => `opt_${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`;
  const promoteToMulti = (prev) => {
    const cur = JSON.parse(JSON.stringify(prev));
    delete cur.options; delete cur.is_multi_option; delete cur._refine;
    const label = prev.meta?.tier_label || "Option A";
    return { ...prev, is_multi_option: true, options: [{ key: "opt_a", label, recommended: true, tier: label, subtotal: cur.totals?.subtotal, grand_total: cur.totals?.grand_total, margin_pct: cur.totals?._internal_margin_pct, draft: cur }] };
  };
  const reorderOptions = useCallback((from, to) => {
    if (from === to || from == null || to == null) return;
    pushHistory("reorder options");
    setDraft(prev => {
      if (!isMultiDraft(prev) || !prev.options[from] || to >= prev.options.length) return prev;
      const opts = prev.options.slice();
      const [m] = opts.splice(from, 1);
      opts.splice(to, 0, m);
      return { ...prev, options: opts };
    });
    setOptIdx(to);
    setDirty(true);
  }, [pushHistory, setOptIdx]);
  const addOptionUI = useCallback((mode, fromIdx) => {
    pushHistory(mode === "duplicate" ? "duplicate option" : "add option");
    let landed = 0;
    setDraft(prev => {
      let base = isMultiDraft(prev) ? prev : promoteToMulti(prev);
      const opts = base.options.slice();
      const srcIdx = mode === "duplicate" ? Math.min(fromIdx ?? opts.length - 1, opts.length - 1) : -1;
      // A blank option must inherit the QUOTE's own van rate. Hardcoding 280
      // priced every hand-added option $25/h under the $305 quote it was added
      // to — 9 quotes pushed to Jobber between 2026-08-20 and 2026-09-22 went
      // out carrying two different labour rates (Q2678, Q2650, Q2638, Q2629,
      // Q2632, Q2617, Q2612 …). A duplicate was always fine: it deep-copies.
      const inheritedRate = Number(base.labour?.rate)
        || Number(opts[0]?.draft?.labour?.rate)
        || DEFAULT_VAN_RATE;
      let draft;
      if (srcIdx >= 0) draft = JSON.parse(JSON.stringify(opts[srcIdx].draft));
      // playbook_version is REQUIRED — isPlaybook gates the ENTIRE editor on it,
      // so a blank option without it renders the "Legacy Quote Format" stub over
      // the whole page (the bug Dylan hit with ＋ Blank option). A manual option
      // is playbook-SHAPED, so stamping it is correct.
      else draft = { meta: { service_family: base.meta?.service_family, tier_label: "New option", playbook_version: "manual@1", brain_path: "MANUAL_OPTION" }, pricing_mode: "browning_standard", materials: [], scope: [], notes: [], exclusions: [], labour: { base_hours: 0, adjustments: [], total_hours: 0, rate: inheritedRate, total: 0 }, totals: { subtotal: 0, grand_total: 0 }, review_flags: [] };
      // Blank labels skip letters already in use — "blank, delete A, blank
      // again" would otherwise mint two "Option B"s, and the AI patch tools
      // target options BY LABEL (first match wins).
      let label;
      if (srcIdx >= 0) label = `${opts[srcIdx].label || "Option"} (copy)`;
      else {
        let n = opts.length;
        do { label = `Option ${String.fromCharCode(65 + (n % 26))}`; n++; }
        while (opts.some(o => String(o.label || "").trim().toLowerCase() === label.toLowerCase()) && n < opts.length + 26);
      }
      draft.meta = { ...(draft.meta || {}), tier_label: label };
      // copied_from marks a duplicate for the mirror guard (quoteMirror.js
      // optionMirrorOutMode): its edits stay local until mirroring is turned on
      // for it — a copy exists to DIFFER from its source, not to rewrite it.
      const nw = {
        key: newOptKey(), label, recommended: false, tier: label,
        ...(srcIdx >= 0 ? { copied_from: opts[srcIdx].key || `opt_${srcIdx}` } : {}),
        subtotal: draft.totals?.subtotal, grand_total: draft.totals?.grand_total, margin_pct: draft.totals?._internal_margin_pct, draft,
      };
      const at = srcIdx >= 0 ? srcIdx + 1 : opts.length;
      opts.splice(at, 0, nw);
      landed = at;
      return { ...base, options: opts };
    });
    setOptIdx(landed);
    setDirty(true);
  }, [pushHistory, setOptIdx]);
  const deleteOption = useCallback((idx) => {
    const opts = draftRef.current?.options || [];
    if (opts.length <= 1) return;
    const label = opts[idx]?.label || `Option ${idx + 1}`;
    if (!window.confirm(`Delete option “${label}”?\n\nUndo (↶) brings it straight back.`)) return;
    pushHistory(`delete option "${label}"`);
    setDraft(prev => {
      if (!isMultiDraft(prev) || prev.options.length <= 1) return prev;
      let rest = prev.options.filter((_, i) => i !== idx);
      if (!rest.some(o => o.recommended)) rest = rest.map((o, i) => (i === 0 ? { ...o, recommended: true } : o));
      // Down to one → collapse back to a single-option quote, exactly as the
      // server's build does (options.length > 1 ? multi : single); leaving a
      // 1-length options array shows a package selector for a quote that isn't
      // one, and the client-facing/push paths already treat it as single.
      if (rest.length === 1) {
        const only = rest[0];
        return { ...only.draft, meta: { ...(only.draft.meta || {}), tier_label: only.label } };
      }
      return { ...prev, options: rest };
    });
    setOptIdx(Math.max(0, Math.min(idx, opts.length - 2)));
    setDirty(true);
  }, [pushHistory, setOptIdx]);

  // Per-option mirror-out switch, persisted ON THE OPTION as `mirror_out` so it
  // survives save + reload. A fresh duplicate starts local-only; this is how the
  // user says "this copy is settled — mirror its shared-line edits out too".
  const setOptionMirrorOut = useCallback((idx, on) => {
    setDraft(prev => {
      if (!isMultiDraft(prev) || !prev.options[idx]) return prev;
      const options = prev.options.map((o, i) => (i === idx ? { ...o, mirror_out: !!on } : o));
      return { ...prev, options };
    });
    setDirty(true);
  }, []);
  // What the mirror would do to the NEXT edit of the active option — rendered
  // inside the options bar next to "⧉ Duplicate this", where it matters.
  const mirrorInfo = useMemo(() => {
    if (!multi) return null;
    const opt = draft.options[activeIdx];
    const mode = optionMirrorOutMode(opt);
    const targets = (mirrorOn && mode === "on")
      ? draft.options
          .map((o, i) => ({ o, i }))
          .filter(({ o, i }) => i !== activeIdx && optionsShareBaseScope(opt?.draft, o?.draft))
          .map(({ o, i }) => o.label || `Option ${String.fromCharCode(65 + i)}`)
      : [];
    return { on: mirrorOn, mode, targets, sent: quoteSent };
  }, [multi, draft, activeIdx, mirrorOn, quoteSent]);

  // Copy a whole scope / notes / exclusions list from the ACTIVE option into
  // another one. Deduped on text, so copying twice doesn't double up.
  const [copyNote, setCopyNote] = useState(null);
  const copyListToOption = useCallback((listKey, targetIdx) => {
    const prev = draftRef.current;
    const secMode = !!(prev?._section_edit && isSectionedDraft(prev));
    const blocks = secMode ? prev?.sections : isMultiDraft(prev) ? prev.options : null;
    if (!blocks || !blocks[targetIdx]) return;
    const srcIdx = Math.min(optIdx, blocks.length - 1);
    if (srcIdx === targetIdx) return;
    const srcList = blocks[srcIdx]?.draft?.[listKey] || [];
    const tgtList = blocks[targetIdx]?.draft?.[listKey] || [];
    const have = new Set(tgtList.map(x => String(x.text || "").trim().toLowerCase()));
    const prefix = listKey === "scope" ? "scope" : listKey === "notes" ? "note" : "exc";
    const stamp = Date.now();
    const adds = srcList
      .filter(x => String(x.text || "").trim() && !have.has(String(x.text).trim().toLowerCase()))
      .map((x, k) => ({ id: `${prefix}_copy_${stamp}_${k}`, text: x.text, source: "copied" }));
    const tgtLabel = blocks[targetIdx].label || `${secMode ? "Phase" : "Option"} ${targetIdx + 1}`;
    if (!adds.length) { setCopyNote({ key: listKey, text: `Nothing new to copy — “${tgtLabel}” already has these.` }); return; }
    pushHistory(`copy ${listKey} to "${tgtLabel}"`);
    setDraft(p => applyPatchToDraft(p, { [listKey]: [...tgtList, ...adds] }, targetIdx));
    setDirty(true);
    setCopyNote({ key: listKey, text: `Copied ${adds.length} line${adds.length === 1 ? "" : "s"} to “${tgtLabel}”.` });
  }, [optIdx, pushHistory]);
  useEffect(() => {
    if (!copyNote) return;
    const t = setTimeout(() => setCopyNote(null), 4000);
    return () => clearTimeout(t);
  }, [copyNote]);

  // Options bar sticks to the top while scrolling so you can tab between
  // packages without scrolling back up. Unpinnable; the choice persists.
  const [optBarPinned, setOptBarPinned] = useState(() => {
    try { return localStorage.getItem("qdr-optbar-pinned") !== "0"; } catch { return true; }
  });
  const toggleOptBarPin = useCallback(() => {
    setOptBarPinned(p => {
      const n = !p;
      try { localStorage.setItem("qdr-optbar-pinned", n ? "1" : "0"); } catch { /* private mode */ }
      return n;
    });
  }, []);

  // TSBC permit schedule (for the permit-type picker) — fetched once.
  const [permitSchedule, setPermitSchedule] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch("/api/quote-brain?action=permitSchedule");
        const j = await r.json();
        if (!cancelled && j.ok && j.schedule) setPermitSchedule(j.schedule);
      } catch { /* picker degrades to fee-only editing */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Tech picked the permit CATEGORY (auto-classifier got it wrong). Recompute
  // the fee for that category and stamp _permit_basis_override so it survives
  // refines/reprices. "By value" resumes the live value-bracket watch.
  const handlePermitType = useCallback((mode, amps) => {
    const patch = permitTypePatch(permitSchedule, mode, amps);
    if (patch) update(patch);
  }, [permitSchedule, update]);

  const updateMaterials = useCallback((newMats) => {
    update({ materials: newMats }); // rides the mirror-aware path
  }, [update]);

  const updateLabour = useCallback((patch) => {
    pushHistory();
    setDraft(prev => {
      const tgt = isMultiDraft(prev) ? prev.options[Math.min(optIdx, prev.options.length - 1)].draft
        : (prev?._section_edit && isSectionedDraft(prev)) ? prev.sections[Math.min(optIdx, prev.sections.length - 1)].draft
        : prev;
      const labour = { ...(tgt.labour || {}), ...patch };
      labour.total = +(Number(labour.total_hours || 0) * Number(labour.rate || 0)).toFixed(2);
      return applyPatchToDraft(prev, { labour }, optIdx);
    });
    setDirty(true);
  }, [optIdx, pushHistory]);

  // Copy selected material lines into ANOTHER option (fresh ids so the two
  // options' lines stay independent). Undo-able like any edit.
  const copyMaterialsToOption = useCallback((rows, targetIdx) => {
    pushHistory(`copy ${rows.length} line(s) to another option`);
    setDraft(prev => {
      const blocks = (prev?._section_edit && isSectionedDraft(prev)) ? prev.sections : isMultiDraft(prev) ? prev.options : null;
      if (!blocks || !blocks[targetIdx]) return prev;
      const tgtMats = blocks[targetIdx].draft?.materials || [];
      const now = Date.now();
      const copies = rows.map((m, k) => ({ ...m, id: `mat_copy_${now}_${k}` }));
      return applyPatchToDraft(prev, { materials: [...tgtMats, ...copies] }, targetIdx);
    });
    setDirty(true);
  }, [pushHistory]);

  // Apply a reviewed supplier quote (SupplierQuoteImport). Every option/phase
  // gets its own proposals, so each block's lines are patched directly rather
  // than riding the mirror path. Lines are re-found by id (index + wording for
  // a legacy line with no id) in the CURRENT draft, so an edit made while the
  // review was open is never overwritten by a stale row. One undo step.
  const applySupplierQuote = useCallback((items, label) => {
    const byBlock = new Map();
    for (const it of items || []) {
      if (!byBlock.has(it.block_idx)) byBlock.set(it.block_idx, []);
      byBlock.get(it.block_idx).push(it);
    }
    const patchAll = (d) => {
      const bl = (d?._section_edit && isSectionedDraft(d)) ? d.sections : isMultiDraft(d) ? d.options : null;
      const lists = bl ? bl.map((b) => b.draft?.materials || []) : [d?.materials || []];
      let next = d, applied = 0;
      for (const [bi, its] of byBlock) {
        const mats = lists[bi];
        if (!mats) continue;
        let hit = 0;
        const patched = mats.map((m, li) => {
          if (!m) return m;
          const it = its.find((x) => (x.line_id != null ? String(m.id) === String(x.line_id) : li === x.line_idx)
            && normDesc(m.description) === normDesc(x.description));
          if (!it) return m;
          hit++;
          // _pinCost/_repriceMe are one refine's bookkeeping. Left on the line,
          // the next refine's pin guard relabels this price "tech_set".
          const { _pinCost, _repriceMe, ...rest } = m;
          const oldPs = m._price_source && typeof m._price_source === "object" ? m._price_source : {};
          const keepLink = it.patch.master_product_id !== null;
          return {
            ...rest, ...it.patch,
            _price_source: { ...it.patch._price_source, master_product_id: keepLink ? (oldPs.master_product_id || m.master_product_id || null) : null },
          };
        });
        if (hit) { next = applyPatchToDraft(next, { materials: patched }, bi); applied += hit; }
      }
      return { next, applied };
    };
    const cur = draftRef.current;
    const { applied } = cur ? patchAll(cur) : { applied: 0 };
    if (!applied) return { applied: 0, missed: (items || []).length };
    pushHistory(`apply supplier quote — ${label}`);
    setDraft((prev) => patchAll(prev).next);
    setDirty(true);
    return { applied, missed: (items || []).length - applied };
  }, [pushHistory]);

  // ── Auto-save ──────────────────────────────────────────────────────────
  // Any edit persists after 2.5s idle — a refine, navigation, or crash can't
  // eat work. Silent (no learning prompt, no spinner); the manual Save button
  // still runs the "remember these changes?" flow. (draftRef is declared with
  // the undo stack above — both need it.)
  useEffect(() => { draftRef.current = draft; }, [draft]);
  // Stale-save guard: every save carries this mount's session id + a per-request
  // seq, and the server drops any save that arrives AFTER a newer one from the
  // same session. Without it a slow or duplicated request landing late silently
  // reverts newer edits (the "ElecBid switched my hours back" bug).
  const saveSessionRef  = useRef(`qdr_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`);
  const saveSeqRef      = useRef(0);
  const autoSaveAbortRef = useRef(null);
  // While a refine is in flight (10-60s), a stale autosave snapshot landing
  // AFTER the refine's PATCH would silently revert the refined quote_json —
  // the refine chat raises this flag around its whole request+reload cycle.
  const refineInFlightRef = useRef(false);
  useEffect(() => {
    if (!dirty || !draft || saving) return;
    const snapshot = draft;
    const timer = setTimeout(async () => {
      if (refineInFlightRef.current) return;
      // A newer edit outranks an in-flight save: abort the old request (the seq
      // guard neutralises it even if the server already received it). The old
      // busy-flag skipped the NEWER save instead, dropping the latest state.
      autoSaveAbortRef.current?.abort();
      const ctrl = new AbortController();
      autoSaveAbortRef.current = ctrl;
      try {
        const r = await apiFetch("/api/quote-brain", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          // singleTitle is sent only when the header was actually renamed, so an
          // ordinary autosave never rewrites project_title.
          body:    JSON.stringify({ action: "saveDraft", quote_id: id, quote_json: snapshot, save_session: saveSessionRef.current, save_seq: ++saveSeqRef.current, ...(singleTitle ? { project_title: singleTitle } : {}) }),
          signal:  ctrl.signal,
        });
        const j = await r.json();
        if (r.ok && j.ok !== false) {
          setSavedAt(new Date());
          // Only mark clean if no further edits happened while saving
          if (draftRef.current === snapshot) setDirty(false);
        }
      } catch { /* silent — aborted or failed; the manual Save button surfaces errors */ }
      finally { if (autoSaveAbortRef.current === ctrl) autoSaveAbortRef.current = null; }
    }, 2500);
    return () => clearTimeout(timer);
  }, [draft, dirty, saving, id, singleTitle]);

  // ── Cross-quote option clipboard ───────────────────────────────────────
  // Copy an option (or a whole quote's worth) out of THIS quote and paste it
  // into another one open in a second window. localStorage is the transport:
  // it is same-origin, survives a reload, needs no permission prompt, and its
  // `storage` event fires in the OTHER tabs — so a copy here lights up the
  // paste button over there without either window reloading. The payload is
  // small enough for this to be comfortable: measured over the last 120
  // quotes, one option is 11 KB at the median and 32 KB at the worst, and a
  // whole multi-option quote tops out at 122 KB, against a ~5 MB quota.
  const [clipPayload, setClipPayload] = useState(null);
  const readClip = useCallback(() => {
    try {
      const raw = localStorage.getItem(CLIPBOARD_KEY);
      if (!raw) return null;
      const r = parsePayload(raw);
      return r.ok ? r.payload : null;
    } catch { return null; }   // private window, or storage disabled
  }, []);
  useEffect(() => {
    setClipPayload(readClip());
    // Fires only in OTHER tabs, which is exactly the cross-window case.
    const onStorage = (e) => { if (!e.key || e.key === CLIPBOARD_KEY) setClipPayload(readClip()); };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [readClip]);

  const clipMeta = useCallback(() => ({
    id, jobber_quote_number: quote?.jobber_quote_number,
    client_name: quote?.client_name || assessment?.client_name || "",
    project_title: quote?.project_title || "",
    address: assessment?.address || "",
  }), [id, quote, assessment]);

  // Put option(s) on the clipboard. `which` is an index, or "all".
  const copyOptions = useCallback((which, opts = {}) => {
    const cur = draftRef.current;
    if (!cur) return null;
    const all = isMultiDraft(cur)
      ? cur.options
      : [{ label: cur.meta?.tier_label || "Quote", recommended: true, draft: cur }];
    const picked = which === "all" ? all : [all[which]].filter(Boolean);
    if (!picked.length) return null;
    const payload = buildPayload({
      quote: clipMeta(),
      options: picked.map((o) => ({ label: o.label, recommended: o.recommended, draft: o.draft })),
      nowIso: new Date().toISOString(),
    });
    const raw = serializePayload(payload);
    // A dragstart only needs the string for the dataTransfer — it must not
    // toast, and it must not overwrite the clipboard for a drag the user may
    // abandon mid-air.
    if (opts.forDrag) return raw;
    try {
      localStorage.setItem(CLIPBOARD_KEY, raw);
      setClipPayload(payload);          // `storage` never fires in the writing tab
    } catch {
      setCopiedNote("Couldn't copy — browser storage is full or blocked");
      setTimeout(() => setCopiedNote(null), 5000);
      return null;
    }
    // Also to the system clipboard, so the same copy survives a different
    // browser profile and can be pasted into a message to look at. Best
    // effort — a denied permission must not fail the copy that already worked.
    try { navigator.clipboard?.writeText(raw); } catch { /* not fatal */ }
    setCopiedNote(picked.length > 1 ? `✓ ${picked.length} options copied — paste them in another quote` : `✓ "${picked[0].label}" copied — paste it in another quote`);
    setTimeout(() => setCopiedNote(null), 4000);
    return raw;
  }, [clipMeta]);

  // Land clipboard option(s) in THIS quote. `raw` lets a cross-window DROP
  // paste what it is carrying rather than whatever localStorage holds.
  const pasteOptions = useCallback(async (raw) => {
    let parsed = raw ? parsePayload(raw) : (clipPayload ? { ok: true, payload: clipPayload } : { ok: false, error: "nothing copied yet" });
    // localStorage is per-PROFILE. A copy made in another browser profile (or
    // on another machine, pasted in as text) only exists on the system
    // clipboard — so fall back to it. readText needs a user gesture, which a
    // button click is, and may be denied, which is not an error worth shouting.
    if (!parsed.ok && !raw) {
      try {
        const sys = await navigator.clipboard?.readText();
        if (sys) parsed = parsePayload(sys);
      } catch { /* denied, or Firefox, which has no readText */ }
    }
    if (!parsed.ok) {
      setCopiedNote(parsed.error);
      setTimeout(() => setCopiedNote(null), 6000);
      return;
    }
    const cur = draftRef.current;
    const prepared = prepareOptionsForPaste(parsed.payload, {
      labels: isMultiDraft(cur)
        ? cur.options.map((o) => o.label)
        // A single-option quote is promoted on paste, and promoteToMulti names
        // the existing option `meta.tier_label || "Option A"`. Passing [] here
        // let a paste mint that same label twice — and findOptIdx resolves
        // options BY LABEL, first match wins, so every later refine would edit
        // the wrong package.
        : [cur?.meta?.tier_label || "Option A"],
      rate: Number(cur?.labour?.rate) || Number(cur?.options?.[0]?.draft?.labour?.rate) || DEFAULT_VAN_RATE,
      permitMarkupPct: (() => {
        const v = isMultiDraft(cur) ? cur.options[0]?.draft?.permit_markup_pct : cur?.permit_markup_pct;
        return v == null || v === "" ? 35 : Number(v) || 0;
      })(),
      quoteId: id,
      isSectioned: isSectionedDraft(cur),
      mintKey: () => newOptKey(),
      nowIso: new Date().toISOString(),
    });
    if (!prepared.ok) {
      setCopiedNote(prepared.error);
      setTimeout(() => setCopiedNote(null), 9000);
      return;
    }
    pushHistory(prepared.options.length > 1 ? "paste options" : "paste option");
    let landed = 0;
    setDraft((prev) => {
      const base = isMultiDraft(prev) ? prev : promoteToMulti(prev);
      const opts = base.options.slice();
      // Totals are recomputed per option so a pasted option shows its real
      // price at the new rate immediately, instead of the source quote's.
      const priced = prepared.options.map((o) => {
        const nd = recomputeTotals(o.draft);
        return { ...o, draft: nd, subtotal: nd.totals?.subtotal, grand_total: nd.totals?.grand_total, margin_pct: nd.totals?._internal_margin_pct };
      });
      landed = opts.length;
      return { ...base, options: [...opts, ...priced] };
    });
    setOptIdx(landed);
    setDirty(true);
    const what = prepared.options.length > 1 ? `${prepared.options.length} options` : `“${prepared.options[0].label}”`;
    setCopiedNote(`✓ Pasted ${what}${prepared.notes.length ? ` · ${prepared.notes.join(" · ")}` : ""}`);
    setTimeout(() => setCopiedNote(null), 9000);
  }, [clipPayload, id, pushHistory, setOptIdx]);

  // ── Exports ────────────────────────────────────────────────────────────
  // The BACKEND quote — our costs, markups, labour and margin — as a file.
  // "📋 Log Book" copies one option as TSV shaped for that spreadsheet; these
  // two take the WHOLE quote (every option) out as a real file you can send to
  // the bookkeeper, keep with the job, or pivot.
  //
  // Both read the SAVED draft through the shared pure builders in
  // lib/quoteExport.js, which repeat the editor's own money math
  // (cost×PST → ×markup → ×qty) rather than inventing their own, and print the
  // app's saved grand total beside the recomputed one so a stale draft shows up
  // instead of being quietly papered over.
  const exportMeta = useCallback(() => ({
    client: quote?.client_name || assessment?.client_name || "",
    quote_number: quote?.jobber_quote_number || "",
    title: quote?.project_title || draft?.meta?.tier_label || "Backend quote",
    address: assessment?.address || "",
  }), [quote, assessment, draft]);

  const exportCsv = useCallback(() => {
    try {
      const csv = buildBackendCsv(draftRef.current, exportMeta());
      // A BOM so Excel opens UTF-8 correctly — without it "90°" and the em
      // dashes in our descriptions arrive as mojibake on Windows.
      const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = exportFilename(exportMeta(), "csv");
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setCopiedNote("✓ Spreadsheet downloaded");
      setTimeout(() => setCopiedNote(null), 2500);
    } catch (e) {
      setCopiedNote("Export failed: " + e.message);
      setTimeout(() => setCopiedNote(null), 5000);
    }
  }, [exportMeta]);

  const exportPdf = useCallback(() => {
    try {
      const html = buildBackendHtml(draftRef.current, exportMeta());
      const w = window.open("", "_blank");
      if (!w) {
        setCopiedNote("Popup blocked — allow popups to make the PDF");
        setTimeout(() => setCopiedNote(null), 5000);
        return;
      }
      w.document.write(html);
      w.document.close();
    } catch (e) {
      setCopiedNote("PDF failed: " + e.message);
      setTimeout(() => setCopiedNote(null), 5000);
    }
  }, [exportMeta]);

  // ── Save / client-facing / copy ────────────────────────────────────────
  async function handleSave({ promptLearning = true } = {}) {
    setSaving(true);
    // A manual save outranks any in-flight autosave — abort it so the two can't
    // land out of order server-side.
    autoSaveAbortRef.current?.abort();
    const snapshot = draft;
    try {
      const r = await apiFetch("/api/quote-brain", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ action: "saveDraft", quote_id: id, quote_json: snapshot, save_session: saveSessionRef.current, save_seq: ++saveSeqRef.current, ...(singleTitle ? { project_title: singleTitle } : {}) }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      // Adopt the server echo only if nothing changed while the request was in
      // flight — a slow response overwriting newer edits (and clearing dirty on
      // them) is exactly the stale-hours resurrection bug.
      if (draftRef.current === snapshot) {
        // normalizeLoadedDraft (not bare recomputeTotals) so a sectioned quote
        // re-adopts per-phase editing from the server echo.
        setDraft(normalizeLoadedDraft(j.quote));
        setDirty(false);
      }
      setSavedAt(new Date());

      // After every save, ask the API what changed vs autofill. If we have any
      // diffs, show the "remember which?" dialog (per Dylan's "ask before learning" pick).
      if (promptLearning) {
        try {
          const dr = await apiFetch("/api/quote-brain", {
            method:  "POST",
            headers: { "Content-Type": "application/json" },
            body:    JSON.stringify({ action: "computeDiff", quote_id: id }),
          });
          const dj = await dr.json();
          if (dj?.ok && Array.isArray(dj.diffs) && dj.diffs.length) {
            setDiffs(dj.diffs);
            setDiffChecked({}); // user must opt-in per row
          }
        } catch (e) {
          // Non-fatal — diff is a bonus
          console.warn("computeDiff failed:", e.message);
        }
      }
    } catch (e) {
      alert("Save failed: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  async function handleConfirmLearning() {
    if (!diffs) return;
    const chosen = diffs.filter((_, i) => diffChecked[i]);
    if (chosen.length === 0) { setDiffs(null); return; }
    setSaving(true);
    try {
      // Bulk createRule calls. apiFetch — teach actions are owner-gated against
      // the Supabase JWT server-side, so a token-less fetch 401s.
      const results = await Promise.allSettled(chosen.map(d =>
        apiFetch("/api/quote-brain", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "createRule", ...d.suggested_rule }),
        }).then(r => r.json())
      ));
      const failed = results.filter(r => r.status === "rejected" || r.value?.ok === false);
      if (failed.length) {
        alert(`${chosen.length - failed.length} rule(s) saved · ${failed.length} failed`);
      } else {
        // Quiet success — no popup, the dialog closing IS the confirmation
      }
    } finally {
      setDiffs(null);
      setDiffChecked({});
      setSaving(false);
    }
  }

  // Save the quote AND flip marked_for_learning so the batch detection engine
  // includes this diff next time analyzePatterns runs. Different from the per-
  // quote "computeDiff → which to remember?" prompt above — that creates rules
  // from THIS quote alone; Save & Learn feeds the cross-quote pattern detector.
  const [markedForLearning, setMarkedForLearning] = useState(false);
  async function handleSaveAndLearn() {
    await handleSave({ promptLearning: false });
    try {
      const r = await apiFetch("/api/quote-brain", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ action: "markForLearning", quote_id: id, marked: true }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      setMarkedForLearning(true);
    } catch (e) {
      alert("Save & Learn: saved OK, but couldn't mark for batch learning: " + e.message);
    }
  }

  // Re-price the red "AI guess" lines against the CURRENT price book — for when
  // a price was saved/bought AFTER this quote line was generated (adding a book
  // price doesn't retro-reprice an already-open line).
  const [repriceBusy, setRepriceBusy] = useState(false);
  const [repriceMsg, setRepriceMsg] = useState(null);
  // Preferred supplier ("we usually only hit one or two") — re-prices ALL lines
  // from that supplier's book rows; only confident matches switch over, the
  // rest keep their current supplier/price. The choice sticks on the quote so
  // refines keep preferring it, and the takeoff inherits it after convert.
  const [prefSupplier, setPrefSupplier] = useState("");
  const [prefSuppliers, setPrefSuppliers] = useState([]);
  const [prefBusy, setPrefBusy] = useState(false);
  // 📦 fold a saved package into the ACTIVE option's materials. Server-side —
  // the same appendPackage + overlap-supersede engine as the auto-fold, plus
  // pricing (preview mode: a fold is not a correction, no teach events), so
  // the rows land green/red exactly as a generated draft's would.
  const [pkgOpen, setPkgOpen] = useState(false);
  const [pkgBusy, setPkgBusy] = useState(false);
  async function handleFoldPackage(p) {
    setPkgOpen(false); setPkgBusy(true); setRepriceMsg(null);
    try {
      // context_text = the quote's own words — drives per-item when/unless
      // variant gates (an "ATS outside" scope folds the 2×-weatherproof build).
      const contextText = [
        singleTitle ?? quote?.project_title, quote?.client_name,
        draft?.meta?.tier_label, activeDraft?.meta?.tier_label,
        ...(activeDraft?.scope || []).map(s => String(s?.text ?? s ?? "")),
        ...(activeDraft?.notes || []).map(s => String(s?.text ?? s ?? "")),
      ].filter(Boolean).join("\n");
      const r = await apiFetch("/api/quote-brain", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "foldPackage", package_id: p.id, price: true,
          materials: activeDraft?.materials || [],
          context_text: contextText,
          ...(prefSupplier ? { prefer_supplier: prefSupplier } : {}),
        }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      updateMaterials(j.materials);
      const bits = [`+${j.added} line${j.added === 1 ? "" : "s"}`];
      if (j.merged) bits.push(`${j.merged} overlap${j.merged === 1 ? "" : "s"} merged — kept your quantities`);
      if (j.skipped) bits.push(`${j.skipped} already on the quote`);
      setRepriceMsg(j.note || `📦 ${j.name}: ${bits.join(" · ")}`);
    } catch (e) { setRepriceMsg("⚠ " + e.message); }
    finally { setPkgBusy(false); }
  }
  useEffect(() => {
    apiFetch("/api/takeoff", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "listSuppliers" }) })
      .then(r => r.json()).then(j => setPrefSuppliers(Array.isArray(j.suppliers) ? j.suppliers : [])).catch(() => {});
  }, []);
  useEffect(() => { if (draft?.preferred_supplier) setPrefSupplier(draft.preferred_supplier); }, [draft?.preferred_supplier]);
  async function handleSupplierReprice() {
    if (!prefSupplier || prefBusy) return;
    setPrefBusy(true); setRepriceMsg(null);
    try {
      if (dirty) await handleSave({ promptLearning: false });
      const r = await apiFetch("/api/quote-brain", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "repriceMaterials", quote_id: id, supplier: prefSupplier }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      await loadQuote({ quiet: true });
      setRepriceMsg(j.updated
        ? `${prefSupplier}: switched ${j.updated} line${j.updated > 1 ? "s" : ""} to book prices — the rest kept their current price/supplier.`
        : `${prefSupplier}: no confident book matches — everything kept as-is (preference saved; refines will keep trying it).`);
    } catch (e) { setRepriceMsg("⚠ " + e.message); }
    finally { setPrefBusy(false); }
  }
  async function handleReprice() {
    if (repriceBusy) return;
    setRepriceBusy(true); setRepriceMsg(null);
    try {
      if (dirty) await handleSave({ promptLearning: false });
      const r = await apiFetch("/api/quote-brain", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "repriceMaterials", quote_id: id }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      await loadQuote({ quiet: true });
      setRepriceMsg(j.updated
        ? `Re-priced ${j.updated} line${j.updated > 1 ? "s" : ""} from the book: ${(j.changes || []).map(c => `${(c.description || "").slice(0, 24)} → $${c.to}`).join(", ")}`
        : "No AI-guess lines matched the book — nothing changed.");
    } catch (e) {
      setRepriceMsg("⚠ " + e.message);
    } finally { setRepriceBusy(false); }
  }

  async function handleCopyToClipboard(text) {
    try {
      await navigator.clipboard.writeText(text);
      alert("Copied to clipboard. Paste into Jobber.");
    } catch {
      alert("Copy failed — select manually with Cmd/Ctrl+A.");
    }
  }
  // The quote as text — the active option, or every option/phase.
  const quoteTextBlocks = (all) => {
    const head = { quoteNumber: quote?.jobber_quote_number || null, title: singleTitle ?? quote?.project_title ?? null };
    const kind = multi ? "Option" : "Phase";
    if (blocks && all) return { blocks: blocks.map((b, i) => ({ label: b.label || `${kind} ${i + 1}`, draft: b.draft })), head };
    if (blocks) return { blocks: [{ label: blocks[activeIdx]?.label || `${kind} ${activeIdx + 1}`, draft: activeDraft }], head };
    return { blocks: [{ label: null, draft: activeDraft }], head };
  };
  const quoteText = (all) => { const { blocks: b, head } = quoteTextBlocks(all); return buildQuotePlainText(b, head); };
  async function copyQuoteText(all) {
    try { await navigator.clipboard.writeText(quoteText(all)); setCopiedNote(all ? "✓ whole quote copied" : "✓ copied"); }
    catch { setCopiedNote("copy failed — open the text view and select it"); }
    setTimeout(() => setCopiedNote(null), 2500);
  }

  // ── Loading / error ────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={pageStyle}>
        <div style={{ textAlign: "center", padding: 40, color: C.muted }}>Loading quote…</div>
      </div>
    );
  }
  // Back goes to the SPECIFIC assessment this quote came from, not the
  // general assessments page — falls back to home for from-scratch quotes.
  const backTo = quote?.assessment_id ? `/assess/smart/${quote.assessment_id}` : "/";
  if (error || !quote) {
    return (
      <div style={pageStyle}>
        <button style={btn("ghost")} onClick={() => navigate("/")}>‹ Back</button>
        <div style={{ ...cardStyle, marginTop: 16, color: C.danger }}>Error: {error || "Quote not found"}</div>
      </div>
    );
  }
  if (!isPlaybook) {
    // Legacy quotes use a different JSON shape. Render a stub + link to legacy editor.
    return (
      <div style={pageStyle}>
        <button style={btn("ghost")} onClick={() => navigate(backTo)}>‹ Back</button>
        <div style={{ ...cardStyle, marginTop: 16 }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Legacy Quote Format</div>
          <div style={{ color: C.muted, fontSize: 13, marginBottom: 12 }}>
            This quote was generated by the legacy quote engine. The editable backend view
            is available only for quotes built by the new Quote Brain playbook system
            (currently: generator installs and service upgrades).
          </div>
          <button style={btn()} onClick={() => navigate(`/quote/legacy/${id}`)}>Open in legacy view</button>
        </div>
      </div>
    );
  }

  // ═════════════════════════════════════════════════════════════════════════
  // EDITABLE BACKEND QUOTE — Playbook shape
  // ═════════════════════════════════════════════════════════════════════════

  return (
    <div style={pageStyle}>
      <style>{QDR_CSS}</style>
      {/* Pipeline flow-through — assessment behind this quote, takeoff/job ahead of it */}
      <FlowStrip anchor={{ quote: id }} style={{ marginBottom: 14 }} />

      {/* Top bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 12 }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <button style={btn("ghost")} onClick={() => navigate(backTo)}>‹ Back</button>
          <button
            style={{ ...btn("ghost"), opacity: historyRef.current.length ? 1 : 0.4 }}
            onClick={handleUndo}
            disabled={!historyRef.current.length}
            title={historyRef.current.length
              ? `Undo — back to before: ${historyRef.current[historyRef.current.length - 1].label} (${historyRef.current.length} step${historyRef.current.length > 1 ? "s" : ""} available)`
              : "Nothing to undo"}
          >
            ↶ Undo{historyRef.current.length ? ` (${historyRef.current.length})` : ""}
          </button>
          <button
            style={{ ...btn("ghost"), opacity: futureRef.current.length ? 1 : 0.4 }}
            onClick={handleRedo}
            disabled={!futureRef.current.length}
            title={futureRef.current.length ? `Redo (${futureRef.current.length})` : "Nothing to redo"}
          >
            ↷ Redo{futureRef.current.length ? ` (${futureRef.current.length})` : ""}
          </button>
          {restorePoints.length > 0 && (
            <button
              style={{ ...btn("ghost"), ...(showRestore ? { borderColor: C.accent, color: C.accent } : {}) }}
              onClick={() => setShowRestore(s => !s)}
              title="Restore a saved snapshot (taken before each AI refine) — just the option you're editing, or the whole quote"
            >
              ⟲ Restore…
            </button>
          )}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {dirty && <span style={{ ...pill(C.warn), alignSelf: "center" }}>unsaved changes</span>}
          {!dirty && savedAt && <span style={{ ...pill(C.good), alignSelf: "center" }}>saved {savedAt.toLocaleTimeString()}</span>}
          <button style={btn()} onClick={handleSave} disabled={saving || !dirty}>{saving ? "Saving…" : "Save Draft"}</button>
          <button
            style={{ ...btn(), ...(markedForLearning ? { background: "rgba(16, 185, 129, 0.18)", borderColor: C.good, color: C.good } : {}) }}
            onClick={handleSaveAndLearn}
            disabled={saving}
            title="Save AND mark this quote as a training example. The Suggestions inbox on the Quote Template page will use it to spot patterns across recent quotes."
          >
            {markedForLearning ? "✓ Learning-marked" : "💡 Save & Learn"}
          </button>
          <button
            style={{ ...btn("ghost"), fontSize: 12, padding: "6px 10px", color: C.muted }}
            onClick={() => handleCopyToClipboard(buildLogBookText(activeDraft))}
            disabled={saving}
            title="Copy this quote as tab-separated rows — pastes straight into the Log Book estimates sheet"
          >
            📋 Log Book
          </button>
          <button
            style={{ ...btn("ghost"), fontSize: 12, padding: "6px 10px", color: C.muted }}
            onClick={() => copyQuoteText(false)}
            disabled={saving}
            title="Copy this option as plain text — scope, materials (qty + description), notes and exclusions. No pricing. Paste it into an email, a text, a job note…"
          >
            📋 Copy text
          </button>
          <button
            style={{ ...btn("ghost"), fontSize: 12, padding: "6px 10px", color: C.muted }}
            onClick={exportCsv}
            disabled={saving}
            title="Download the WHOLE backend quote as a spreadsheet — every option's lines with our cost, PST, markup and sell, plus a totals-by-option block. Internal: it carries our costs and margin."
          >
            📊 Spreadsheet
          </button>
          <button
            style={{ ...btn("ghost"), fontSize: 12, padding: "6px 10px", color: C.muted }}
            onClick={exportPdf}
            disabled={saving}
            title="Open the WHOLE backend quote as a printable sheet, then Save as PDF. Internal: it carries our costs and margin, unlike Review & Push which is the client-facing quote."
          >
            📄 PDF
          </button>
          <button
            style={{ ...btn("ghost"), fontSize: 12, padding: "6px 10px", ...(textView ? { borderColor: C.accent, color: C.accent } : { color: C.muted }) }}
            onClick={() => setTextView(v => !v)}
            disabled={saving}
            title="Show the quote as selectable text so you can highlight and copy any part of it with the cursor"
          >
            🔤 Text view
          </button>
          {copiedNote && <span style={{ ...pill(copiedNote.startsWith("✓") ? C.good : C.warn), alignSelf: "center" }}>{copiedNote}</span>}
          <button
            style={{ ...btn("ghost"), fontSize: 12, padding: "6px 10px", ...(showSaveTpl ? { borderColor: C.accent, color: C.accent } : { color: C.muted }) }}
            onClick={() => setShowSaveTpl(s => !s)}
            disabled={saving}
            title="Save this quote as a reusable template — pick it when converting a future Smart Assessment and the AI adapts it to the new scope"
          >
            💾 Save as Template
          </button>
          <button style={btn("primary")} onClick={() => setShowReviewPush(true)} disabled={saving}>
            Review &amp; Push →
          </button>
        </div>
      </div>

      {/* Save-as-Template — snapshot this quote into the Convert-time picker */}
      {showSaveTpl && (
        <SaveTemplateCard
          quoteId={id}
          defaultName={singleTitle || activeDraft?.meta?.tier_label || draft?.meta?.tier_label || ""}
          onSaveIfDirty={async () => { if (dirty) await handleSave({ promptLearning: false }); }}
          onClose={() => setShowSaveTpl(false)}
        />
      )}

      {/* Restore picker — per-option or whole-quote revert to a saved snapshot */}
      {showRestore && (
        <div style={{ ...cardStyle, marginBottom: 12, padding: 12 }}>
          <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600, marginBottom: 8 }}>
            Restore points · snapshots saved before each AI refine
          </div>
          {restorePoints.map((pt) => {
            const snapOpt = multi ? snapshotOptionFor(pt, draft, activeIdx) : null;
            const activeLabel = multi ? (draft.options[activeIdx]?.label || `Option ${activeIdx + 1}`) : null;
            return (
              <div key={pt.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "8px 0", borderTop: `1px solid ${C.border}` }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{pt.label}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>
                    {pt.ts ? new Date(pt.ts).toLocaleString() : ""}{pt.total_estimated != null ? ` · ${fmt$(pt.total_estimated)}` : ""}
                  </div>
                </div>
                {multi && (
                  <button
                    style={{ ...btn(), fontSize: 12, padding: "6px 10px", opacity: snapOpt?.draft ? 1 : 0.4 }}
                    disabled={!snapOpt?.draft}
                    onClick={() => restoreActiveOption(pt)}
                    title={snapOpt?.draft
                      ? `Replace ONLY "${activeLabel}" with its state from this snapshot — other options (incl. ones added since) untouched. Undo reverses it.`
                      : "This option didn't exist at this snapshot"}
                  >
                    Only “{activeLabel}”
                  </button>
                )}
                <button
                  style={{ ...btn("ghost"), fontSize: 12, padding: "6px 10px", color: C.warn }}
                  onClick={() => restoreWholeQuote(pt)}
                  title="Replace the ENTIRE quote with this snapshot — options added since will disappear. Undo reverses it."
                >
                  Whole quote
                </button>
              </div>
            );
          })}
          <div style={{ fontSize: 11, color: C.muted, fontStyle: "italic", marginTop: 6 }}>
            Every restore pushes an Undo step first — ↶ Undo brings you right back if it's not what you wanted.
          </div>
        </div>
      )}

      {/* Options bar — pick / reorder (drag) / add / duplicate. Rendered even for
          a single-option quote so you can start a good/better/best ladder — via
          duplicate OR a blank option (an add-on priced on top starts blank).
          A sectioned quote must never grow options[]: promoteToMulti would
          strand its phases (is_multi_option wins over is_sectioned at save), so
          onDuplicate/onAddBlank are withheld for sectioned drafts even in flat
          view — OptionSelector hides the buttons when the handlers are absent. */}
      {/* Copy/paste are refused on a SECTIONED quote in BOTH views. In per-phase
          view the tiles come from draft.sections, not draft.options, so an index
          would miss; in flat view the draft IS every phase merged, so "copy this
          option" would quietly put the ENTIRE job on the clipboard as one option. */}
      <OptionSelector
        options={multi ? draft.options : sectionedEdit ? draft.sections : null}
        additive={sectionedEdit}
        sectioned={!sectionedEdit && isSectionedDraft(draft)}
        activeIdx={activeIdx}
        onSelect={setOptIdx}
        onReorder={sectionedEdit ? undefined : reorderOptions}
        onDuplicate={sectionedEdit || isSectionedDraft(draft) ? undefined : (i) => addOptionUI("duplicate", i)}
        onAddBlank={sectionedEdit || isSectionedDraft(draft) ? undefined : () => addOptionUI("blank")}
        onCopyOption={sectionedEdit || isSectionedDraft(draft) ? undefined : (i, o) => copyOptions(i, o)}
        onCopyAll={sectionedEdit || !multi ? undefined : () => copyOptions("all")}
        onPaste={sectionedEdit || isSectionedDraft(draft) ? undefined : pasteOptions}
        clipLabel={clipPayload ? describePayload(clipPayload) : ""}
        clipCount={clipPayload?.options?.length || 0}
        clipIsThisQuote={!!(clipPayload && clipPayload.source?.quote_id && String(clipPayload.source.quote_id) === String(id))}
        onDelete={sectionedEdit ? undefined : deleteOption}
        pinned={optBarPinned}
        onTogglePin={toggleOptBarPin}
        mirror={mirrorInfo}
        onToggleMirror={setMirrorOn}
        onSetOptionMirrorOut={setOptionMirrorOut}
      />
      {/* The mirror toggle and its live state sit INSIDE the options bar, next
          to "⧉ Duplicate this" — the Huhn #2656 copy (2026-09-15) was edited with
          the mirror on because nothing near the duplicate button said so. */}

      {/* Mirror OFFER toast — the edit stayed local and mirroring is off. Says
          so and applies it in one click, instead of the tech discovering three
          packages later that they have to redo the edit in each. Deliberately
          an offer, never automatic: a pushed quote's options are client-facing
          prices, which is why the toggle defaults off (Huhn #2656). */}
      {mirrorOffer && !mirrorUndo && (
        <div style={{ position: "fixed", bottom: 76, left: "50%", transform: "translateX(-50%)", zIndex: 400, display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", borderRadius: 999, background: C.surface, border: `1px solid ${C.border}`, boxShadow: "0 6px 24px rgba(0,0,0,0.35)", fontSize: 12.5, color: C.text }}>
          <span>
            ✋ Stayed in <b>this package</b> only
            {quoteSent ? " — mirroring is off because this quote was sent" : ""}
          </span>
          <span style={{ color: C.accent, cursor: "pointer", fontWeight: 600 }} onClick={applyMirrorOffer}
                title={`Apply the same change to ${mirrorOffer.labels.join(", ")}`}>
            → Also apply to {mirrorOffer.labels.join(", ")}
          </span>
          <span style={{ color: C.muted, cursor: "pointer" }} onClick={() => setMirrorOffer(null)}>✕</span>
        </div>
      )}

      {/* Mirror toast — bottom-center pill with one-click undo of JUST the
          mirrored copies (the edit itself stays). */}
      {mirrorUndo && (
        <div style={{ position: "fixed", bottom: 76, left: "50%", transform: "translateX(-50%)", zIndex: 400, display: "flex", alignItems: "center", gap: 10, padding: "9px 16px", borderRadius: 999, background: C.surface, border: `1px solid ${C.accent}`, boxShadow: "0 6px 24px rgba(0,0,0,0.35)", fontSize: 12.5, color: C.text }}>
          <span>🔗 Mirrored to <b>{mirrorUndo.labels.join(", ")}</b></span>
          <span style={{ color: C.accent, cursor: "pointer", fontWeight: 600 }} onClick={undoMirror}>↩ Undo</span>
          <span style={{ color: C.muted, cursor: "pointer" }} onClick={() => { setMirrorOn(false); setMirrorUndo(null); }} title="Turn mirroring off for this quote">turn off</span>
          <span style={{ color: C.muted, cursor: "pointer" }} onClick={() => setMirrorUndo(null)}>✕</span>
        </div>
      )}

      {/* Header card (active option). The title is editable: for a multi-option
          quote it renames the ACTIVE option (switch options to rename each); for
          a single-option quote it renames the whole quote. Either way it's the
          header that converts to client-facing + Jobber. */}
      <HeaderCard
        draft={activeDraft}
        quote={quote}
        assessment={assessment}
        title={multi ? (draft.options[activeIdx]?.label || `Option ${activeIdx + 1}`)
          : sectionedEdit ? (draft.sections[activeIdx]?.label || `Phase ${activeIdx + 1}`)
          : (singleTitle ?? quote?.project_title ?? activeDraft?.meta?.tier_label ?? "Quote")}
        onRename={(v) => (multi ? relabelOption(activeIdx, v) : sectionedEdit ? relabelSection(activeIdx, v) : setQuoteTitle(v))}
        renameHint={multi ? `Renaming “${draft.options[activeIdx]?.label || `Option ${activeIdx + 1}`}” — switch packages above to rename the others`
          : sectionedEdit ? `Renaming phase “${draft.sections[activeIdx]?.label || `Phase ${activeIdx + 1}`}” — this is its Jobber line name` : null}
      />

      {/* Client row — a from-scratch quote has no Jobber client; assign one so push works */}
      <ClientRow quoteId={id} assessment={assessment} onReload={() => loadQuote({ quiet: true })} />

      {/* Second look — the whole-quote review pass (auto after a fresh Convert) */}
      <SecondLookCard quoteId={id} workflowState={quote?.workflow_state} draftSig={secondLookSig} getDraft={() => draft} />

      {/* Sectioned (rough-in + finishing) quotes: per-phase compare strip */}
      <SectionCompareStrip draft={draft} activeIdx={activeIdx} onSelect={setOptIdx} editable={sectionedEdit} />

      {/* Text view — the whole quote as selectable text. Every editor row below
          is an <input>, and a selection can't span inputs, so dragging across
          the editor only ever grabbed one field. */}
      {textView && (
        <div style={{ ...cardStyle, marginTop: 12, borderColor: C.accent + "66" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 200, fontSize: 14, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              🔤 Text view <span style={{ color: C.muted, fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— drag to select any part, or copy it all</span>
            </div>
            {blocks && blocks.length > 1 && (
              <label style={{ fontSize: 12, color: C.muted, display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                <input type="checkbox" checked={textViewAll} onChange={e => setTextViewAll(e.target.checked)} />
                all {multi ? "options" : "phases"}
              </label>
            )}
            <button style={btn("primary")} onClick={() => copyQuoteText(textViewAll)}>📋 Copy {textViewAll && blocks ? "whole quote" : "this"}</button>
            <button style={btn("ghost")} onClick={() => setTextView(false)}>✕ Close</button>
          </div>
          <pre style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word", userSelect: "text", WebkitUserSelect: "text", cursor: "text", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 12.5, lineHeight: 1.55, color: C.text, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", maxHeight: 560, overflowY: "auto" }}>
            {quoteText(textViewAll)}
          </pre>
        </div>
      )}

      {/* Sections — edit the active option's backend */}
      <Section
        title="Scope of Work"
        action={<CopyToOption targets={copyTargets} onCopy={(i) => copyListToOption("scope", i)} note={copyNote?.key === "scope" ? copyNote.text : null} />}
      >
        <ScopeEditor scope={activeDraft.scope || []} onChange={(scope) => update({ scope })} />
      </Section>

      <Section title="Materials">
        <SupplierQuoteImport
          quoteId={id}
          blocks={blocks
            ? blocks.map((b, i) => ({ label: b.label || (multi ? `Option ${i + 1}` : `Phase ${i + 1}`), materials: b.draft?.materials || [] }))
            : [{ label: null, materials: draft?.materials || [] }]}
          onApply={applySupplierQuote}
        />
        <MaterialsEditor
          materials={activeDraft.materials || []}
          onChange={updateMaterials}
          copyTargets={copyTargets}
          onCopyToOption={copyMaterialsToOption}
          preferredSupplier={draft?.preferred_supplier || null}
          quoteId={id}
        />
        {(() => {
          const flagged = (activeDraft.materials || []).filter(m => m._price_source?.tier === "web_placeholder").length;
          return (
            <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <select value={prefSupplier} onChange={e => setPrefSupplier(e.target.value)}
                title="Preferred supplier — pricing tries this supplier's book first; lines with no confident match keep their current supplier/price."
                style={{ background: "#1c2028", border: "1px solid #2a3140", borderRadius: 8, color: "#e8eaf0", fontSize: 12, padding: "6px 8px" }}>
                <option value="">Preferred supplier…</option>
                {prefSuppliers.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={handleSupplierReprice} disabled={!prefSupplier || prefBusy} style={{ ...btn("ghost"), fontSize: 12, padding: "6px 10px", opacity: prefSupplier ? 1 : .5 }}
                title="Try to price every line from this supplier's book — no confident match = line unchanged.">
                {prefBusy ? "Matching…" : "↻ Price from supplier"}
              </button>
              {flagged > 0 && (
                <button onClick={handleReprice} disabled={repriceBusy} style={{ ...btn("ghost"), fontSize: 12, padding: "6px 10px" }}
                  title="Re-check the red AI-guess lines against the current price book — use this after you've saved/added a supplier price.">
                  {repriceBusy ? "Re-pricing…" : `↻ Re-price ${flagged} AI-guess line${flagged > 1 ? "s" : ""}`}
                </button>
              )}
              <button onClick={() => setPkgOpen(true)} disabled={pkgBusy} style={{ ...btn("ghost"), fontSize: 12, padding: "6px 10px" }}
                title="Fold a saved material package into this option — overlapping lines merge instead of doubling, and the new rows price from the book.">
                {pkgBusy ? "Folding…" : "📦 Add package"}
              </button>
              {repriceMsg && <span style={{ fontSize: 11, color: repriceMsg.startsWith("⚠") ? C.danger : C.muted }}>{repriceMsg}</span>}
              {pkgOpen && <PackagePickModal onPicked={handleFoldPackage} onClose={() => setPkgOpen(false)} />}
              {/* Which packages the auto-fold applied at draft time (and any size
                  conflicts it resolved) — provenance was invisible before this. */}
              {(draft?.meta?.packages_applied || []).map((pa, i) => pa.conflict_skipped ? (
                <span key={i} style={{ fontSize: 11, color: "#f2b53c" }}
                  title={`"${pa.name}" also matched this quote's text but lost the size-family resolution to "${pa.lost_to}" — double-check the generator size.`}>
                  ⚠ 📦 {pa.name} skipped (lost to {pa.lost_to})
                </span>
              ) : (
                <span key={i} style={{ fontSize: 11, color: C.muted, border: `1px solid ${C.border}`, borderRadius: 999, padding: "2px 8px" }}
                  title={`Auto-folded at draft time${pa.merged ? ` — ${pa.merged} overlap${pa.merged === 1 ? "" : "s"} merged (draft quantities kept)` : ""}${pa.variant_skipped ? ` · ${pa.variant_skipped} variant line${pa.variant_skipped === 1 ? "" : "s"} not applicable` : ""}${pa.into ? ` · into ${pa.into}` : ""}`}>
                  📦 {pa.name} +{pa.added}{pa.merged ? ` (${pa.merged} merged)` : ""}
                </span>
              ))}
            </div>
          );
        })()}
      </Section>

      {activeDraft.load_shedding?.available && (
        <Section title="Load Shedding (SMMs)">
          <LoadSheddingEditor
            loadShedding={activeDraft.load_shedding}
            onChange={(load_shedding) => update({ load_shedding })}
          />
        </Section>
      )}

      <Section title="Labour">
        <LabourEditor labour={activeDraft.labour || {}} onChange={updateLabour} />
      </Section>

      <Section
        title="Notes"
        action={<CopyToOption targets={copyTargets} onCopy={(i) => copyListToOption("notes", i)} note={copyNote?.key === "notes" ? copyNote.text : null} />}
      >
        <BulletListEditor
          items={activeDraft.notes || []}
          onChange={(notes) => update({ notes })}
          placeholder="Add a note (visible to client)"
          rangeId="notes"
        />
      </Section>

      <Section
        title="Exclusions"
        action={<CopyToOption targets={copyTargets} onCopy={(i) => copyListToOption("exclusions", i)} note={copyNote?.key === "exclusions" ? copyNote.text : null} />}
      >
        <BulletListEditor
          items={activeDraft.exclusions || []}
          onChange={(exclusions) => update({ exclusions })}
          placeholder="Add an exclusion"
          rangeId="exclusions"
        />
      </Section>

      {/* Totals readout (active option) — permit fee is editable/removable.
          A manual edit stamps _permit_fee_override (source tech_ui) so refines/
          reprices keep the tech's number AND the live value-bracket watch pauses;
          "↺ auto" clears it to resume watching. We keep permit_basis intact on a
          manual edit so the bracket data survives for the resume. $0 = no permit. */}
      {/* Sectioned: the permit is a PACKAGE-level line (phase drafts carry 0),
          so the picker reads/writes the top draft — update() routes permit keys
          there automatically. Totals stay per-phase; package total is in the strip. */}
      <TotalsCard
        totals={activeDraft.totals || {}}
        permitFee={(sectionedEdit ? draft : activeDraft).permit_fee}
        permitBasis={(sectionedEdit ? draft : activeDraft).permit_basis}
        permitMarkupPct={(sectionedEdit ? draft : activeDraft).permit_markup_pct}
        permitOverridden={!!((sectionedEdit ? draft : activeDraft)._permit_fee_override && Number.isFinite(Number((sectionedEdit ? draft : activeDraft)._permit_fee_override.value)))}
        canPickType={!!permitSchedule}
        permitTypeValue={permitTypeOf(sectionedEdit ? draft : activeDraft)}
        permitAmps={(sectionedEdit ? draft : activeDraft)._permit_basis_override?.amps || (sectionedEdit ? draft : activeDraft).permit_basis?.amps || 200}
        onPermitType={handlePermitType}
        onPermitChange={(v) => update({
          permit_fee: v,
          _permit_fee_override: { value: Number(v), source: "tech_ui" },
        })}
        onPermitAuto={() => update({ _permit_fee_override: null })}
        onPermitMarkupChange={(pct) => update({ permit_markup_pct: pct })}
      />

      {/* Refine with AI — chat to re-quote, grounded in real similar jobs */}
      <RefineChat
        quoteId={id}
        assessmentId={assessment?.id || quote?.assessment_id || null}
        onRefineStart={async () => {
          pushHistory("AI refine"); // one Undo reverts the whole refine
          refineInFlightRef.current = true;
          // Refine reads the DB copy — unsaved table edits must land first or
          // the refine works on stale data and the reload discards them.
          if (dirty) await handleSave({ promptLearning: false });
        }}
        onRefined={() => loadQuote({ quiet: true, rethrow: true })}
        onRefineEnd={() => { refineInFlightRef.current = false; }}
      />

      {/* The "Brain feedback" box that used to sit here is gone (2026-08-09).
          The refine chat above carries remember_this, so saying "remember: never
          include paving stones" in the chat writes the same standing rule — one
          place to teach the brain instead of a box that did it silently. The
          diff-learn modal still catches rules from hand edits on save. */}

      {/* Consolidated convert → refine → push modal */}
      {showReviewPush && (
        <ReviewPushModal
          quoteId={id}
          draft={activeDraft}
          onSaveIfDirty={async () => { if (dirty) await handleSave({ promptLearning: false }); }}
          onReloadDraft={() => loadQuote({ quiet: true })}
          onClose={() => setShowReviewPush(false)}
        />
      )}

      {/* Diff → "remember these changes?" modal */}
      {diffs !== null && (
        <DiffLearnModal
          diffs={diffs}
          checked={diffChecked}
          onToggle={(i) => setDiffChecked(c => ({ ...c, [i]: !c[i] }))}
          onSkip={() => setDiffs(null)}
          onConfirm={handleConfirmLearning}
          saving={saving}
        />
      )}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

// Options bar — one pill per package (click to edit, DRAG to reorder the price
// ladder), plus add / duplicate. `options` null → single-option quote: just the
// "add option" affordance so a good/better/best ladder can be started.
function OptionSelector({ options, additive, sectioned, activeIdx, onSelect, onReorder, onDuplicate, onAddBlank, onDelete, pinned, onTogglePin, mirror, onToggleMirror, onSetOptionMirrorOut, onCopyOption, onCopyAll, onPaste, clipLabel, clipCount, clipIsThisQuote }) {
  const dragFrom = useRef(null);
  const [overIdx, setOverIdx] = useState(null);
  // A drag that came from ANOTHER window carries our payload on the
  // dataTransfer; the in-window reorder drag carries it in dragFrom.current,
  // which is per-window and therefore null over here. That is how one drop
  // handler tells "reorder me" from "paste me".
  const [dropArmed, setDropArmed] = useState(false);
  const carriesOption = (e) => {
    const t = e?.dataTransfer?.types;
    if (!t) return false;
    return Array.from(t).includes(DND_MIME);
  };
  const readDropped = (e) => {
    // getData is only readable on DROP, never during dragover.
    try { return e.dataTransfer.getData(DND_MIME) || e.dataTransfer.getData("text/plain") || ""; }
    catch { return ""; }
  };
  const handleExternalDrop = (e) => {
    if (!onPaste || dragFrom.current != null) return false;
    const raw = readDropped(e);
    if (!raw) return false;
    e.preventDefault();
    e.stopPropagation();   // else the pill's drop bubbles to the bar and pastes twice
    setDropArmed(false);
    onPaste(raw);
    return true;
  };
  const multi = Array.isArray(options) && options.length > 0;
  const addBtn = (label, onClick, title, tone) => (
    <button onClick={onClick} title={title}
      style={{ border: `1px dashed ${tone || C.border}`, background: "transparent", color: tone || C.muted, borderRadius: 10, padding: "8px 12px", cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600 }}>
      {label}
    </button>
  );
  // Mirror state, next to the duplicate button: the toggle, what the NEXT edit
  // of the active package will touch, and the per-package switch. A fresh copy
  // reads "edits stay here" even with the toggle on — that is the guard that
  // would have saved the Huhn #2656 as-sent options.
  const mirrorBlock = () => {
    if (!mirror) return null;
    const label = options[activeIdx]?.label || `Option ${String.fromCharCode(65 + activeIdx)}`;
    const held = mirror.on && mirror.mode !== "on";
    const tone = !mirror.on ? C.muted : (held || mirror.sent) ? C.warn : C.accent;
    let status, link = null;
    if (!mirror.on) {
      status = mirror.sent
        ? "edits change only this package · starts OFF because this quote was sent — the other packages are client-facing prices"
        : "edits change only this package";
    } else if (mirror.mode === "copy") {
      status = `fresh copy — edits stay in “${label}”, nothing mirrors back into the original`;
      link = ["mirror out from this copy too", () => onSetOptionMirrorOut(activeIdx, true)];
    } else if (mirror.mode === "off") {
      status = `edits stay in “${label}”`;
      link = ["mirror out from this package", () => onSetOptionMirrorOut(activeIdx, true)];
    } else {
      status = mirror.targets.length
        ? `edits to shared lines also change: ${mirror.targets.join(", ")}${mirror.sent ? " ⚠ this quote was sent — those are client-facing prices" : ""}`
        : "no other package shares this one's base scope — nothing mirrors";
      link = ["keep this package's edits local", () => onSetOptionMirrorOut(activeIdx, false)];
    }
    return (
      <div style={{ display: "inline-flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "6px 10px", borderRadius: 10, border: `1px solid ${mirror.on ? tone : C.border}`, background: mirror.on ? `${tone}1f` : "transparent", fontSize: 11.5, color: C.muted, maxWidth: "100%" }}>
        <label style={{ cursor: "pointer", userSelect: "none", color: mirror.on ? tone : C.muted, fontWeight: 700, whiteSpace: "nowrap" }} title="Edits to a line that exists identically in other packages apply there too (with Undo). A freshly duplicated package never mirrors out until you turn it on for it.">
          <input type="checkbox" checked={mirror.on} onChange={e => onToggleMirror(e.target.checked)} style={{ verticalAlign: "middle", marginRight: 5 }} />
          🔗 mirror {mirror.on ? "ON" : "OFF"}
        </label>
        <span>· {status}</span>
        {link && <span onClick={link[1]} style={{ color: C.accent, cursor: "pointer", fontWeight: 600, whiteSpace: "nowrap" }}>{link[0]}</span>}
      </div>
    );
  };
  return (
    <div className={pinned ? "qdr-optbar-pinned" : undefined} style={{ ...cardStyle, marginBottom: 12, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <div style={{ flex: 1, minWidth: 0, fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 600 }}>
          {multi ? (additive
            ? <>Phases · additive (they SUM) · editing {options[activeIdx]?.label} · each pushes as its own Jobber line</>
            : <>Packages · editing {options[activeIdx]?.label} · drag to reorder the ladder</>)
            : sectioned ? "Sectioned quote — phases add up (not pick-one), so options can't be added here; change the split in the assessment and re-convert" : "This quote has one option"}
        </div>
        <button
          onClick={onTogglePin}
          title={pinned ? "Pinned — the bar follows you as you scroll. Click to unpin." : "Unpinned — click to keep the bar at the top while scrolling."}
          style={{ border: "none", background: "transparent", cursor: "pointer", fontSize: 13, color: pinned ? C.accent : C.muted, fontFamily: "inherit", padding: "2px 4px", flexShrink: 0 }}
        >
          {pinned ? "📌 Pinned" : "📍 Unpinned"}
        </button>
      </div>
      <div
        style={{
          display: "flex", gap: 8, flexWrap: "wrap", alignItems: "stretch",
          ...(dropArmed ? { outline: `2px dashed ${C.good}`, outlineOffset: 6, borderRadius: 10 } : {}),
        }}
        onDragOver={onPaste ? (e) => {
          if (dragFrom.current != null || !carriesOption(e)) return;   // our own reorder
          e.preventDefault();
          e.dataTransfer.dropEffect = "copy";                          // COPY, never move
          if (!dropArmed) setDropArmed(true);
        } : undefined}
        onDragLeave={onPaste ? (e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDropArmed(false); } : undefined}
        onDrop={onPaste ? (e) => { handleExternalDrop(e); } : undefined}
      >
        {dropArmed && (
          <div style={{ width: "100%", fontSize: 12, color: C.good, fontWeight: 600 }}>
            ⤵ Drop to paste a copy here — the quote you dragged from is not changed
          </div>
        )}
        {multi && options.map((o, i) => {
          const on = i === activeIdx;
          return (
            <button
              key={o.key || i}
              onClick={() => onSelect(i)}
              draggable={!additive}
              onDragStart={additive ? undefined : (e) => {
                dragFrom.current = i;
                // The reorder drag stays a ref (it never leaves this window).
                // The PAYLOAD rides on dataTransfer so the same gesture also
                // works into a second window: a custom MIME so we recognise
                // our own, and text/plain so it survives wherever a custom
                // type is dropped.
                if (onCopyOption && e.dataTransfer) {
                  try {
                    const raw = onCopyOption(i, { forDrag: true });
                    if (raw) {
                      // The custom MIME ONLY. text/plain here made a near-miss
                      // drop take the browser default and paste 10-30 KB of
                      // JSON into whatever textarea it landed on — the scope,
                      // notes and proposal-body fields have no drop handlers,
                      // and those are client-facing. The clipboard write still
                      // uses text/plain; that is the cross-profile transport.
                      e.dataTransfer.setData(DND_MIME, raw);
                      e.dataTransfer.effectAllowed = "copyMove";
                    }
                  } catch { /* a drag that can't carry the payload still reorders */ }
                }
              }}
              onDragOver={additive ? undefined : (e) => { e.preventDefault(); if (overIdx !== i) setOverIdx(i); }}
              onDragEnd={additive ? undefined : () => { dragFrom.current = null; setOverIdx(null); setDropArmed(false); }}
              onDrop={additive ? undefined : (e) => {
                e.preventDefault();
                // Foreign payload + no local drag in flight → this is a paste
                // from another window, not a reorder.
                if (dragFrom.current == null) { handleExternalDrop(e); setOverIdx(null); return; }
                onReorder(dragFrom.current, i);
                dragFrom.current = null; setOverIdx(null);
              }}
              style={{
                border: `1.5px solid ${overIdx === i ? C.good : on ? C.accent : C.border}`,
                background: on ? C.accentDim : C.surface2,
                color: on ? C.accent : C.text,
                borderRadius: 10, padding: "8px 12px", cursor: additive ? "pointer" : "grab", fontFamily: "inherit",
                textAlign: "left", minWidth: 150,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
                {!additive && <span style={{ color: C.muted, cursor: "grab" }} title="Drag to reorder">⠿</span>}
                {o.recommended && !additive && <span title="Recommended" style={{ color: C.good }}>★</span>}
                {additive ? `Phase ${i + 1}` : `Option ${String.fromCharCode(65 + i)}`}
              </div>
              <div style={{ fontSize: 11, color: on ? C.accent : C.muted, marginTop: 2 }}>{o.label}</div>
              <div style={{ fontSize: 13, fontWeight: 700, marginTop: 2 }}>{fmt$(additive ? (o.subtotal ?? o.grand_total) : o.grand_total)}</div>
            </button>
          );
        })}
        {multi && !additive && onDuplicate && addBtn("⧉ Duplicate this", () => onDuplicate(activeIdx), "Copy the current option as a new package, then edit it. The copy's edits stay in the copy — nothing mirrors back into the original — until you turn mirroring on for it.")}
        {multi && !additive && mirrorBlock()}
        {!multi && !additive && onDuplicate && addBtn("⧉ Duplicate as new option", () => onDuplicate(0), "Turn this into a good/better/best quote — copies this as Option A and adds an identical Option B to edit")}
        {!additive && onAddBlank && addBtn("＋ Blank option", () => onAddBlank(), multi ? "Add an empty package — starts at $0 (good for an add-on priced on top)" : "Add an empty Option B — this quote becomes Option A; the blank starts at $0 (good for an add-on priced on top)")}
        {!additive && onCopyOption && addBtn(
          "📋 Copy option",
          () => onCopyOption(multi ? activeIdx : 0),
          "Copy this option — every line, its costs and markups, and the labour hours — so you can paste it into a DIFFERENT quote. The quote you copy from is not changed. You can also just drag the option's tile into the other window.")}
        {!additive && onCopyAll && addBtn(
          `📋 Copy all ${options.length}`,
          () => onCopyAll(),
          "Copy every option in this quote, so you can paste the whole ladder into another quote.")}
        {!additive && onPaste && addBtn(
          clipCount > 0 ? `📥 Paste ${clipCount > 1 ? `${clipCount} options` : "option"}` : "📥 Paste option",
          () => onPaste(),
          clipCount > 0
            ? `Paste: ${clipLabel}. It lands as a NEW option here — nothing is overwritten, and the quote it came from is untouched. Labour is re-rated to this quote's rate and the permit re-derived for this job.`
            : "Paste an option copied from another quote. Copy one there first with 📋 Copy option — or, if you copied it in a different browser profile, this reads your system clipboard.",
          clipCount > 0 ? C.good : undefined)}
        {!additive && onPaste && clipCount > 0 && (
          <div style={{ display: "flex", alignItems: "center", fontSize: 11, color: C.muted, maxWidth: 280 }}>
            on the clipboard: {clipLabel}{clipIsThisQuote ? " (this quote)" : ""}
          </div>
        )}
        {multi && !additive && options.length > 1 && addBtn("🗑 Delete this", () => onDelete(activeIdx), `Delete "${options[activeIdx]?.label}" — Undo brings it back`, C.danger)}
      </div>
    </div>
  );
}

// Click-to-edit heading. The value it edits IS the client-facing section title
// and the Jobber line-item name, so the tooltip says so — the point of the
// feature is that what you type here is what converts.
function EditableHeading({ value, onSave, style }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value || "");
  useEffect(() => { setVal(value || ""); }, [value]);
  const commit = () => {
    setEditing(false);
    const v = val.trim();
    if (v && v !== (value || "")) onSave(v); else setVal(value || "");
  };
  if (editing) {
    return (
      <input
        autoFocus value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={commit}
        onKeyDown={e => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          else if (e.key === "Escape") { setVal(value || ""); setEditing(false); }
        }}
        style={{ ...style, background: C.surface2, border: `1px solid ${C.accent}`, borderRadius: 6, padding: "1px 8px", color: C.text, fontFamily: "inherit", width: "100%", boxSizing: "border-box" }}
      />
    );
  }
  return (
    <div
      onClick={() => setEditing(true)}
      title="Click to rename — this is the client-facing header and the Jobber line name"
      style={{ ...style, cursor: "text", display: "inline-flex", alignItems: "center", gap: 8, maxWidth: "100%" }}
    >
      <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{value || "Untitled — click to name"}</span>
      <span style={{ fontSize: 13, color: C.muted, opacity: 0.55, flexShrink: 0 }}>✎</span>
    </div>
  );
}

function HeaderCard({ draft, quote, assessment, title, onRename, renameHint }) {
  const m = draft.meta || {};
  const jobberUrl = jobberRequestUrl(assessment?.jobber_request_id);
  const unverified = (draft.materials || []).filter(line => isUnverifiedPriceSource(line._price_source));
  const reviewFlags = (draft.review_flags || []).filter(f => f.code !== "unverified_materials");
  if (unverified.length) reviewFlags.push({ code: "unverified_materials", severity: "warn", message: `${unverified.length} of ${(draft.materials || []).length} material lines have unverified prices — confirm supplier pricing before sending.` });
  return (
    <div style={cardStyle}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <EditableHeading
            value={title || quote.project_title || m.tier_label || "Quote"}
            onSave={onRename}
            style={{ fontSize: 22, fontWeight: 700, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.03em", marginBottom: 4 }}
          />
          {renameHint && <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>{renameHint}</div>}
          {assessment && (
            <div style={{ fontSize: 13, color: C.muted, marginBottom: 6, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ color: C.text, fontWeight: 600 }}>{assessment.client_name || "Client unknown"}</span>
              {assessment.phone && <span>{assessment.phone}</span>}
              {jobberUrl && (
                <a href={jobberUrl} target="_blank" rel="noreferrer" style={{ color: "#7dd3fc", textDecoration: "none", fontWeight: 600 }} title="Open the request in Jobber">
                  Jobber request ↗
                </a>
              )}
            </div>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
            <span style={pill(C.accent)}>{m.tier_label}</span>
            {m.genny_kw && <span style={pill("#7dd3fc")}>{m.genny_kw}kW</span>}
            {m.ats_choice && <span style={pill("#a78bfa")}>{m.ats_choice.replace(/_/g, " ")}</span>}
            {m.panel_workaround && <span style={pill(C.warn)}>Panel workaround: {m.panel_workaround}</span>}
            <span style={pill(C.muted)}>{m.playbook_version}</span>
          </div>
          {reviewFlags.length > 0 && (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 4 }}>
              {reviewFlags.map((f, i) => (
                <div key={i} style={{
                  fontSize: 12,
                  color: f.severity === "error" ? C.danger : f.severity === "warn" ? C.warn : C.muted,
                  background: (f.severity === "error" ? "rgba(239,68,68," : f.severity === "warn" ? "rgba(245,158,11," : "rgba(136,146,164,") + "0.08)",
                  padding: "6px 10px",
                  borderRadius: 6,
                  border: `1px solid ${f.severity === "error" ? C.danger : f.severity === "warn" ? C.warn : C.border}`,
                }}>
                  <strong>{f.code}:</strong> {f.message}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── 📦 Package picker (quote editor) ─────────────────────────────────────────
// Same saved_material_packages the takeoff + SA use; picking one calls the
// server fold so manual and auto attach can never drift apart.
function PackagePickModal({ onPicked, onClose }) {
  const [pkgs, setPkgs] = useState(null);
  const [err, setErr] = useState("");
  const [openId, setOpenId] = useState(null); // package with its line list expanded
  useEffect(() => {
    (async () => {
      try {
        const r = await apiFetch("/api/takeoff", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "listPackages" }),
        });
        const j = await r.json();
        if (!r.ok || j.ok === false) { setErr(j.error || `Couldn't load packages (${r.status})`); setPkgs([]); return; }
        setPkgs(Array.isArray(j.packages) ? j.packages : []);
      } catch (e) { setErr(e.message); setPkgs([]); }
    })();
  }, []);
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 300, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "10vh 16px 16px" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...cardStyle, width: "100%", maxWidth: 560, maxHeight: "70vh", overflowY: "auto" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
          <div style={{ flex: 1, fontSize: 14, fontWeight: 700 }}>📦 Add a material package</div>
          <button type="button" style={btn("ghost")} onClick={onClose}>✕</button>
        </div>
        {err && <div style={{ fontSize: 12, color: C.danger }}>{err}</div>}
        {pkgs === null && !err && <div style={{ fontSize: 12, color: C.muted }}>Loading packages…</div>}
        {pkgs !== null && pkgs.length === 0 && !err && (
          <div style={{ fontSize: 12, color: C.muted }}>No saved packages yet — save one from a takeoff sheet (📦 on any selection).</div>
        )}
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {(pkgs || []).map((p) => (
            <div key={p.id} style={{ background: "#1c2028", border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, color: C.text }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <button type="button" onClick={() => onPicked(p)}
                  style={{ flex: 1, textAlign: "left", cursor: "pointer", fontFamily: "inherit", color: "inherit", background: "none", border: "none", padding: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{p.name}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                    {p.item_count} line{p.item_count === 1 ? "" : "s"}
                    {p.use_count > 0 ? ` · used ${p.use_count}×` : ""}
                    {p.auto_apply ? ` · auto: “${String(p.auto_apply).slice(0, 40)}”` : ""}
                    {p.source_job_number ? ` · from job #${p.source_job_number}` : ""}
                  </div>
                </button>
                <button type="button" title="See what's in this package"
                  onClick={() => setOpenId(openId === p.id ? null : p.id)}
                  style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, color: C.muted, fontSize: 10.5, padding: "3px 8px", cursor: "pointer", fontFamily: "inherit", flexShrink: 0 }}>
                  {openId === p.id ? "▾ hide" : "▸ view"}
                </button>
              </div>
              {openId === p.id && (
                <div style={{ marginTop: 6, borderTop: `1px solid ${C.border}`, paddingTop: 5 }}>
                  {(p.items || []).filter(it => String(it?.description || "").trim()).map((it, k) => (
                    <div key={k} style={{ fontSize: 11.5, color: C.muted, padding: "1px 0" }}>
                      {it.qty ?? 1} {it.unit || "ea"} · {it.description}
                      {it.when && <span style={{ color: "#f2b53c" }}> · only if “{String(it.when).split(",")[0].trim()}”</span>}
                      {it.unless && <span> · unless “{String(it.unless).split(",")[0].trim()}”</span>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Second look — whole-quote review pass ────────────────────────────────────
// The engine composes and prices line-BY-line; this is the only thing that
// reads the quote WHOLE: deterministic checks (duplicates, size conflicts,
// unit conversions, solar mismatches) + one AI read-through. Auto-runs once on
// a fresh AI draft; the button re-runs on demand. Reviews the LIVE on-screen
// draft (getDraft), not the last save. Results grey out the moment a material
// is edited — a stale verdict lies.
// TWO PASSES, DELIBERATELY SPLIT (2026-08-11, after Dylan: "the flags seem
// like a lot and I'm not sure they're overly accurate — may just add more
// confusion on things that might be right"). Measured on 60 real quotes: the
// old card averaged 1.1 findings per quote and put 18 on one, and the loudest
// checks were the least accurate. Two changes came out of that:
//
//  • the RULE pass was tightened until every surviving check earns its place
//    (mean 0.2/quote, max 3) — that one still runs automatically, because a
//    finding you can trust is worth showing unasked;
//  • the AI read is now ON DEMAND. It is the half that cannot be verified,
//    it was the volume driver (up to 8 more per quote), and an unprompted
//    paragraph of maybes next to a trustworthy list devalues both.
//
// When nothing is wrong this renders as one quiet line, not a card.
function SecondLookCard({ quoteId, workflowState, draftSig, getDraft }) {
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);       // { verdict, summary, issues, ai_ran }
  const [ranSig, setRanSig] = useState(null);
  const [err, setErr] = useState("");
  const autoRan = useRef(false);

  const run = useCallback(async (withAi) => {
    setBusy(true); setErr("");
    try {
      const r = await apiFetch("/api/quote-brain", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "secondLook", quote_id: quoteId, draft: getDraft(), skipAi: !withAi }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) { setErr(j.error || `Review failed (${r.status})`); return; }
      setRes(j); setRanSig(draftSig);
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteId, draftSig]);

  // Rules only, once per mount, on a fresh AI draft.
  useEffect(() => {
    if (autoRan.current || busy || res) return;
    if (workflowState === "ai_draft_generated" && getDraft()) { autoRan.current = true; run(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflowState, run]);

  const stale = res && ranSig !== draftSig;
  const sevColor = (s) => (s === "error" ? C.danger : s === "warn" ? C.warn : C.muted);
  const issues = res?.issues || [];
  const clean = res && !stale && issues.length === 0;

  const deepBtn = (
    <button onClick={() => run(true)} disabled={busy} style={{ ...btn("ghost"), fontSize: 11, padding: "4px 8px" }}
      title="Have the AI read the whole quote end to end — scope vs materials, quantities, missing parts. Slower, and its findings are suggestions, not rules.">
      {busy ? "Reading…" : res?.ai_ran ? "↻ Deep check" : "🤖 Deep check"}
    </button>
  );

  // Nothing wrong → one line, no card. The point of a review pass is the
  // findings; a green box every time is just another thing to scroll past.
  if (clean) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "10px 2px 0", fontSize: 12, color: C.muted }}>
        <span style={{ color: "#4ade80" }}>✓</span>
        <span>Second look: nothing flagged{res.ai_ran ? " (incl. AI read)" : ""}.</span>
        <div style={{ flex: 1 }} />
        {deepBtn}
      </div>
    );
  }

  return (
    <div style={{ ...cardStyle, marginTop: 12, border: `1px solid ${res && !stale ? "rgba(245,158,11,.45)" : C.border}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ fontSize: 14, fontWeight: 700 }}>🧐 Second look</div>
        {res && !stale && (
          <span style={{ fontSize: 12, fontWeight: 700, color: C.warn }}>
            {issues.length} thing{issues.length === 1 ? "" : "s"} to check
          </span>
        )}
        {stale && <span style={{ fontSize: 11, color: C.muted }}>edited since the last look — re-run for a fresh read</span>}
        <div style={{ flex: 1 }} />
        {deepBtn}
        <button onClick={() => run(false)} disabled={busy} style={{ ...btn("ghost"), fontSize: 11, padding: "4px 8px" }}
          title="Re-run the rule checks — duplicates, wrong-size matches, quantity basis">
          {busy ? "…" : "↻ Re-check"}
        </button>
      </div>
      {err && <div style={{ fontSize: 12, color: C.danger, marginTop: 8 }}>⚠ {err}</div>}
      {res?.summary && !stale && <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>🤖 {res.summary}</div>}
      {issues.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 4, opacity: stale ? 0.45 : 1 }}>
          {issues.map((f, i) => (
            <div key={i} style={{
              fontSize: 12, color: sevColor(f.severity),
              background: (f.severity === "error" ? "rgba(239,68,68," : f.severity === "warn" ? "rgba(245,158,11," : "rgba(136,146,164,") + "0.08)",
              padding: "6px 10px", borderRadius: 6,
              border: `1px solid ${f.severity === "error" ? C.danger : f.severity === "warn" ? C.warn : C.border}`,
            }}>
              {/* No "(line N)" — the grid shows no line numbers, so a number
                  is a counting exercise. Findings name the part instead. */}
              {f.code === "ai_review" ? "🤖 " : ""}{f.message}
            </div>
          ))}
        </div>
      )}
      {res && !stale && !res.ai_ran && (
        <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
          Rule checks only. <b>Deep check</b> has the AI read the whole quote — scope vs materials, quantities, missing parts.
        </div>
      )}
    </div>
  );
}

function Section({ title, action, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ ...cardStyle, marginTop: 12 }}>
      {/* `action` sits OUTSIDE the toggle button — nesting interactive controls
          inside a <button> is invalid and the click would also collapse the
          section. */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: open ? 12 : 0 }}>
        <button
          onClick={() => setOpen(o => !o)}
          style={{
            flex: 1, minWidth: 0, border: "none", background: "transparent",
            color: C.text, fontSize: 14, fontWeight: 700, textTransform: "uppercase",
            letterSpacing: "0.06em", textAlign: "left", padding: 0, cursor: "pointer",
            fontFamily: "inherit", display: "flex", justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <span>{title}</span>
          <span style={{ color: C.muted, fontWeight: 400 }}>{open ? "▾" : "▸"}</span>
        </button>
        {action}
      </div>
      {open && children}
    </div>
  );
}

// "Copy to…" for a whole scope / notes / exclusions list. Copies FROM the option
// on screen INTO the one picked; the select resets so it reads as an action, not
// a setting.
function CopyToOption({ targets, onCopy, note }) {
  if (!targets.length) return null;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
      {note && <span style={{ fontSize: 11, color: C.good }}>{note}</span>}
      <select
        value=""
        onChange={(e) => { const v = e.target.value; e.target.value = ""; if (v !== "") onCopy(Number(v)); }}
        title="Copy these lines into another option"
        style={{ background: C.surface2, color: C.muted, border: `1px solid ${C.border}`, borderRadius: 8, padding: "4px 8px", fontSize: 11, fontFamily: "inherit", cursor: "pointer" }}
      >
        <option value="">⧉ Copy to…</option>
        {targets.map(t => <option key={t.idx} value={t.idx}>{t.label}</option>)}
      </select>
    </div>
  );
}

// Sectioned (additive-phase) quotes edit the FLAT top level, so without a
// visual split the two phases render as one undifferentiated list ("i still
// see it as one" — Whipple). Shows each phase's hours/materials/subtotal like
// the options format, plus whether the phases still mirror the top level
// (mirroring = pushes as separate firm lines; broken = pushes as ONE line).
function SectionCompareStrip({ draft, activeIdx, onSelect, editable }) {
  const secs = draft?.is_sectioned && Array.isArray(draft.sections) ? draft.sections : null;
  if (!secs || secs.length < 2) return null;
  const money = (n) => `$${Number(n || 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const permitFee = Number(draft.totals?.permit_fee || 0);
  const permitMk  = Number(draft.totals?.permit_markup_amount || 0);
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 12, marginBottom: 12 }}>
      <div style={{ fontSize: 10, letterSpacing: "0.08em", textTransform: "uppercase", color: C.muted, marginBottom: 8 }}>
        Sectioned quote — phases add up (not pick-one) and push as separate firm Jobber lines
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        {secs.map((s, i) => {
          const d = s?.draft || {};
          const on = editable && i === activeIdx;
          return (
            <div key={s.key || i}
              onClick={editable && onSelect ? () => onSelect(i) : undefined}
              style={{ flex: "1 1 220px", background: C.surface2, border: `1.5px solid ${on ? C.accent : C.border}`, borderRadius: 10, padding: 10, cursor: editable ? "pointer" : "default" }}
              title={editable ? `Edit ${s.label || `Phase ${i + 1}`} — its scope, materials, and labour below` : undefined}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.accent, marginBottom: 6 }}>
                {s.label || `Section ${i + 1}`}{on ? " · editing" : ""}
              </div>
              <div style={{ fontSize: 12, color: C.muted, display: "flex", flexDirection: "column", gap: 2 }}>
                <span>Labour: {Number(d.labour?.total_hours || 0)}h × ${Number(d.labour?.rate || 0)}/h = {money(d.totals?.labour_total)}</span>
                <span>Materials ({(d.materials || []).length} lines): {money(d.totals?.materials_subtotal)}</span>
                <span style={{ color: C.text, fontWeight: 600 }}>Phase total: {money(s.subtotal ?? d.totals?.subtotal)}</span>
              </div>
            </div>
          );
        })}
        <div style={{ flex: "1 1 180px", background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 10, padding: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 6 }}>Package total</div>
          <div style={{ fontSize: 12, color: C.muted, display: "flex", flexDirection: "column", gap: 2 }}>
            {permitFee > 0 && <span>Permit (TSBC): {money(permitFee)}{permitMk > 0 ? ` + ${money(permitMk)} markup` : ""}</span>}
            <span style={{ color: C.text, fontWeight: 700, fontSize: 14 }}>{money(draft.totals?.grand_total)}</span>
            <span style={{ fontSize: 10.5 }}>+ GST · phases + permit</span>
          </div>
        </div>
      </div>
      <div style={{ fontSize: 11, marginTop: 8, color: editable ? C.good : C.warn }}>
        {editable
          ? "✓ Editing per phase — labour and materials belong to the phase you pick above; the package total updates live and the push sends one firm line per phase (permit as its own line)."
          : "⟳ Hand-edited on the flat layout (older quote) — push regroups the edited lines by their phase tags and still sends one firm line per phase; lines added without a phase ride the first phase."}
      </div>
    </div>
  );
}

// Drag down a list of lines to select them as a block, then Copy / Ctrl+C —
// one hook per list, its own pill, plain lines out. Shared by the scope,
// notes and exclusions editors.
function useLineRange(id, items) {
  const copyRef = useRef(() => {});
  const { range, rc, clearRange, copied, markCopied } = useCellRange({ id, onCopy: (r) => copyRef.current(r) });
  const [note, setNote] = useState(null);   // "nothing to copy" / "copy failed", shown in the pill
  const text = (rng) => rangeToText(rng, items.map((_, i) => i), (r) => (items[r] ? String(items[r].text || "") : null));
  copyRef.current = async (rng) => {
    const t = text(rng);
    if (!t.trim()) { setNote("Nothing to copy in that block"); return; }
    if (await copyToClipboard(t)) { setNote(null); markCopied(); }
    else setNote("Copy failed — select the lines by hand");
  };
  const pill = range ? (
    <RangePill rsel={id} bottom={130} rows={text(range).split("\n").filter(l => l.trim()).length} cols={1} done={copied} note={note}
      onCopy={() => copyRef.current(range)} onClear={() => { setNote(null); clearRange(); }} />
  ) : null;
  return { rc, pill };
}

function ScopeEditor({ scope, onChange }) {
  const { rc, pill } = useLineRange("scope", scope);
  function setItem(idx, text) {
    const next = scope.slice();
    next[idx] = { ...next[idx], text };
    onChange(next);
  }
  function removeItem(idx) { onChange(scope.filter((_, i) => i !== idx)); }
  function addItem() {
    onChange([...scope, { id: `scope_new_${Date.now()}`, text: "", source: "manual" }]);
  }
  return (
    <div data-rsel="scope" style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {pill}
      {scope.map((s, i) => (
        <React.Fragment key={s.id || i}>
          {s.section && s.section !== scope[i - 1]?.section && (
            <div style={{ marginTop: i ? 10 : 0, padding: "4px 10px", background: C.accentDim, border: `1px solid ${C.border}`, borderRadius: 8, fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: C.accent }}>
              {s.section}
            </div>
          )}
          <div {...rc("scope", i, 0, { display: "flex", gap: 6, alignItems: "flex-start" })}>
            <span style={{ color: C.muted, paddingTop: 8 }}>•</span>
            <textarea
              value={s.text}
              onChange={e => setItem(i, e.target.value)}
              rows={1}
              style={{ ...input(), resize: "vertical", minHeight: 32 }}
            />
            <button onClick={() => removeItem(i)} style={iconBtn()} title="Remove">×</button>
          </div>
        </React.Fragment>
      ))}
      <button onClick={addItem} style={{ ...btn("ghost"), alignSelf: "flex-start", fontSize: 12, padding: "6px 10px" }}>+ Add scope line</button>
    </div>
  );
}

// Compact supplier-column text from a material's price provenance.
function supplierCellText(ps) {
  if (!ps) return "—";
  if (typeof ps === "string") return ps.replace(/_/g, " ");
  const date = ps.date_recorded ? ` · ${String(ps.date_recorded).slice(0, 10)}` : "";
  // Lead with the matched part # — "Q120 · EB Horsman" says push-in at a
  // glance, the fastest wrong-product check the tech has.
  const sku = ps.supplier_sku ? `${ps.supplier_sku} · ` : "";
  switch (ps.tier) {
    case "supplier_prices":  return `${sku}${ps.supplier_name || "supplier"}${date}`;
    case "supplier_quote":   return `${sku}${ps.supplier_name || "supplier"} quote${ps.quote_number ? ` #${ps.quote_number}` : ""}${date}`;
    case "tech_set":         return ps.origin === "manual" ? "Manual · unverified" : "Unverified price";
    case "manual_pin":       return ps.supplier_name || "pinned";
    case "org_price_list":   return "Price List";
    case "logbook_history":  return `Log Book #${ps.quote_num || "?"}${date}`;
    case "allowance":        return "allowance";
    case "breaker_package":  return `🤖 AI estimate${ps.package?.poles ? ` · ${ps.package.poles} poles` : ps.package?.count ? ` · ${ps.package.count} ea` : " · no count"}`;
    case "template_default": return "template";
    case "memory_override":  return "learned rule";
    case "web_placeholder":  return ps.origin === "unknown" ? "Unverified price" : "AI estimate";
    default:                 return ps.tier || "—";
  }
}

// Loose supplier-name match ("City Electric" vs "CITY ELECTRIC SUPPLY SAL/…").
function supNorm(s) { return String(s || "").toLowerCase().replace(/[^a-z0-9]/g, ""); }
function supMatchLoose(a, b) {
  const na = supNorm(a), nb = supNorm(b);
  return !!na && !!nb && (na.includes(nb) || nb.includes(na));
}

function MaterialsEditor({ materials, onChange, copyTargets = [], onCopyToOption, preferredSupplier = null, quoteId = null }) {
  const [searchIdx, setSearchIdx] = useState(null);   // row with the pricebook search open
  const [savePriceIdx, setSavePriceIdx] = useState(null); // row with the save-to-pricebook panel open
  const [suppliers, setSuppliers] = useState(null);
  // One-click "add to the price book" offer, raised on cost-blur for a
  // hand-priced line the matcher never resolved. Techs believe typing a price
  // IS saving the product — it isn't (that's a tech_set edit local to this
  // quote), which is how the KCG-11000I-TF generator got hand-entered on two
  // quotes while every new draft still guessed "Generac".
  const [saveOffer, setSaveOffer] = useState(null);   // { idx, busy?, err? }
  const offerDismissedRef = useRef(new Set());        // row ids the tech waved off this session
  // Pre-edit pin snapshots: retyping a description clears master_product_id on
  // the FIRST keystroke, so by blur time the "what was it pinned to" half of
  // the correction signal is gone unless captured here (keyed by row id).
  const retypeRef = useRef(new Map());
  // Row selection (by material id) → the action bar: copy to another option,
  // or group the selected lines into a collapsible "lot" under an anchor line.
  const [selected, setSelected] = useState(() => new Set());
  const [lotLabel, setLotLabel] = useState("Breakers");
  const [barMsg, setBarMsg] = useState(null);
  // Pending "fix the rest of the quote too?" offer from the last pick.
  const [propose, setPropose] = useState(null);
  const [bulkMarkup, setBulkMarkup] = useState(""); // markup % to apply to all / selected
  const dragFromRef = useRef(null);
  // Spreadsheet-style block selection: press a cell, drag across and down,
  // Copy / Ctrl+C. Every cell is an <input>, so a native selection could never
  // span rows — this is how "grab the qty and description column" works.
  const copyRangeRef = useRef(() => {});
  const { range: cellRange, rc, clearRange, copied: rangeCopied, markCopied } = useCellRange({ id: "qmat", onCopy: (r) => copyRangeRef.current(r) });
  // Inline type-ahead on unpriced rows: typing in the description searches the
  // price book directly (no 🔍 needed); options sorted most-recent price first.
  const [inline, setInline] = useState(null);           // { idx, term }
  const [inlineResults, setInlineResults] = useState(null);
  const [inlineBusy, setInlineBusy] = useState(false);
  const suggestable = (m) => !m.master_product_id && m?._price_source?.tier !== "supplier_prices";
  useEffect(() => {
    if (!inline || String(inline.term || "").trim().length < 3) { setInlineResults(null); return; }
    const t = setTimeout(async () => {
      setInlineBusy(true);
      try {
        const r = await apiFetch("/api/pricebook", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "quoteSearch", term: inline.term.trim(), limit: 12 }),
        });
        const j = await r.json();
        const flat = (Array.isArray(j.results) ? j.results : [])
          .flatMap(p => (p.prices || []).map(pr => ({ p, pr })))
          .sort((a, b) => new Date(b.pr.date_recorded || 0) - new Date(a.pr.date_recorded || 0))
          .slice(0, 12);
        setInlineResults(flat);
      } catch { setInlineResults([]); }
      finally { setInlineBusy(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [inline]);

  const rowId = (m, i) => m.id || `row_${i}`;
  const isSel = (m, i) => selected.has(rowId(m, i));
  const toggleSel = (m, i) => setSelected(prev => {
    const next = new Set(prev);
    const k = rowId(m, i);
    if (next.has(k)) next.delete(k); else next.add(k);
    return next;
  });
  const clearSel = () => { setSelected(new Set()); setBarMsg(null); };

  async function ensureSuppliers() {
    if (suppliers) return;
    try {
      const r = await apiFetch("/api/pricebook", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "listSuppliers" }),
      });
      const j = await r.json();
      setSuppliers(Array.isArray(j.suppliers) && j.suppliers.length ? j.suppliers : ["City Electric", "EB Horsman", "Wesco"]);
    } catch { setSuppliers(["City Electric", "EB Horsman", "Wesco"]); }
  }

  function setRow(idx, patch) {
    const next = materials.slice();
    // A bare typed cost on a red AI-guess line becomes tech_set + pinned; a
    // patch that brings its own _price_source (a pick, a 💾 save) keeps it —
    // the typed-cost stamp used to clobber a pick's supplier provenance. Rule
    // + its regression test live in lib/materialRowPatch.js.
    next[idx] = patchMaterialRow(next[idx], patch);
    onChange(next);
  }
  // Stamp a markup % onto every material, or only the selected ones — one number
  // in the toolbar instead of retyping the Markup column per line. Lot anchors
  // carry no price, so leave those untouched.
  function applyBulkMarkup(onlySelected) {
    const pct = Number(bulkMarkup);
    if (!Number.isFinite(pct) || bulkMarkup === "") return;
    const next = materials.map((m, i) => {
      if (m._lot) return m; // a collapsed-lot anchor line isn't priced
      if (onlySelected && !isSel(m, i)) return m;
      return { ...m, markup_pct: pct };
    });
    onChange(next);
    const n = onlySelected ? materials.filter((m, i) => isSel(m, i) && !m._lot).length : materials.filter(m => !m._lot).length;
    setBarMsg(`Set ${pct}% markup on ${n} line${n === 1 ? "" : "s"} ✓`);
    setTimeout(() => setBarMsg(null), 2500);
  }
  function removeRow(idx) {
    const gone = materials[idx];
    let next = materials.filter((_, i) => i !== idx);
    // Removing a lot's anchor ungroups its members (they stay as plain lines).
    if (gone?._lot) next = next.map(m => (m._lot_member === gone._lot.id ? { ...m, _lot_member: undefined } : m));
    setSaveOffer(null); // the offer targets a row INDEX — stale after a delete
    onChange(next);
  }
  function addRow() {
    onChange([...materials, {
      id: `mat_new_${Date.now()}`,
      qty: 1, unit: "ea",
      sku: "", description: "",
      our_cost: 0, markup_pct: 35, pst_pct: 7,
      source: "manual",
    }]);
  }

  // ── Drag to reorder (⠿ handle). A collapsed lot's anchor drags its hidden
  // members along as a block, so the package stays intact.
  function blockAt(idx) {
    const m = materials[idx];
    if (m?._lot?.collapsed) {
      const ids = new Set([idx]);
      materials.forEach((x, j) => { if (x._lot_member === m._lot.id) ids.add(j); });
      return ids;
    }
    return new Set([idx]);
  }
  function moveRow(from, to) {
    if (from == null || to == null || from === to) return;
    setSaveOffer(null); // index-addressed offer goes stale on reorder
    const block = blockAt(from);
    const moving = materials.filter((_, i) => block.has(i));
    const rest = materials.filter((_, i) => !block.has(i));
    // Where the drop target lands within the remaining rows:
    const target = materials[to];
    let insertAt = rest.findIndex(m => m === target);
    if (insertAt < 0) insertAt = rest.length;
    if (to > from) insertAt += 1;
    onChange([...rest.slice(0, insertAt), ...moving, ...rest.slice(insertAt)]);
  }

  // ── Lots (compressed groups): members hide behind ONE summed "1 lot" row
  // under the anchor line. Pure display grouping — every line still prices
  // and totals individually, so the quote math never changes.
  const selIdxs = materials.map((m, i) => (isSel(m, i) ? i : -1)).filter(i => i >= 0);
  // Anchor = the nearest non-selected line above the first selected one
  // (select the breakers → the panel above becomes the anchor).
  let anchorIdx = -1;
  if (selIdxs.length) {
    for (let i = selIdxs[0] - 1; i >= 0; i--) {
      if (!isSel(materials[i], i) && !materials[i]._lot_member) { anchorIdx = i; break; }
    }
  }
  const anySelInLot = selIdxs.some(i => materials[i]._lot_member || materials[i]._lot);
  function groupSelected() {
    if (anchorIdx < 0 || !selIdxs.length) return;
    const gid = `lot_${Date.now()}`;
    const label = (lotLabel || "Materials").trim() || "Materials";
    const anchor = materials[anchorIdx];
    const members = selIdxs.map(i => ({ ...materials[i], _lot_member: gid, _lot: undefined }));
    // Normalize order: anchor, then its members right below it.
    const rest = materials.filter((m, i) => i !== anchorIdx && !selIdxs.includes(i));
    const anchorPos = materials.slice(0, anchorIdx).filter((m, i) => !selIdxs.includes(i)).length;
    const next = [...rest.slice(0, anchorPos), { ...anchor, _lot: { id: gid, label, collapsed: true } }, ...members, ...rest.slice(anchorPos)];
    onChange(next);
    clearSel();
  }
  function ungroupSelected() {
    onChange(materials.map((m, i) => {
      if (!isSel(m, i)) return m;
      const { _lot, _lot_member, ...restM } = m;
      return restM;
    }));
    clearSel();
  }
  function copySelected(target) {
    if (!onCopyToOption || !selIdxs.length) return;
    // A selected collapsed anchor brings its hidden members along — copying
    // the panel copies the whole package. Lot ids are remapped so the copy
    // is its own group in the target option.
    const idxSet = new Set(selIdxs);
    for (const i of selIdxs) {
      const m = materials[i];
      if (m._lot) materials.forEach((x, j) => { if (x._lot_member === m._lot.id) idxSet.add(j); });
    }
    const lotMap = {};
    const rows = materials.filter((_, i) => idxSet.has(i)).map(m => {
      const c = { ...m };
      if (c._lot) { lotMap[c._lot.id] = `${c._lot.id}_c${Date.now()}`; c._lot = { ...c._lot, id: lotMap[c._lot.id] }; }
      return c;
    }).map(c => (c._lot_member ? { ...c, _lot_member: lotMap[c._lot_member] || undefined } : c));
    onCopyToOption(rows, target.idx);
    setBarMsg(`Copied ${rows.length} line${rows.length > 1 ? "s" : ""} to ${target.label} ✓`);
  }

  // A hand-priced line the book knows nothing about: tier tech_set (the tech
  // typed the cost), no product pin, and no book_match (when the server keeps
  // your price but knows the book row, the product IS in the book already —
  // offering to "add" it would mint a duplicate).
  function bookOfferable(m) {
    return m?._price_source?.tier === "tech_set"
      && !m.master_product_id
      && !m._price_source?.book_match
      && Number(m.our_cost) > 0
      && String(m.description || "").trim().length >= 4;
  }

  // One-click accept on the offer banner: push the line into the price book as
  // typed. Supplier is stamped "Tech verified" — honest provenance for a price
  // whose real vendor is unknown; the matcher trusts it anyway because
  // source_type 'quote_editor' passes the retrieval allowlist regardless of
  // supplier name. The ✏️ path opens the full panel for real attribution.
  async function quickSaveToBook(idx) {
    const m = materials[idx];
    if (!m || !Number(m.our_cost)) return;
    setSaveOffer({ idx, busy: true });
    try {
      const r = await apiFetch("/api/pricebook", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "upsertQuotePrice",
          master_product_id: null,
          description: cleanCatalogDescription(m.description),
          supplier_name: "Tech verified",
          unit_price: Number(m.our_cost),
          unit: m.unit || "ea",
        }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      setRow(idx, {
        master_product_id: j.master_product_id,
        _price_source: {
          tier: "supplier_prices", supplier_name: j.supplier_name, date_recorded: j.date_recorded, confidence: "high",
          flag: j.created_new_product ? "saved as a NEW price book item — future quotes price it automatically" : null,
        },
      });
      setSaveOffer(null);
      setBarMsg("Added to the price book ✓ — future quotes price this automatically");
      setTimeout(() => setBarMsg(null), 3500);
    } catch (e) {
      // Most common failure is the server's pollution gate ("reads like a
      // quote line") — keep the banner up with the reason; ✏️ opens the full
      // panel where the description can be edited before saving.
      setSaveOffer({ idx, err: e.message });
    }
  }

  // Pricebook pick → load price + provenance onto the row. Manual cost edits
  // afterwards keep the supplier tag but flag it as edited.
  function applyPick(idx, product, price) {
    // A pick is the cleanest training signal there is: the tech looked at what
    // the matcher chose and chose something else. Nothing recorded it before,
    // so "Dylan has corrected this same thing four times" was unanswerable.
    // Fire-and-forget — a logging hiccup must never block the edit.
    const prev = materials[idx] || {};
    // A retype that ends in a pick is ONE correction, not two: fold the
    // pre-edit pin (erased by the description onChange) back into the "from"
    // side and drop the pending retype snapshot so blur doesn't log it again.
    const pinSnap = retypeRef.current.get(rowId(prev, idx));
    retypeRef.current.delete(rowId(prev, idx));
    apiFetch("/api/quote-brain", {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        action: "logMatchCorrection",
        surface: "quote",
        quote_id: quoteId || null,
        from: {
          description: prev.description || null,
          tier: prev._price_source?.tier || null,
          our_cost: Number(prev.our_cost) || null,
          supplier_name: prev._price_source?.supplier_name || null,
          supplier_sku: prev._price_source?.supplier_sku || null,
          master_product_id: prev.master_product_id || pinSnap?.master_product_id || null,
        },
        to: {
          description: price.display_description || price.supplier_description || product.description || null,
          our_cost: Number(price.unit_price) || null,
          supplier_name: price.supplier_name || null,
          supplier_sku: price.supplier_sku || null,
          master_product_id: product.master_product_id || null,
        },
      }),
    }).catch(() => { /* never block the edit on a log write */ });
    setRow(idx, {
      // display_description first: on most cable rows the size lives ONLY in
      // the supplier sku, and a size-less quote line re-enters the matcher
      // blind on every later reprice (the "ACWU; ARMOURED ALUMINUM CABLE"
      // lesson — server composes "3C #6 …" from the sku's parsed identity).
      description: price.display_description || price.supplier_description || product.description || materials[idx]?.description,
      our_cost: Number(price.unit_price) || 0,
      master_product_id: product.master_product_id || null,
      // Same builder the propagated siblings use — carries supplier_sku +
      // supplier_description so the line shows its part # and the order /
      // takeoff sheets can link back to the supplier row.
      _price_source: pickPriceSource(product, price),
    });
    setSearchIdx(null);
    // Offer the same fix on the rest of the quote. Never applied silently —
    // the tech confirms, and Undo covers it (see propagatePick.js).
    const sibs = siblingLinesFor(materials, idx, prev, { master_product_id: product.master_product_id || null });
    // Always raise the bar (even with no siblings) — it also carries the
    // "Teach it" affordance that turns this one pick into a durable match rule.
    setPropose({ idxs: sibs, product, price, before: prev, teachOpen: false, teachText: "" });
  }

  // 💡 Teach it: this wording should ALWAYS resolve to the picked product (and
  // never to what it wrongly matched). Server-side teach gate applies — the
  // matcher rule steers every future quote. The auto-veto for the wrong product
  // was already written by logMatchCorrection; this activates the alias too.
  async function teachPick() {
    if (!propose) return;
    const p = propose;
    try {
      const res = await apiFetch("/api/quote-brain", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "teachMatchRule",
          quote_id: quoteId || null,
          phrase: p.before?.description || "",
          wrong_master_product_id: p.before?.master_product_id || null,
          to: {
            description: p.price?.display_description || p.price?.supplier_description || p.product?.description || null,
            master_product_id: p.product?.master_product_id || null,
            supplier_sku: p.price?.supplier_sku || null,
          },
          explanation: (p.teachText || "").trim() || null,
        }),
      });
      const j = await res.json();
      if (!res.ok || j.ok === false) throw new Error(j.error || `HTTP ${res.status}`);
      setPropose(null);
      setBarMsg("Rule saved — this wording now always matches that product ✓");
    } catch (e) {
      setBarMsg(/401|403|teach/i.test(String(e.message)) ? "Only owners can save match rules — the correction itself was still logged." : `Rule save failed: ${e.message}`);
    }
  }

  // Apply the last pick to its sibling lines. Siblings are RECOMPUTED here,
  // not read from the offer: rows can be added, deleted or reordered between
  // the pick and the confirm, and stale indices would patch the wrong lines.
  function applyPropagation() {
    if (!propose) return;
    const idxs = siblingLinesFor(materials, -1, propose.before, { master_product_id: propose.product.master_product_id || null });
    if (!idxs.length) { setPropose(null); setBarMsg("Those lines have already changed — nothing left to fix ✓"); return; }
    const patch = pickPatch(propose.product, propose.price);
    onChange(materials.map((m, i) => (idxs.includes(i) ? { ...m, ...patch } : m)));
    setPropose(null);
    setBarMsg(`Fixed ${idxs.length} more line${idxs.length === 1 ? "" : "s"} ✓`);
  }

  // Selection action bar (shown when rows are selected).
  const actionBar = selected.size > 0 && (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "8px 10px", marginBottom: 8, border: `1px solid ${C.accent}`, borderRadius: 8, background: "rgba(165,206,58,0.07)", fontSize: 12 }}>
      <span style={{ fontWeight: 700 }}>{selected.size} selected</span>
      {copyTargets.map(t => (
        <button key={t.idx} style={{ ...btn(), fontSize: 12, padding: "5px 9px" }} onClick={() => copySelected(t)} title={`Copy the selected lines (a collapsed lot brings its members) to "${t.label}"`}>
          ⧉ → {t.label.length > 26 ? t.label.slice(0, 26) + "…" : t.label}
        </button>
      ))}
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        <input
          style={{ ...inputCell(), width: 110, fontSize: 12 }}
          value={lotLabel}
          onChange={e => setLotLabel(e.target.value)}
          placeholder="Lot label"
          title="Label for the compressed lot line"
        />
        <button
          style={{ ...btn(), fontSize: 12, padding: "5px 9px", opacity: anchorIdx >= 0 ? 1 : 0.4 }}
          disabled={anchorIdx < 0}
          onClick={groupSelected}
          title={anchorIdx >= 0
            ? `Compress the selected lines into "1 lot — ${lotLabel || "Materials"}" under "${(materials[anchorIdx]?.description || "").slice(0, 40)}" — totals unchanged, expandable any time`
            : "Needs a non-selected line above the selection to attach the lot to"}
        >
          ▣ Lot under line above
        </button>
      </span>
      {anySelInLot && (
        <button style={{ ...btn("ghost"), fontSize: 12, padding: "5px 9px" }} onClick={ungroupSelected} title="Remove the selected lines/anchors from their lot">Ungroup</button>
      )}
      <button style={{ ...btn("ghost"), fontSize: 12, padding: "5px 9px" }} onClick={clearSel}>✕ Clear</button>
      {barMsg && <span style={{ color: C.good, fontSize: 12 }}>{barMsg}</span>}
    </div>
  );

  const selCount = materials.filter((m, i) => isSel(m, i) && !m._lot).length;
  // Tax basis for the DISPLAYED cost figures (column + footer). Default is
  // ex-tax because that is how suppliers quote, so their number and ours line
  // up without arithmetic. Shared with the takeoff via one localStorage key so
  // the two screens never disagree about what basis you are looking at.
  const [taxBasis, setTaxBasis] = useState(() => {
    try { const j = JSON.parse(localStorage.getItem(MAT_TAX_KEY) || "{}"); return { pst: !!j.pst, gst: !!j.gst }; }
    catch { return { pst: false, gst: false }; }
  });
  const changeTaxBasis = useCallback((patch) => {
    setTaxBasis(prev => {
      const next = { ...prev, ...patch };
      try { localStorage.setItem(MAT_TAX_KEY, JSON.stringify(next)); } catch { /* private window */ }
      return next;
    });
  }, []);
  // Which rows the footer adds up. Nothing ticked → every line (the whole
  // quote). Anything ticked → only those, PLUS the hidden members of a ticked
  // COLLAPSED lot anchor: those lines are the anchor's package and the tech
  // cannot tick what the lot is hiding, so leaving them out would under-report
  // the very number they ticked the anchor to see. Expanded lots are not
  // expanded here — their members are on screen and tickable individually.
  const { footSumIdxs, footHiddenLots } = (() => {
    const ticked = materials.map((m, i) => (isSel(m, i) ? i : -1)).filter(i => i >= 0);
    if (!ticked.length) return { footSumIdxs: null, footHiddenLots: 0 };
    const idxs = new Set(ticked);
    // Collect the ticked collapsed lots first, then walk the rows ONCE — a
    // callback inside the loop would close over the counter (no-loop-func).
    const hiddenLots = new Set();
    for (const i of ticked) {
      const lot = materials[i] && materials[i]._lot;
      if (lot && lot.collapsed) hiddenLots.add(lot.id);
    }
    let hidden = 0;
    if (hiddenLots.size) {
      for (let j = 0; j < materials.length; j++) {
        const mem = materials[j]._lot_member;
        if (mem && hiddenLots.has(mem) && !idxs.has(j)) { idxs.add(j); hidden++; }
      }
    }
    return { footSumIdxs: [...idxs].sort((a, b) => a - b), footHiddenLots: hidden };
  })();
  // "You fixed one — the same row is on N others." Offered, never auto-applied.
  const propagateBar = propose && (
    <div style={{ padding: "8px 10px", marginBottom: 8, border: `1px solid ${C.warn}`, borderRadius: 8, background: "rgba(245,158,11,0.08)", fontSize: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        {propose.idxs.length > 0 ? (
          <span>
            <b>{siblingReason(propose.idxs.length, propose.before)}</b> — fix {propose.idxs.length === 1 ? "it" : "them"} the same way?
            <span style={{ color: C.muted }}> → {String(propose.price.display_description || propose.price.supplier_description || propose.product.description || "").slice(0, 52)}</span>
          </span>
        ) : (
          <span style={{ color: C.muted }}>
            Matched → {String(propose.price.display_description || propose.price.supplier_description || propose.product.description || "").slice(0, 60)}
          </span>
        )}
        <div style={{ flex: 1 }} />
        <button style={{ ...btn("ghost"), fontSize: 12, padding: "5px 9px" }} onClick={() => setPropose(p => ({ ...p, teachOpen: !p.teachOpen }))}>
          💡 Teach it
        </button>
        {propose.idxs.length > 0 && (
          <button style={{ ...btn("ghost"), fontSize: 12, padding: "5px 9px" }} onClick={() => setPropose(null)}>No, just this one</button>
        )}
        {propose.idxs.length > 0 ? (
          <button style={{ ...btn(), fontSize: 12, padding: "5px 9px" }} onClick={applyPropagation}>
            Fix {propose.idxs.length === 1 ? "it" : `all ${propose.idxs.length}`}
          </button>
        ) : (
          <button style={{ ...btn("ghost"), fontSize: 12, padding: "5px 9px" }} onClick={() => setPropose(null)}>Done</button>
        )}
      </div>
      {propose.teachOpen && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
          <span style={{ color: C.muted }}>Save as a permanent match rule — why was the old match wrong?</span>
          <input
            style={{ ...input(), flex: 1, minWidth: 180, fontSize: 12 }}
            placeholder={`e.g. a maintenance kit is consumables, not the machine`}
            value={propose.teachText}
            onChange={e => setPropose(p => ({ ...p, teachText: e.target.value }))}
          />
          <button style={{ ...btn(), fontSize: 12, padding: "5px 9px" }} onClick={teachPick}>Save rule</button>
        </div>
      )}
    </div>
  );

  const markupBar = (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "6px 2px", marginBottom: 6, fontSize: 12 }}>
      <span style={{ color: C.muted }}>Set markup %:</span>
      <input
        type="number" step="1" value={bulkMarkup}
        onChange={e => setBulkMarkup(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter") applyBulkMarkup(selCount > 0); }}
        placeholder="e.g. 30"
        style={{ ...inputCell(), width: 80, fontSize: 12 }}
        title="Type a markup %, then apply it to every material line — or to just the selected lines"
      />
      <button
        style={{ ...btn(), fontSize: 12, padding: "5px 9px", opacity: bulkMarkup === "" ? 0.4 : 1 }}
        disabled={bulkMarkup === ""}
        onClick={() => applyBulkMarkup(false)}
        title="Apply this markup % to every material line"
      >Apply to all ({materials.filter(m => !m._lot).length})</button>
      {selCount > 0 && (
        <button
          style={{ ...btn("ghost"), fontSize: 12, padding: "5px 9px", opacity: bulkMarkup === "" ? 0.4 : 1 }}
          disabled={bulkMarkup === ""}
          onClick={() => applyBulkMarkup(true)}
          title="Apply this markup % only to the selected lines"
        >Apply to {selCount} selected</button>
      )}
      {barMsg && selected.size === 0 && <span style={{ color: C.good, fontSize: 12 }}>{barMsg}</span>}
    </div>
  );

  // ── block selection: what each column reads as when copied ────────────────
  // Ordinals follow the cells in render order (the ordinal passed to rc()).
  // Money copies as plain numbers so a sheet can add it up.
  const matCellText = (m, c) => {
    switch (c) {
      case 0: return m.qty ?? "";
      case 1: return m.unit || "";
      case 2: return m.description || "";
      case 3: { const t = supplierCellText(m._price_source); return t === "—" ? "" : t; }
      case 4: return m.our_cost ?? "";
      case 5: { const v = ourTotalShown(m, taxBasis); return v ? Number(v).toFixed(2) : ""; }
      case 6: return m.markup_pct ?? "";
      case 7: return m.pst_pct ?? "";
      case 8: return m.client_each != null ? Number(m.client_each).toFixed(2) : "";
      case 9: return m.client_total != null ? Number(m.client_total).toFixed(2) : "";
      default: return "";
    }
  };
  // Members hidden inside a collapsed lot are not on screen and stay out.
  const matRangeText = (rng) => rangeToText(rng, materials.map((_, i) => i), (r, c) => {
    const m = materials[r];
    if (!m) return null;
    if (m._lot_member) {
      const anchor = materials.find(a => a._lot && a._lot.id === m._lot_member);
      if (anchor?._lot?.collapsed) return null;
    }
    return matCellText(m, c);
  });
  copyRangeRef.current = async (rng) => {
    const text = matRangeText(rng);
    if (!text) { setBarMsg("Nothing to copy in that block."); return; }
    const ok = await copyToClipboard(text);
    if (ok) markCopied();
    else setBarMsg("Copy failed — open 🔤 Text view and select it by hand.");
  };
  const rangeSize = cellRange ? { rows: matRangeText(cellRange).split("\n").filter(Boolean).length, cols: cellRange.c1 - cellRange.c0 + 1 } : null;

  return (
    <div data-rsel="qmat" style={{ overflowX: "auto" }}>
      {/* bottom 130: the mirror-undo toast lives at 76 — both can show at once */}
      {cellRange && rangeSize && (
        <RangePill rsel="qmat" bottom={130} rows={rangeSize.rows} cols={rangeSize.cols} done={rangeCopied} onCopy={() => copyRangeRef.current(cellRange)} onClear={clearRange} />
      )}
      {propagateBar}
      {markupBar}
      {actionBar}
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
        <thead>
          <tr style={{ color: C.muted, textTransform: "uppercase", fontSize: 10, letterSpacing: "0.06em" }}>
            <th style={th(24)}></th>
            <th style={th(26)}>
              <input
                type="checkbox"
                checked={selected.size > 0 && selected.size === materials.length}
                onChange={e => setSelected(e.target.checked ? new Set(materials.map((m, i) => rowId(m, i))) : new Set())}
                title="Select all"
              />
            </th>
            <th style={th(60)}>Qty</th>
            <th style={th(50)}>Unit</th>
            <th style={th("auto")}>Description</th>
            <th style={th(130)}>Supplier</th>
            <th style={th(110)} title="What ONE costs us, as typed — before any tax">Our cost</th>
            <th style={th(120)} title="Qty × our cost each. Shown ex tax by default so it matches a supplier's quote line; use the tax boxes at the bottom of the table to add PST or GST.">
              Our total<div style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, color: taxBasis.pst || taxBasis.gst ? C.accent : C.muted }}>{taxBasisLabel(taxBasis)}</div>
            </th>
            <th style={th(80)}>Markup %</th>
            <th style={th(60)}>PST %</th>
            <th style={th(110)}>Client each</th>
            <th style={th(110)}>Client total</th>
            <th style={th(40)}></th>
          </tr>
        </thead>
        <tbody>
          {materials.map((m, i) => {
            // Members of a COLLAPSED lot hide behind the anchor's summed row.
            if (m._lot_member) {
              const anchor = materials.find(a => a._lot && a._lot.id === m._lot_member);
              if (anchor?._lot?.collapsed) return null;
            }
            const lot = m._lot;
            const lotMembers = lot ? materials.filter(x => x._lot_member === lot.id) : [];
            const lotCost = lotMembers.reduce((s, x) => s + (Number(x.our_cost) || 0) * (Number(x.qty) || 1), 0);
            const lotClient = lotMembers.reduce((s, x) => s + (Number(x.client_total) || 0), 0);
            const lotCostPst = lotMembers.reduce((s, x) => s + ourTotalShown(x, taxBasis), 0);
            return (
            <React.Fragment key={m.id || i}>
              {m.section && m.section !== materials[i - 1]?.section && (
                <tr>
                  <td colSpan={MAT_COLSPAN} {...rc("mat", i, null, { padding: "12px 8px 6px", fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: C.accent, borderTop: `2px solid ${C.border}` })}>
                    {m.section}
                    <span style={{ color: C.muted, fontWeight: 500, textTransform: "none", letterSpacing: 0, marginLeft: 10 }}>
                      materials ${materials.filter(x => x.section === m.section).reduce((s2, x) => s2 + (Number(x.client_total) || 0), 0).toLocaleString("en-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </td>
                </tr>
              )}
              <tr
                style={{ borderTop: `1px solid ${C.border}`, ...(m._lot_member ? { background: "rgba(165,206,58,0.04)" } : {}) }}
                onDragOver={e => e.preventDefault()}
                // preventDefault is NOT optional here: without it a dropped
                // FILE falls through to the browser, which navigates the tab to
                // it and takes the unsaved draft with it. (The option bar's
                // drop handler at ~1832 always got this right.) Row reordering
                // only proceeds when a row drag actually started.
                onDrop={e => {
                  e.preventDefault();
                  if (dragFromRef.current != null) moveRow(dragFromRef.current, i);
                  dragFromRef.current = null;
                }}
              >
                <td
                  style={{ ...td(), cursor: "grab", color: C.muted, userSelect: "none" }}
                  draggable
                  onDragStart={e => { dragFromRef.current = i; e.dataTransfer.effectAllowed = "move"; }}
                  // Clear the ref when a drag ENDS, however it ended. Without
                  // this an abandoned drag (Esc, or a drop outside the table)
                  // leaves an index behind, and the next thing dropped on a row
                  // — including a FILE — silently reorders that line.
                  onDragEnd={() => { dragFromRef.current = null; }}
                  title="Drag to reorder (a collapsed lot moves as a block)"
                >⠿</td>
                <td style={td()}><input type="checkbox" checked={isSel(m, i)} onChange={() => toggleSel(m, i)} /></td>
                <td {...rc("mat", i, 0, td())}><input style={inputCell()} type="number" step="0.5"      value={m.qty       ?? ""} onChange={e => setRow(i, { qty:        e.target.value === "" ? "" : Number(e.target.value) })} /></td>
                <td {...rc("mat", i, 1, td())}><input style={inputCell()} type="text"                    value={m.unit      ?? ""} onChange={e => setRow(i, { unit:       e.target.value })} /></td>
                <td {...rc("mat", i, 2, { ...td(), whiteSpace: "nowrap" })}>
                  <div style={{ display: "flex", gap: 4, alignItems: "center", position: "relative" }}>
                    <input
                      style={{ ...inputCell(), minWidth: 180 }} type="text" value={m.description ?? ""}
                      // Editing the description un-pins any picked/saved product id —
                      // a stale id would let a 💾 save overwrite a DIFFERENT product's
                      // price (the server re-verifies too; this keeps the UI honest).
                      // The pre-edit pin is snapshotted first: the un-pin is silent,
                      // and the blur handler turns it into a retype correction signal.
                      onChange={e => {
                        if (m.master_product_id && !retypeRef.current.has(rowId(m, i))) {
                          retypeRef.current.set(rowId(m, i), {
                            description: m.description || null,
                            tier: m._price_source?.tier || null,
                            our_cost: Number(m.our_cost) || null,
                            supplier_name: m._price_source?.supplier_name || null,
                            supplier_sku: m._price_source?.supplier_sku || null,
                            master_product_id: m.master_product_id,
                          });
                        }
                        setRow(i, { description: e.target.value, ...(m.master_product_id ? { master_product_id: null } : {}) });
                        if (suggestable(m)) setInline({ idx: i, term: e.target.value });
                      }}
                      onFocus={() => { if (suggestable(m) && String(m.description || "").trim().length >= 3) setInline({ idx: i, term: m.description }); }}
                      onBlur={() => {
                        setTimeout(() => setInline(cur => (cur && cur.idx === i ? null : cur)), 200);
                        const pin = retypeRef.current.get(rowId(m, i));
                        if (!pin) return;
                        retypeRef.current.delete(rowId(m, i));
                        if (String(m.description || "").trim() === String(pin.description || "").trim()) {
                          // Typed and reverted to the exact original wording — same
                          // words, same product. Restore the pin instead of logging a
                          // retype that never really happened.
                          if (!m.master_product_id) setRow(i, { master_product_id: pin.master_product_id });
                          return;
                        }
                        // The pin was cleared by a real rewording and nothing was
                        // picked in its place — the matcher's choice was wrong enough
                        // to retype over. to: no destination; the nightly match-learn
                        // proposer reads these alongside pick corrections.
                        apiFetch("/api/quote-brain", {
                          method: "POST", headers: { "Content-Type": "application/json" },
                          body: JSON.stringify({
                            action: "logMatchCorrection",
                            surface: "quote",
                            quote_id: quoteId || null,
                            reason: "retype",
                            from: pin,
                            to: { description: String(m.description || "").trim() || null, master_product_id: null },
                          }),
                        }).catch(() => { /* never block the edit on a log write */ });
                      }}
                      onKeyDown={e => { if (e.key === "Escape") setInline(null); }}
                      placeholder="Item description — type to search the book"
                    />
                    {inline?.idx === i && (inlineResults || inlineBusy) && (
                      <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 60, background: C.surface, border: `1px solid ${C.accent}`, borderRadius: 8, minWidth: 420, maxWidth: 560, maxHeight: 260, overflowY: "auto", boxShadow: "0 8px 24px rgba(0,0,0,0.55)" }}>
                        {inlineBusy && <div style={{ padding: 8, fontSize: 11, color: C.muted }}>Searching the book…</div>}
                        {!inlineBusy && inlineResults?.length === 0 && (
                          <div style={{ padding: 8, fontSize: 11, color: C.muted }}>No book match — set your cost, then 💾 to add it.</div>
                        )}
                        {!inlineBusy && (inlineResults || []).map(({ p, pr }, k) => (
                          <button
                            key={k}
                            // mousedown beats the input's onBlur close
                            onMouseDown={e => { e.preventDefault(); applyPick(i, p, pr); setInline(null); }}
                            style={{ display: "block", width: "100%", textAlign: "left", border: "none", borderBottom: `1px solid ${C.border}`, background: "transparent", color: C.text, padding: "7px 10px", fontSize: 12, fontFamily: "inherit", cursor: "pointer", whiteSpace: "normal" }}
                          >
                            <span style={{ fontWeight: 700 }}>{fmt$(pr.unit_price)}</span>
                            <span> {pr.supplier_name}</span>
                            <span style={{ color: C.muted, fontSize: 11 }}> · {(pr.date_recorded || "").slice(0, 10)}</span>
                            <div style={{ fontSize: 11, color: C.muted }}>{pr.display_description || pr.supplier_description || p.description}{p.sku ? ` · ${p.sku}` : ""}</div>
                          </button>
                        ))}
                      </div>
                    )}
                    <button onClick={() => setSearchIdx(i)} style={{ ...iconBtn(), flexShrink: 0 }} title="Search the price book for this item">🔍</button>
                    {lot && (
                      <button
                        onClick={() => setRow(i, { _lot: { ...lot, collapsed: !lot.collapsed } })}
                        style={{ ...iconBtn(), flexShrink: 0, width: "auto", padding: "0 6px", fontSize: 10, color: C.accent, borderColor: C.accent }}
                        title={lot.collapsed ? `Expand the ${lotMembers.length} lines in this lot` : "Compress this lot back to one summed line"}
                      >
                        {lot.collapsed ? "▸" : "▾"} {lot.label} ({lotMembers.length})
                      </button>
                    )}
                  </div>
                  {/* Server-found book candidates on an unpriced line — the same
                      search the 🔍 runs, already done at gen time. One tap = the
                      same pick (and the same match-correction log) as the modal. */}
                  {m._price_source?.tier === "web_placeholder" && Array.isArray(m._price_source?.search_candidates) && m._price_source.search_candidates.length > 0 && (
                    <div style={{ display: "flex", gap: 4, marginTop: 3, flexWrap: "wrap" }}>
                      {m._price_source.search_candidates.slice(0, 3).map((c, k) => (
                        <button
                          key={k}
                          onClick={() => applyPick(i,
                            { master_product_id: c.master_product_id, description: c.description, sku: c.sku },
                            { supplier_name: c.supplier_name, unit_price: c.unit_price, date_recorded: c.date_recorded, supplier_sku: c.supplier_sku, supplier_description: c.supplier_description, display_description: c.display_description })}
                          title={`${c.supplier_description || c.description}${c.times_purchased ? ` · bought ${c.times_purchased}×` : ""}`}
                          style={{ border: `1px solid ${C.accent}`, background: "transparent", color: C.accent, borderRadius: 6, padding: "1px 6px", fontSize: 10, cursor: "pointer", fontFamily: "inherit", maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                        >
                          {fmt$(c.unit_price)} · {String(c.display_description || c.description || "").slice(0, 30)}
                        </button>
                      ))}
                    </div>
                  )}
                </td>
                <td {...rc("mat", i, 3, { ...td(), fontSize: 11, whiteSpace: "nowrap", ...priceSourceStyle(m._price_source) })}>
                  <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                    <span title={typeof m._price_source === "object" ? (m._price_source?.flag || "") : ""}>{supplierCellText(m._price_source)}</span>
                    {preferredSupplier && m._price_source?.tier === "supplier_prices" && m._price_source?.supplier_name
                      && !supMatchLoose(m._price_source.supplier_name, preferredSupplier) && (
                      <span style={{ color: C.warn, fontWeight: 700, fontSize: 10, border: `1px solid ${C.warn}`, borderRadius: 6, padding: "0 4px", flexShrink: 0 }}
                        title={`Priced from ${m._price_source.supplier_name}, but this quote's preferred supplier is ${preferredSupplier} — hit "↻ Price from supplier" or 🔍 to switch before sending.`}>
                        ≠ {preferredSupplier.split(" ")[0]}
                      </span>
                    )}
                    <button onClick={() => { ensureSuppliers(); setSavePriceIdx(savePriceIdx === i ? null : i); }}
                      style={{ ...iconBtn(), flexShrink: 0, width: 24, height: 24, fontSize: 11 }}
                      title="Save this price to the price book as the default for future quotes">💾</button>
                    {m._price_source?.tier !== "web_placeholder" && (
                      <button onClick={() => setRow(i, { _price_source: aiEstimateSource(m.our_cost), _tech_edit: true })}
                        style={{ ...iconBtn(), flexShrink: 0, width: "auto", padding: "0 5px", fontSize: 10, color: C.danger }}
                        title="Flag this price as an AI estimate (internal only)">Flag AI</button>
                    )}
                  </div>
                </td>
                <td {...rc("mat", i, 4, td())}><input style={inputCell()} type="number" step="0.01"      value={m.our_cost   ?? ""} onChange={e => setRow(i, { our_cost:    e.target.value === "" ? "" : Number(e.target.value) })}
                  // Hand-pricing an unmatched line is the moment the book gap
                  // opens — offer to close it right here, once per row.
                  onBlur={() => {
                    if (bookOfferable(m) && savePriceIdx !== i && !offerDismissedRef.current.has(rowId(m, i))) setSaveOffer({ idx: i });
                  }} /></td>
                <td {...rc("mat", i, 5, { ...td(), color: C.muted })} title={`${Number(m.qty || 1)} × ${fmt$(m.our_cost)} = ${fmt$(ourTotalEx(m))} ex tax · with PST ${fmt$(ourTotalPst(m))}`}>{fmt$(ourTotalShown(m, taxBasis))}</td>
                <td {...rc("mat", i, 6, td())}><input style={inputCell()} type="number" step="1"         value={m.markup_pct ?? ""} onChange={e => setRow(i, { markup_pct:  e.target.value === "" ? "" : Number(e.target.value) })} /></td>
                <td {...rc("mat", i, 7, td())}><input style={inputCell()} type="number" step="1"         value={m.pst_pct    ?? ""} onChange={e => setRow(i, { pst_pct:     e.target.value === "" ? "" : Number(e.target.value) })} /></td>
                <td {...rc("mat", i, 8, { ...td(), color: C.muted })}>{fmt$(m.client_each)}</td>
                <td {...rc("mat", i, 9, { ...td(), color: C.text, fontWeight: 600 })}>{fmt$(m.client_total)}</td>
                <td style={td()}><button onClick={() => removeRow(i)} style={iconBtn()} title="Remove">×</button></td>
              </tr>
              {lot?.collapsed && (
                <tr style={{ background: "rgba(165,206,58,0.05)" }}>
                  <td style={td()} />
                  <td style={td()} />
                  <td style={{ ...td(), color: C.text }}>1</td>
                  <td style={{ ...td(), color: C.text }}>lot</td>
                  <td style={{ ...td(), color: C.text }}>
                    <span style={{ fontStyle: "italic" }}>{lot.label}</span>
                    <span style={{ color: C.muted, fontSize: 11 }}> — {lotMembers.length} lines compressed</span>
                    <button
                      onClick={() => setRow(i, { _lot: { ...lot, collapsed: false } })}
                      style={{ ...iconBtn(), width: "auto", padding: "0 6px", fontSize: 10, marginLeft: 6 }}
                      title="Expand to edit the individual lines"
                    >▸ expand</button>
                  </td>
                  <td style={{ ...td(), color: C.muted, fontSize: 11 }}>summed</td>
                  <td style={{ ...td(), color: C.muted }} title="Compressed lines' cost each, summed (before PST)">{fmt$(lotCost)}</td>
                  <td style={{ ...td(), color: C.muted }} title={`Compressed lines' cost to us (${taxBasisLabel(taxBasis)})`}>{fmt$(lotCostPst)}</td>
                  <td style={{ ...td(), color: C.muted }}>—</td>
                  <td style={{ ...td(), color: C.muted }}>—</td>
                  <td style={{ ...td(), color: C.muted }}>{fmt$(lotClient)}</td>
                  <td style={{ ...td(), color: C.text, fontWeight: 600 }}>{fmt$(lotClient)}</td>
                  <td style={td()} />
                </tr>
              )}
              {savePriceIdx === i && (
                <tr>
                  <td colSpan={MAT_COLSPAN} style={{ padding: "4px 8px 8px" }}>
                    <SavePricePanel
                      material={m}
                      suppliers={suppliers}
                      onSaved={(saved) => {
                        setRow(i, {
                          master_product_id: saved.master_product_id,
                          _price_source: {
                            tier: "supplier_prices", supplier_name: saved.supplier_name, date_recorded: saved.date_recorded, confidence: "high",
                            // Tell the tech when the save created a NEW book item
                            // (didn't match any existing product's description).
                            flag: saved.created_new_product ? "saved as a NEW price book item — no existing product matched this description" : null,
                          },
                        });
                        setSavePriceIdx(null);
                      }}
                      onClose={() => setSavePriceIdx(null)}
                    />
                  </td>
                </tr>
              )}
              {saveOffer?.idx === i && savePriceIdx !== i && bookOfferable(m) && (
                <tr>
                  <td colSpan={MAT_COLSPAN} style={{ padding: "4px 8px 8px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", border: `1px solid ${C.accent}`, background: "rgba(165,206,58,0.07)", borderRadius: 8, padding: "7px 10px", fontSize: 12 }}>
                      <span>
                        💾 <strong>{fmt$(m.our_cost)}</strong> — “{cleanCatalogDescription(m.description).slice(0, 60)}” isn’t in the price book, so the next quote will guess again.
                      </span>
                      <button onClick={() => quickSaveToBook(i)} disabled={!!saveOffer.busy} style={{ ...btn(), fontSize: 12, padding: "5px 9px" }}>
                        {saveOffer.busy ? "Saving…" : "Add to price book"}
                      </button>
                      <button
                        onClick={() => { setSaveOffer(null); ensureSuppliers(); setSavePriceIdx(i); }}
                        style={{ ...btn("ghost"), fontSize: 12, padding: "5px 9px" }}
                        title="Edit the description / name the real supplier before saving"
                      >✏️ Edit details</button>
                      <button onClick={() => { offerDismissedRef.current.add(rowId(m, i)); setSaveOffer(null); }} style={{ ...btn("ghost"), fontSize: 12, padding: "5px 9px" }}>✕</button>
                      {saveOffer.err
                        ? <span style={{ color: C.danger }}>{saveOffer.err}</span>
                        : <span style={{ color: C.muted, fontSize: 10 }}>saved under supplier “Tech verified” — use ✏️ to name the real one</span>}
                    </div>
                  </td>
                </tr>
              )}
              {m._price_source && typeof m._price_source === "object" && m._price_source.flag && (
                <tr>
                  <td colSpan={MAT_COLSPAN} style={{ padding: "0 8px 4px 8px", fontSize: 10, fontStyle: "italic", ...priceSourceStyle(m._price_source) }}>
                    {priceSourceLabel(m._price_source, m)}
                  </td>
                </tr>
              )}
            </React.Fragment>
            );
          })}
        </tbody>
        <MaterialsFooter materials={materials} sumIdxs={footSumIdxs} hiddenLotLines={footHiddenLots} basis={taxBasis} onBasisChange={changeTaxBasis} />
      </table>
      <button onClick={addRow} style={{ ...btn("ghost"), marginTop: 10, fontSize: 12, padding: "6px 10px" }}>+ Add material line</button>

      {searchIdx != null && (
        <MaterialSearchModal
          initialTerm={materials[searchIdx]?.description || ""}
          onPick={(product, price) => applyPick(searchIdx, product, price)}
          onClose={() => setSearchIdx(null)}
        />
      )}
    </div>
  );
}

// The price book wants CATALOGUE descriptions ("Siemens 2P 30A QP breaker"),
// not site wording ("… — new main panel shop feed"): job-context tails poison
// future matching. Deterministic strip — the save panel prefills with this
// and the tech can still edit before saving.

// Inline "push this price back to the price book" panel — the tech verified a
// price (on the comp / a supplier site), so store it as the default for future
// quotes. Creates the product if it isn't in the book yet.
function SavePricePanel({ material, suppliers, onSaved, onClose }) {
  const existing = typeof material._price_source === "object" ? material._price_source?.supplier_name : null;
  const [supplier, setSupplier] = useState(existing || "City Electric");
  const [customSupplier, setCustomSupplier] = useState("");
  // Prefilled with the site-wording-stripped catalogue description; editable.
  const [saveDesc, setSaveDesc] = useState(() => cleanCatalogDescription(material.description));
  const wasCleaned = saveDesc !== String(material.description || "").trim();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const chosen = supplier === "__custom__" ? customSupplier.trim() : supplier;
  const cost = Number(material.our_cost) || 0;

  async function save() {
    if (!chosen || !cost) { setErr(!cost ? "Set Our cost first" : "Pick a supplier"); return; }
    if (!saveDesc.trim()) { setErr("Description can't be empty"); return; }
    setBusy(true); setErr(null);
    try {
      const r = await apiFetch("/api/pricebook", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "upsertQuotePrice",
          master_product_id: material.master_product_id || null,
          description: saveDesc.trim(),
          supplier_name: chosen,
          unit_price: cost,
          unit: material.unit || "ea",
        }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      onSaved(j);
    } catch (e) { setErr(e.message); } finally { setBusy(false); }
  }

  return (
    <div style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", fontSize: 12 }}>
      <span style={{ color: C.muted }}>Save <strong style={{ color: C.text }}>{fmt$(cost)}</strong> to the book as</span>
      <input
        style={{ ...input(), flex: 1, minWidth: 220, padding: "6px 8px" }}
        value={saveDesc}
        onChange={e => setSaveDesc(e.target.value)}
        title="Catalogue description saved to the price book — keep it site-neutral so future quotes match it"
      />
      {wasCleaned && (
        <span style={{ color: C.warn, fontSize: 10, flexBasis: "100%" }}>
          Site wording stripped for the book (“{String(material.description || "").slice(0, 60)}”) — edit above if it cut too much. The quote line keeps your original text.
        </span>
      )}
      <span style={{ color: C.muted }}>from</span>
      <select value={supplier} onChange={e => setSupplier(e.target.value)} style={{ ...input(), width: 180, padding: "6px 8px" }}>
        {(suppliers || ["City Electric", "EB Horsman", "Wesco"]).map(s => <option key={s} value={s}>{s}</option>)}
        <option value="__custom__">Other…</option>
      </select>
      {supplier === "__custom__" && (
        <input style={{ ...input(), width: 150, padding: "6px 8px" }} placeholder="Supplier name" value={customSupplier} onChange={e => setCustomSupplier(e.target.value)} />
      )}
      <button onClick={save} style={btn("primary")} disabled={busy}>{busy ? "Saving…" : "Save to price book"}</button>
      <button onClick={onClose} style={btn("ghost")} disabled={busy}>Cancel</button>
      {err && <span style={{ color: C.danger }}>{err}</span>}
      <span style={{ color: C.muted, fontSize: 10, flexBasis: "100%" }}>
        Updates today's price for this product+supplier in the Price List (adds the product if it's new) — future quotes will load it automatically.
      </span>
    </div>
  );
}

// Pricebook keyword search — same data as the Price List tab. Pick a supplier
// price to load it onto the material line, or keep what you typed as a custom line.
function MaterialSearchModal({ initialTerm, onPick, onClose }) {
  const [term, setTerm] = useState(initialTerm || "");
  const [results, setResults] = useState(null); // null = not searched yet
  const [busy, setBusy] = useState(false);
  // A failed request must NEVER render as "nothing found" — an expired session
  // returning 401 looked exactly like an empty price book, which sent us
  // hunting a search bug that didn't exist.
  const [searchError, setSearchError] = useState("");

  useEffect(() => {
    const t = String(term || "").trim();
    if (t.length < 3) { setResults(null); setSearchError(""); return; }
    const timer = setTimeout(async () => {
      setBusy(true);
      try {
        const r = await apiFetch("/api/pricebook", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "quoteSearch", term: t, limit: 20 }),
        });
        if (r.status === 401 || r.status === 403) {
          setSearchError("Your session expired — refresh the page to sign back in.");
          setResults(null);
          return;
        }
        const j = await r.json();
        if (!r.ok || j.ok === false) {
          setSearchError(j.error || `Price book search failed (${r.status}).`);
          setResults(null);
          return;
        }
        setSearchError("");
        setResults(Array.isArray(j.results) ? j.results : []);
      } catch {
        setSearchError("Couldn't reach the price book — check your connection and try again.");
        setResults(null);
      }
      finally { setBusy(false); }
    }, 350);
    return () => clearTimeout(timer);
  }, [term]);

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 200, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "8vh 16px 16px" }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, width: "100%", maxWidth: 640, maxHeight: "80vh", overflowY: "auto" }}>
        <div style={{ padding: "14px 16px", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.surface, zIndex: 1 }}>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              autoFocus
              style={{ ...input(), flex: 1 }}
              placeholder="Search the price book… e.g. duplex receptacle, nmd90 14/2, 2 gang box"
              value={term}
              onChange={e => setTerm(e.target.value)}
              onKeyDown={e => { if (e.key === "Escape") onClose(); }}
            />
            <button onClick={onClose} style={btn("ghost")}>✕</button>
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
            Click a supplier price to load it onto the line. {busy ? "Searching…" : ""}
          </div>
        </div>
        <div style={{ padding: 12, display: "flex", flexDirection: "column", gap: 8 }}>
          {searchError && (
            <div style={{ color: "#fca5a5", background: "rgba(239,68,68,.08)", border: "1px solid rgba(239,68,68,.25)", borderRadius: 8, fontSize: 12, padding: 10, textAlign: "center" }}>
              {searchError}
            </div>
          )}
          {results === null && !searchError && <div style={{ color: C.muted, fontSize: 12, padding: 12, textAlign: "center" }}>Type at least 3 characters to search.</div>}
          {results !== null && results.length === 0 && !busy && (
            <div style={{ color: C.muted, fontSize: 12, padding: 12, textAlign: "center" }}>
              Nothing in the price book for “{term}”. Keep it as a custom line, set your cost, then hit 💾 to add it to the book.
            </div>
          )}
          {(results || []).map((p, i) => (
            <div key={p.master_product_id || i} style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: 10 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>
                {p.display_description || p.description}
                {p.sku ? <span style={{ color: C.muted, fontWeight: 400, fontSize: 11 }}>  ·  {p.sku}</span> : null}
                {p.times_purchased > 0 && (
                  <span
                    title={`Bought on ${p.times_purchased} invoice line${p.times_purchased === 1 ? "" : "s"}${p.last_purchased_at ? ` · last ${String(p.last_purchased_at).slice(0, 10)}` : ""}`}
                    style={{ marginLeft: 8, fontWeight: 700, fontSize: 10, color: C.accent, background: "rgba(165,206,58,.12)", border: `1px solid ${C.accent}55`, borderRadius: 999, padding: "2px 7px", whiteSpace: "nowrap" }}
                  >
                    bought {p.times_purchased}×
                  </span>
                )}
              </div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {(p.prices || []).map((pr, j) => (
                  <button key={j} onClick={() => onPick(p, pr)}
                    style={{ ...btn("ghost"), padding: "6px 10px", fontSize: 12, display: "flex", gap: 6, alignItems: "center" }}
                    title={pr.supplier_description}>
                    <span style={{ color: C.accent, fontWeight: 700 }}>{fmt$(pr.unit_price)}</span>
                    <span>{pr.supplier_name}</span>
                    {pr.date_recorded && <span style={{ color: C.muted, fontSize: 10 }}>{String(pr.date_recorded).slice(0, 10)}</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function LoadSheddingEditor({ loadShedding, onChange }) {
  const picks = loadShedding.picks || [];
  function setPick(idx, patch) {
    const next = picks.slice();
    next[idx] = { ...next[idx], ...patch };
    onChange({ ...loadShedding, picks: next });
  }
  function removePick(idx)  { onChange({ ...loadShedding, picks: picks.filter((_, i) => i !== idx) }); }
  function addPick()        { onChange({ ...loadShedding, picks: [...picks, { name: "", amps: 50 }] }); }
  return (
    <div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
        Pick which loads should be shed by SMMs. The materials list above will get one SMM per pick
        (size matches the load amperage) the next time you save.
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {picks.map((p, i) => (
          <div key={i} style={{ display: "flex", gap: 6 }}>
            <input style={{ ...input(), flex: 1 }} type="text"   placeholder="e.g. Hot tub" value={p.name || ""} onChange={e => setPick(i, { name: e.target.value })} />
            <input style={{ ...input(), width: 100 }} type="number" placeholder="amps"        value={p.amps ?? ""}  onChange={e => setPick(i, { amps: e.target.value === "" ? "" : Number(e.target.value) })} />
            <button onClick={() => removePick(i)} style={iconBtn()}>×</button>
          </div>
        ))}
        <button onClick={addPick} style={{ ...btn("ghost"), alignSelf: "flex-start", fontSize: 12, padding: "6px 10px" }}>+ Add load to shed</button>
      </div>
    </div>
  );
}

function LabourEditor({ labour, onChange }) {
  const total_hours = Number(labour.total_hours || 0);
  const [calNote, setCalNote] = useState(null);
  function setRate(rate)     { onChange({ rate, rate_mode: "standard" }); }
  function setBaseHours(h)   {
    // Typed hours ARE the estimate. The engine's "Estimate calibration
    // (learned from won jobs)" row is a fixed-hours delta sized against the
    // SUGGESTED hours, so it must not survive a manual override — a 7h draft
    // carrying a −2.8h ×0.6 row kept the −2.8 after the tech typed 4h and
    // billed 1.2h ($336). Hand-added adjustment rows are the tech's own and stay.
    const rows = labour.adjustments || [];
    const kept = rows.filter(a => a?.source !== "calibration");
    const dropped = rows.length - kept.length;
    if (dropped) {
      const hrs = rows.filter(a => a?.source === "calibration").reduce((s, a) => s + Number(a.hours || 0), 0);
      setCalNote(`Removed the ${hrs > 0 ? "+" : ""}${hrs}h “learned from won jobs” adjustment — you set the hours, so they bill exactly as typed.`);
    }
    onChange({
      base_hours: h,
      ...(dropped ? { adjustments: kept, labour_factor: null } : {}),
      total_hours: h + kept.reduce((s, a) => s + Number(a.hours || 0), 0),
    });
  }
  function setAdj(idx, patch) {
    const next = (labour.adjustments || []).slice();
    next[idx] = { ...next[idx], ...patch };
    const new_total = Number(labour.base_hours || 0) + next.reduce((s, a) => s + Number(a.hours || 0), 0);
    onChange({ adjustments: next, total_hours: new_total });
  }
  function addAdj()    {
    const next = [...(labour.adjustments || []), { name: "", hours: 1 }];
    const new_total = Number(labour.base_hours || 0) + next.reduce((s, a) => s + Number(a.hours || 0), 0);
    onChange({ adjustments: next, total_hours: new_total });
  }
  function removeAdj(idx) {
    const next = (labour.adjustments || []).filter((_, i) => i !== idx);
    const new_total = Number(labour.base_hours || 0) + next.reduce((s, a) => s + Number(a.hours || 0), 0);
    onChange({ adjustments: next, total_hours: new_total });
  }
  return (
    <div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <div>
          <label style={lbl()}>Base hours</label>
          <input style={{ ...input(), width: 90 }} type="number" step="0.5" value={labour.base_hours ?? ""} onChange={e => setBaseHours(Number(e.target.value || 0))} />
        </div>
        <div>
          <label style={lbl()}>Rate ($/hr)</label>
          <input style={{ ...input(), width: 90 }} type="number" step="5" value={labour.rate ?? 280} onChange={e => setRate(Number(e.target.value || 0))} />
        </div>
        <div style={{ marginLeft: "auto", textAlign: "right" }}>
          <div style={{ fontSize: 11, color: C.muted }}>{total_hours}h × ${labour.rate || 0}/hr</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: total_hours < 0 ? C.danger : undefined }}>{fmt$(total_hours * Number(labour.rate || 0))}</div>
        </div>
      </div>
      {calNote && (
        <div style={{ fontSize: 12, color: C.warn, marginBottom: 10 }}>{calNote}</div>
      )}
      {total_hours < 0 && (
        <div style={{ fontSize: 12, color: C.danger, marginBottom: 10 }}>
          ⚠ Adjustment rows subtract more hours than the base — labour is negative. Delete or shrink a row below.
        </div>
      )}
      <div style={{ fontSize: 12, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>Adjustments</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {(labour.adjustments || []).map((a, i) => (
          <div key={i} style={{ display: "flex", gap: 6 }}>
            <input style={{ ...input(), flex: 1 }} placeholder="Adjustment name"  value={a.name || ""}  onChange={e => setAdj(i, { name: e.target.value })} />
            <input style={{ ...input(), width: 80 }} type="number" step="0.5" placeholder="hrs" value={a.hours ?? ""} onChange={e => setAdj(i, { hours: e.target.value === "" ? "" : Number(e.target.value) })} />
            <button onClick={() => removeAdj(i)} style={iconBtn()}>×</button>
          </div>
        ))}
        <button onClick={addAdj} style={{ ...btn("ghost"), alignSelf: "flex-start", fontSize: 12, padding: "6px 10px" }}>+ Add labour adjustment</button>
      </div>
    </div>
  );
}

function BulletListEditor({ items, onChange, placeholder, rangeId = "list" }) {
  const { rc, pill } = useLineRange(rangeId, items);
  function setItem(idx, text) {
    const next = items.slice();
    next[idx] = { ...next[idx], text };
    onChange(next);
  }
  function removeItem(idx) { onChange(items.filter((_, i) => i !== idx)); }
  function addItem() {
    onChange([...items, { id: `n_${Date.now()}`, text: "", source: "manual" }]);
  }
  return (
    <div data-rsel={rangeId} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {pill}
      {items.map((n, i) => (
        <div key={n.id || i} {...rc(rangeId, i, 0, { display: "flex", gap: 6, alignItems: "flex-start" })}>
          <span style={{ color: C.muted, paddingTop: 8 }}>•</span>
          <textarea
            value={n.text || ""}
            placeholder={placeholder}
            onChange={e => setItem(i, e.target.value)}
            rows={1}
            style={{ ...input(), resize: "vertical", minHeight: 32 }}
          />
          <button onClick={() => removeItem(i)} style={iconBtn()}>×</button>
        </div>
      ))}
      <button onClick={addItem} style={{ ...btn("ghost"), alignSelf: "flex-start", fontSize: 12, padding: "6px 10px" }}>+ Add</button>
    </div>
  );
}

function TotalsCard({ totals, permitFee, permitBasis, permitMarkupPct, permitOverridden, canPickType, permitTypeValue, permitAmps, onPermitType, onPermitChange, onPermitAuto, onPermitMarkupChange }) {
  const t = totals || {};
  // Permit fee is editable: prefer the live draft value, fall back to the
  // computed totals. Editing flows straight into recomputeTotals via update().
  const permitVal = permitFee != null ? permitFee : t.permit_fee;
  const editable = typeof onPermitChange === "function";
  const mkPct    = Number(permitMarkupPct ?? t.permit_markup_pct ?? 35);
  const mkAmt    = Number(t.permit_markup_amount ?? 0);
  // How the fee was set → what the tech sees under "Permit fee". Value-bracket
  // families show the bracket + live declared value (auto-watched); flat/amps
  // show their category; a manual edit shows "manual" + a resume-auto affordance.
  const pb        = permitBasis || {};
  const isValue   = pb.mode === "value";
  const live      = pb._live_bracket || null;
  const declared  = live ? live.value : pb.value;
  const rangeStr  = live
    ? `$${Number(live.min).toLocaleString()}–${live.max == null ? "up" : "$" + Number(live.max).toLocaleString()}`
    : null;
  let basisNode = null;
  if (Number(permitVal) === 0) {
    basisNode = "no permit on this job";
  } else if (permitOverridden) {
    basisNode = (
      <>manual override{isValue && typeof onPermitAuto === "function" && (
        <button type="button" onClick={onPermitAuto} title="Resume auto — re-derive the permit from the value-of-work bracket and keep it in sync as you tweak the quote"
          style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 5, color: C.accent, cursor: "pointer", fontSize: 9, padding: "0 4px", marginLeft: 5, lineHeight: 1.6 }}>↺ auto</button>
      )}</>
    );
  } else if (isValue && rangeStr) {
    basisNode = `By value of work · bracket ${rangeStr}${declared != null ? ` · declared $${Number(declared).toLocaleString()}` : ""}`;
  } else if (pb.category) {
    basisNode = pb.category;
  }
  return (
    <div style={{ ...cardStyle, marginTop: 12, background: C.surface2 }}>
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr auto", rowGap: 6, columnGap: 12, fontSize: 13, alignItems: "center" }}>
        <span style={{ color: C.muted }}>Materials (PST in)</span><span></span><span style={{ textAlign: "right" }}>{fmt$(t.materials_subtotal)}</span>
        <span style={{ color: C.muted }}>Labour</span>   <span></span><span style={{ textAlign: "right" }}>{fmt$(t.labour_total)}</span>
        {/* The bundled work LINE ITEM (materials + labour) — what pushes to
            Jobber as one line, so the tech can gauge/adjust it before pushing. */}
        <span style={{ fontWeight: 600, borderTop: `1px dashed ${C.border}`, paddingTop: 6 }}>
          Work line <span style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}>(materials + labour → 1 Jobber line)</span>
        </span>
        <span style={{ borderTop: `1px dashed ${C.border}` }}></span>
        {/* = subtotal − permit fee: exactly what pushes as the work line, so it
            includes any permit markup that folds in here. */}
        <span style={{ textAlign: "right", fontWeight: 600, borderTop: `1px dashed ${C.border}`, paddingTop: 6 }}>{fmt$((Number(t.subtotal) || 0) - (Number(t.permit_fee) || 0))}</span>
        <span style={{ color: C.muted }}>
          Permit fee
          {basisNode && (
            <span style={{ display: "block", fontSize: 10, color: C.muted, opacity: 0.85, fontWeight: 400, marginTop: 1 }}>
              {basisNode}
            </span>
          )}
        </span>
        <span></span>
        <span style={{ textAlign: "right", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
          {editable ? (
            <>
              <span style={{ color: C.muted }}>$</span>
              <input
                type="number"
                step="1"
                value={permitVal ?? ""}
                onChange={(e) => onPermitChange(e.target.value === "" ? 0 : Number(e.target.value))}
                title={isValue ? "Edit to pin the permit fee (pauses the value-bracket auto-watch — use ↺ auto to resume); $0 = no permit line" : "Edit the permit fee — flows into the subtotal live; $0 = no permit line"}
                style={{ ...inputCell(), width: 90, textAlign: "right", fontSize: 13 }}
              />
              {Number(permitVal) !== 0 && (
                <button
                  type="button"
                  onClick={() => onPermitChange(0)}
                  title="Remove permit — no TSBC permit on this job (e.g. covered by an operating permit). Sets $0; the permit line is left off the Jobber quote."
                  style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 14, padding: "0 2px", lineHeight: 1 }}
                >✕</button>
              )}
            </>
          ) : fmt$(permitVal)}
        </span>
        {/* Permit-type picker — override the auto-classifier when it guesses
            wrong (e.g. a sauna tagged as gen/solar). "By value" recomputes off
            the TSBC value brackets live; the amps types pick the SFR tier. The
            choice is stamped so refines/reprices keep it. */}
        {editable && canPickType && Number(permitVal) !== 0 && typeof onPermitType === "function" && (
          <>
            <span style={{ color: C.muted, fontSize: 12 }}>
              Permit type
              <span style={{ display: "block", fontSize: 10, color: C.muted, opacity: 0.75, fontWeight: 400, marginTop: 1 }}>
                fix it if the auto-pick is wrong
              </span>
            </span>
            <span></span>
            <span style={{ textAlign: "right", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 6, flexWrap: "wrap" }}>
              {(permitTypeValue === "service_upgrade" || permitTypeValue === "new_service") && (
                <select
                  value={permitAmps || 200}
                  onChange={(e) => onPermitType(permitTypeValue, Number(e.target.value))}
                  title="Service amperage — drives the TSBC by-amps permit fee"
                  style={{ ...inputCell(), width: 74, fontSize: 12 }}
                >
                  {[100, 125, 150, 200, 400, 600].map((a) => <option key={a} value={a}>{a}A</option>)}
                </select>
              )}
              <select
                value={permitTypeValue || "value"}
                onChange={(e) => onPermitType(e.target.value, permitAmps || 200)}
                title="Permit category — override if the auto-pick is wrong; the fee recomputes from the TSBC schedule"
                style={{ ...inputCell(), width: 176, fontSize: 12 }}
              >
                <option value="value">By value of work</option>
                <option value="alt_energy">Alt energy (gen/solar)</option>
                <option value="service_upgrade">Service upgrade (amps)</option>
                <option value="new_service">New service (amps)</option>
                <option value="silver_label">Silver Label (used home)</option>
                {permitTypeValue === "custom" && <option value="custom" disabled>Custom $ (typed)</option>}
              </select>
            </span>
          </>
        )}
        {/* Permit markup — folds into the WORK line (the permit line to the client
            stays at the real fee); the markup $ counts as margin. */}
        {editable && Number(permitVal) !== 0 && typeof onPermitMarkupChange === "function" && (
          <>
            <span style={{ color: C.muted, fontSize: 12 }}>
              Permit markup
              <span style={{ display: "block", fontSize: 10, color: C.muted, opacity: 0.75, fontWeight: 400, marginTop: 1 }}>
                folds into the work line{mkAmt > 0 ? ` · +${fmt$(mkAmt)}` : ""}
              </span>
            </span>
            <span></span>
            <span style={{ textAlign: "right", display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 2 }}>
              <input
                type="number"
                step="1"
                value={mkPct || ""}
                onChange={(e) => onPermitMarkupChange(e.target.value === "" ? 0 : Number(e.target.value))}
                title="Markup % on the permit fee — added to the work line as margin; the client-facing permit line stays at the real fee"
                style={{ ...inputCell(), width: 60, textAlign: "right", fontSize: 13 }}
              />
              <span style={{ color: C.muted }}>%</span>
            </span>
          </>
        )}
        <span style={{ fontWeight: 700, fontSize: 16, color: C.text, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>Subtotal</span>
        <span style={{ borderTop: `1px solid ${C.border}` }}></span>
        <span style={{ textAlign: "right", fontWeight: 700, fontSize: 18, color: C.accent, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>{fmt$(t.subtotal)}</span>
        <span style={{ color: C.muted, fontSize: 11 }}>+ GST 5% (added in Jobber)</span><span></span><span style={{ textAlign: "right", fontSize: 11, color: C.muted }}>{fmt$(t.subtotal * 0.05)}</span>
        {/* Final client price incl. GST — the number the client sees on the
            Jobber quote, so it can be gauged before pushing. */}
        <span style={{ fontWeight: 700, fontSize: 15, color: C.text, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>Total incl. GST</span>
        <span style={{ borderTop: `1px solid ${C.border}` }}></span>
        <span style={{ textAlign: "right", fontWeight: 700, fontSize: 18, color: C.good, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>{fmt$(t.subtotal * 1.05)}</span>
      </div>
      <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
        7% PST is baked into material prices (we pay it; client sees no PST line). Jobber adds 5% GST at checkout.
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: C.muted, display: "flex", gap: 12, flexWrap: "wrap" }}>
        <span>Material cost: {fmt$(t._internal_cost_total)}</span>
        <span>Labour cost: {fmt$(t._internal_labour_cost)}</span>
        <span style={{ color: (t._internal_margin_pct ?? 0) >= 35 ? C.good : (t._internal_margin_pct ?? 0) >= 25 ? C.warn : C.danger }}>
          Est. gross margin: {t._internal_margin_pct ?? 0}% (target ≥35%)
        </span>
      </div>
    </div>
  );
}

// What each tool is doing, in the tech's words rather than the tool's name.
const TOOL_STATUS = {
  web_search: "🔎 searching the web…",
  web_fetch: "🌐 reading a page…",
  set_material: "✏️ editing a line…",
  add_material: "➕ adding a line…",
  remove_material: "➖ removing a line…",
  set_labour: "⏱ adjusting labour…",
  edit_text: "📝 rewording…",
  set_headline: "🏷 renaming the headline…",
  count_panel_breakers: "🔢 counting the panel's breakers…",
  suggest_actions: "💡 working out your options…",
  remember_this: "🧠 saving to the brain…",
  look_up_similar_jobs: "📁 checking past jobs…",
  // Research mode — these are reads, so the tech should see it looking things
  // up rather than a generic "🔧 tool".
  search_job_actuals: "📊 checking what we billed…",
  get_labour_actuals: "⏱ pulling real hours…",
  search_logbook_backend: "📒 opening the Log Book…",
  search_purchase_history: "🧾 checking what we paid…",
  search_price_list: "🏷 checking the price list…",
  lookup_code: "📖 checking the code…",
};

// Refine with AI — chat to edit the quote line-by-line, grounded in real similar
// Jobber jobs. Streams (thinking + tool status) so a deep/web refine isn't a
// dead "Thinking…" for a minute. Same model/thinking pickers as the scope chat.
function RefineChat({ quoteId, assessmentId, onRefineStart, onRefined, onRefineEnd }) {
  const [chatInput, setChatInput] = useState("");
  const [busy, setBusy] = useState(false);
  // Staged attachments (photos / plan PDFs / text files) for the NEXT message —
  // same bucket + wire shape as the assessment's scope chat, so the plan tools
  // (count_devices_on_plan / count_devices_visually) read them identically.
  // Documents already attached in the assessment chat are ALWAYS available
  // server-side; this tray is for anything new.
  const [attachTray, setAttachTray] = useState([]); // { id, kind, name, path?, text?, status }
  const fileInputRef = useRef(null);
  const attachFiles = async (files) => {
    for (const file of Array.from(files || [])) {
      const id = `att_${Date.now().toString(36)}_${Math.floor(Math.random() * 1e6).toString(36)}`;
      const ext = (file.name.split(".").pop() || "bin").toLowerCase();
      const isImg = /^image\//.test(file.type);
      const isPdf = file.type === "application/pdf" || ext === "pdf";
      const isTxt = /^text\//.test(file.type) || ["txt", "csv", "md"].includes(ext);
      if (!isImg && !isPdf && !isTxt) { setAttachTray(t => [...t, { id, kind: "document", name: file.name, status: "error", error: "Only photos, PDFs, or text files" }]); continue; }
      if (isTxt) {
        const text = await file.text().catch(() => "");
        setAttachTray(t => [...t, { id, kind: "text", name: file.name, text: text.slice(0, 16000), status: "ready" }]);
        continue;
      }
      setAttachTray(t => [...t, { id, kind: isImg ? "image" : "document", name: file.name, status: "uploading" }]);
      // Same private bucket the assessment chat uses; keyed under the
      // assessment so both chats share one document pool for the job.
      const base = assessmentId || `quote-refine/${quoteId}`;
      const path = `${base}/chat/refine-${id}.${ext}`;
      try {
        const { error } = await supabase.storage.from("assessment-videos").upload(path, file, { contentType: file.type || (isPdf ? "application/pdf" : "application/octet-stream"), upsert: true });
        if (error) throw error;
        setAttachTray(t => t.map(a => a.id === id ? { ...a, path, status: "ready" } : a));
      } catch (e) {
        setAttachTray(t => t.map(a => a.id === id ? { ...a, status: "error", error: String(e.message || e).slice(0, 120) } : a));
      }
    }
  };
  // Sign the staged files fresh at send time (1h) — matches scope-chat's wire.
  const wireAttachments = async () => {
    const out = [];
    for (const a of attachTray) {
      if (a.status !== "ready") continue;
      if (a.kind === "text") { out.push({ kind: "text", name: a.name, text: a.text }); continue; }
      try {
        const { data, error } = await supabase.storage.from("assessment-videos").createSignedUrl(a.path, 3600);
        if (error || !data?.signedUrl) throw error || new Error("no url");
        out.push({ kind: a.kind, name: a.name, url: data.signedUrl });
      } catch { /* skip unsigned — the send note below reports the count sent */ }
    }
    return out;
  };
  const [log, setLog] = useState([]); // { role:'you'|'ai'|'divider', text, thinking, status, changes, suggestions, historical, ... }
  // Same keys as the assessment's scope chat, so the picker follows the tech
  // between the two screens instead of resetting per surface.
  const [modelPref, setModelPref] = useState(() => localStorage.getItem("sc-model") || "standard");
  // Defaults ON (with a one-time off→standard migration) so the tech can watch
  // the AI think while it works — see src/lib/chatPrefs.js.
  const [thinkPref, setThinkPref] = useState(initialThinkPref);
  // Edit vs Research. Research drops the patch tools SERVER-side, so this isn't
  // a hint to the model — it genuinely cannot touch the draft in that mode.
  // Deliberately not persisted: edit is the default every time the tech opens a
  // quote, so a research session yesterday can't silently swallow today's edit.
  const [chatMode, setChatMode] = useState("edit");
  const [openThinking, setOpenThinking] = useState({});
  const abortRef = useRef(null);
  const scrollRef = useRef(null);

  // One conversation follows the workflow: seed the chat with the assessment's
  // scope chat + any refine turns already made on this quote, so converting to
  // a quote doesn't reset the conversation to a blank box. The server also
  // feeds this same history to the model, so follow-ups ("the panel we talked
  // about") resolve without being re-explained.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch("/api/quote-brain", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "chatHistory", quote_id: quoteId }),
        });
        const j = await r.json();
        if (cancelled || !r.ok || j.ok === false) return;
        const seed = [];
        const scope = (j.scope_chat || []).map(m => ({
          role: m.role, historical: true,
          text: m.text || ((m.attachments || []).length ? `📎 ${m.attachments.join(", ")}` : ""),
        })).filter(m => m.text);
        if (scope.length) {
          seed.push({ role: "divider", text: "From the assessment — same conversation, continued" });
          seed.push(...scope);
        }
        if ((j.refines || []).length) {
          seed.push({ role: "divider", text: "Earlier refines on this quote" });
          for (const t of j.refines) {
            if (t.instruction) seed.push({ role: "you", text: t.instruction, historical: true });
            seed.push({ role: "ai", text: t.reply || "(changes applied)", changes: t.changes, historical: true });
          }
        }
        // Don't clobber a conversation the tech already started this session.
        if (seed.length) setLog(l => (l.length ? l : seed));
      } catch { /* no history is fine — the chat still works from blank */ }
    })();
    return () => { cancelled = true; };
  }, [quoteId]);

  // Keep the newest turn visible — the seeded history fills the scroll box.
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [log.length]);

  const setPref = (which, v) => {
    if (which === "model") { setModelPref(v); localStorage.setItem("sc-model", v); }
    else { setThinkPref(v); localStorage.setItem("sc-think", v); }
  };

  async function send(instructionOverride, opts = {}) {
    const msg = String(instructionOverride ?? chatInput).trim();
    if (!msg || busy) return;
    if (!instructionOverride) setChatInput("");
    setBusy(true);
    // A review forces Max + deep thinking + web regardless of the pickers — it's
    // the one job that always needs the horsepower, and Dylan asked for "think
    // deep". Ad-hoc turns still honour the tech's picker.
    const model = opts.model_pref || modelPref;
    const think = opts.thinking || thinkPref;
    const mode = opts.mode || chatMode;
    // Carry the instruction on the AI entry: refine is stateless per call (quote
    // + one instruction, no chat history), so a "keep going" after a truncated
    // batch has to RE-SEND the original list. The model sees the partly-edited
    // draft, so already-applied edits harmlessly no-op and only the rest lands.
    setLog(l => [...l, { role: "you", text: msg }, { role: "ai", text: "", streaming: true, instruction: msg, mode, status: opts.reviewing ? "🔍 reviewing every option — this takes a minute…" : mode === "research" ? "🔬 digging through the real numbers…" : null }]);

    const patchLast = (patch) => setLog(l => {
      const next = l.slice();
      next[next.length - 1] = { ...next[next.length - 1], ...patch };
      return next;
    });

    let reply = "", thinking = "";
    // The activity trail — every tool step this turn, kept on the message so
    // the tech can watch the work live and reopen it after (⚙ N steps).
    let steps = [];
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      // Flush dirty edits + pause the autosave so a stale snapshot can't land
      // over the refined quote_json mid-flight.
      if (onRefineStart) await onRefineStart();
      // Sign + attach anything staged in the tray (photos / prints / text).
      const attachments = await wireAttachments();
      if (attachments.length) {
        setAttachTray([]);
        setLog(l => l.map((m, i) => i === l.length - 2 && m.role === "you"
          ? { ...m, text: `${m.text}\n📎 ${attachments.map(a => a.name).join(", ")}` } : m));
      }
      // apiFetch — the server reads the caller's JWT to decide whether to offer
      // the remember_this tool. A bare fetch reads as anonymous.
      const r = await apiFetch("/api/quote-refine", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ quote_id: quoteId, instruction: msg, model_pref: model, thinking: think, mode, ...(attachments.length ? { attachments } : {}) }),
        signal: controller.signal,
      });
      if (!r.ok || !r.body) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }

      const reader = r.body.getReader();
      const dec = new TextDecoder();
      let buf = "", done = null;
      while (true) {
        const { done: fin, value } = await reader.read();
        if (fin) break;
        buf = (buf + dec.decode(value, { stream: true })).replace(/\r\n/g, "\n");
        let sep;
        while ((sep = buf.indexOf("\n\n")) !== -1) {
          const line = buf.slice(0, sep); buf = buf.slice(sep + 2);
          if (!line.startsWith("data:")) continue;
          let ev; try { ev = JSON.parse(line.slice(5).trim()); } catch { continue; }
          if (ev.type === "text") { reply += ev.text || ""; patchLast({ text: reply }); }
          else if (ev.type === "thinking") { thinking += ev.text || ""; patchLast({ thinking }); }
          else if (ev.type === "tool") {
            const label = TOOL_STATUS[ev.name] || `🔧 ${ev.name}…`;
            steps = startStep(steps, ev.name, label);
            patchLast({ status: label, steps });
          }
          else if (ev.type === "tool_call") { steps = argStep(steps, ev.name, ev.arg); patchLast({ steps }); }
          else if (ev.type === "tool_result") {
            steps = finishStep(steps, ev.name, ev.ok !== false, stepNote(ev.name, ev.detail, ev.ok !== false));
            patchLast({ status: null, steps });
          }
          else if (ev.type === "error") throw new Error(ev.error || "Refine failed");
          else if (ev.type === "done") done = ev;
        }
      }
      if (!done) throw new Error("The connection dropped mid-reply — check the quote before sending again.");

      // Reload the page draft BEFORE settling the reply — the chat must never
      // claim an update the table doesn't show. A no_changes reply (a question)
      // didn't touch the quote, so skip the reload.
      let reloadNote = "";
      if (onRefined && !done.no_changes) {
        try { await onRefined(); }
        catch { reloadNote = "\n\n⚠ Updated on the server, but the page reload failed — refresh to see the changes."; }
      }
      patchLast({
        text: (done.reply || reply || "Refined.") + reloadNote,
        streaming: false, status: null, thinking,
        references: done.references_used, assumptions: done.assumptions, options: done.options,
        changes: done.changes, rejected: done.rejected, memory: done.memory?.saved,
        suggestions: done.suggestions, model: done.model, truncated: done.truncated,
      });
    } catch (e) {
      const aborted = e && e.name === "AbortError";
      patchLast({ text: aborted ? (reply.trim() + " …(stopped)").trim() : "⚠ " + e.message, streaming: false, status: null });
    } finally {
      abortRef.current = null;
      if (onRefineEnd) onRefineEnd();
      setBusy(false);
    }
  }

  return (
    <div style={{ ...cardStyle, marginTop: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap", marginBottom: 4 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: C.accent }}>
          {chatMode === "research" ? "🔬 Dig into this quote" : "💬 Refine with AI"}
        </div>
        {/* Same brain as the assessment's scope chat — model + thinking, and it
            can search the web to check a real part price. */}
        <div style={{ display: "flex", gap: 6 }}>
          {/* Edit vs Research. The server drops the patch tools in research
              mode, so this genuinely changes what the AI is able to do — it
              isn't a prompt hint it can talk itself out of. */}
          <div style={{ display: "flex", border: `1px solid ${C.border}`, borderRadius: 6, overflow: "hidden" }}>
            {[["edit", "✏️ Edit"], ["research", "🔬 Research"]].map(([m, label]) => (
              <button
                key={m}
                onClick={() => setChatMode(m)}
                disabled={busy}
                style={{
                  fontSize: 11, padding: "3px 8px", border: "none", cursor: busy ? "default" : "pointer",
                  background: chatMode === m ? C.accent : "transparent",
                  color: chatMode === m ? "#fff" : C.muted,
                  fontWeight: chatMode === m ? 700 : 500,
                }}
                title={m === "edit"
                  ? "Applies changes to the quote"
                  : "Read-only. Looks up real hours, past job profit, what we paid suppliers, and the code — and can't touch the draft."}
              >{label}</button>
            ))}
          </div>
          <select value={modelPref} onChange={e => setPref("model", e.target.value)} style={{ ...input(), padding: "3px 6px", fontSize: 11, width: "auto" }} title="Which model answers">
            <option value="fast">Fast</option>
            <option value="standard">Standard</option>
            <option value="max">Max</option>
          </select>
          <select value={thinkPref} onChange={e => setPref("think", e.target.value)} style={{ ...input(), padding: "3px 6px", fontSize: 11, width: "auto" }} title="How hard it thinks before answering">
            <option value="off">No thinking</option>
            <option value="standard">Think</option>
            <option value="deep">Think deep</option>
          </select>
        </div>
      </div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
        {chatMode === "research" ? (
          <>
            Think a quote through before it goes out — <strong>it can't edit anything in this mode</strong>. It looks up the real numbers: clock hours off comparable jobs, what those jobs actually billed and made, what we last paid a supplier, the Log Book cost + markup, and the code. It remembers the rest of this conversation, so you can keep digging. e.g. “are the hours realistic?”, “did we make money on jobs like this?”, “what do we actually pay for that panel?”, “is this scope missing anything?”.
          </>
        ) : (
          <>
            Tell it what to change in plain English and it edits <strong>only those lines</strong> — everything else keeps your costs and wording. It can see where every price came from, so ask it <em>why</em> a line is priced that way. e.g. “make the 60A breaker $42.50”, “bump labour to 10h”, “why is the premium option cheaper?”, “remember: we never charge for breakers that come with the panel”.
          </>
        )}
      </div>
      {/* One-click thorough audit: forces Max + deep thinking + web so it verifies
          manufacturer specs (licences, PoE, compatibility) instead of guessing,
          and checks completeness option by option — the GPT-parity pass. */}
      <button
        onClick={() => send(
          "Review this whole quote before it goes to the client. Go through every option: are the parts actually compatible and complete, is anything missing or mismatched, and is it ready to send? Verify the specs, and separate what you confirmed from what I need to check with the supplier or my FSR.",
          // Research mode makes "REPORT and SUGGEST only" structural: the
          // review used to ask for read-only in the prompt while the patch
          // tools stayed armed, so nothing stopped it editing mid-audit.
          { model_pref: "max", thinking: "deep", reviewing: true, mode: "research" }
        )}
        disabled={busy}
        style={{ ...btn(), marginBottom: 8, fontSize: 12, opacity: busy ? 0.5 : 1 }}
        title="Deep review — verifies manufacturer specs and checks every option for gaps"
      >
        🔍 Review this quote
      </button>
      {log.length > 0 && (
        <div ref={scrollRef} style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10, maxHeight: 380, overflowY: "auto" }}>
          {log.map((m, i) => m.role === "divider" ? (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, margin: "2px 0" }}>
              <div style={{ flex: 1, height: 1, background: C.border }} />
              <div style={{ fontSize: 10, color: C.muted, whiteSpace: "nowrap" }}>{m.text}</div>
              <div style={{ flex: 1, height: 1, background: C.border }} />
            </div>
          ) : m.role === "you" ? (
            <div key={i} style={{ alignSelf: "flex-end", background: C.accentDim, color: C.text, borderRadius: "10px 10px 2px 10px", padding: "8px 10px", fontSize: 13, maxWidth: "85%", opacity: m.historical ? 0.8 : 1, whiteSpace: "pre-wrap" }}>{m.text}</div>
          ) : (
            <div key={i} style={{ background: C.surface2, border: `1px solid ${C.border}`, borderRadius: "10px 10px 10px 2px", padding: 10, fontSize: 13, maxWidth: "92%", opacity: m.historical ? 0.8 : 1 }}>
              {m.thinking && (
                m.streaming
                  ? <div style={{ fontSize: 11, color: C.muted, fontStyle: "italic", borderLeft: `2px solid ${C.border}`, paddingLeft: 8, marginBottom: 6, maxHeight: 90, overflow: "hidden" }}>💭 {m.thinking.slice(-400)}</div>
                  : (
                    <>
                      <button onClick={() => setOpenThinking(o => ({ ...o, [i]: !o[i] }))} style={{ background: "none", border: "none", color: C.muted, fontSize: 11, cursor: "pointer", padding: 0, marginBottom: 4 }}>
                        {openThinking[i] ? "▾" : "▸"} thinking
                      </button>
                      {openThinking[i] && <div style={{ fontSize: 11, color: C.muted, fontStyle: "italic", borderLeft: `2px solid ${C.border}`, paddingLeft: 8, marginBottom: 6, whiteSpace: "pre-wrap" }}>{m.thinking}</div>}
                    </>
                  )
              )}
              <ActivityTrail steps={m.steps} streaming={m.streaming} colors={{ muted: C.muted, border: C.border }} />
              {/* RichText, not pre-wrap: the model reaches for tables to compare
                  two options, and raw pipes are unreadable. */}
              <RichText text={m.text} accent={C.accent} />
              {m.streaming && !m.text && !m.thinking && <div style={{ fontSize: 12, color: C.muted, fontStyle: "italic" }}>…</div>}
              {m.status && <div style={{ fontSize: 11, color: C.muted, fontStyle: "italic", marginTop: 4 }}>{m.status}</div>}
              {/* A big batch that ran out of room — re-send the original list;
                  the draft already reflects what landed, so already-applied edits
                  no-op and only the remainder gets made. */}
              {m.truncated && !m.streaming && m.instruction && (
                <button onClick={() => send(`Some of this list is already applied — go through it again and ONLY make the changes still missing:\n\n${m.instruction}`)} disabled={busy}
                  style={{ ...btn("primary"), marginTop: 8, fontSize: 12, opacity: busy ? 0.5 : 1 }}>
                  ↻ Keep going — finish the rest
                </button>
              )}
              {/* Suggested next steps: each button just sends its instruction back
                  through refine, so there's no second apply path to keep honest —
                  and "type your own" is always the input box below. */}
              {Array.isArray(m.suggestions) && m.suggestions.map((s, si) => (
                <div key={si} style={{ marginTop: 8, padding: 8, border: `1px solid ${C.border}`, borderRadius: 8, background: C.surface }}>
                  {s.question && <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{s.question}</div>}
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    {s.options.map((o, oi) => (
                      <button key={oi} onClick={() => send(o.instruction)} disabled={busy}
                        style={{ textAlign: "left", background: C.surface2, border: `1px solid ${C.accent}55`, borderRadius: 6, padding: "6px 9px", cursor: busy ? "default" : "pointer", opacity: busy ? 0.5 : 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.accent }}>{o.label}</div>
                        {o.why && <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>{o.why}</div>}
                      </button>
                    ))}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>…or just type what you want below.</div>
                </div>
              ))}
              {/* The exact lines the ops changed, straight off the server. The
                  prose above is the model's account of what it did; this is the
                  record of what actually happened to the quote. */}
              {Array.isArray(m.changes) && m.changes.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 11 }}>
                  <div style={{ fontWeight: 700, color: C.good, marginBottom: 3 }}>Changed {m.changes.length} line{m.changes.length === 1 ? "" : "s"} — nothing else was touched</div>
                  {/* Live entries carry {tool, summary}; seeded history from the
                      audit log carries plain summary strings. */}
                  {m.changes.map((c, j) => (
                    <div key={j} style={{ color: C.text, padding: "2px 0" }}>✏️ {typeof c === "string" ? c : c.summary}</div>
                  ))}
                </div>
              )}
              {Array.isArray(m.rejected) && m.rejected.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 11, color: C.warn }}>
                  <div style={{ fontWeight: 700, marginBottom: 2 }}>Skipped</div>
                  {m.rejected.map((x, j) => <div key={j}>⚠ {x.error}</div>)}
                </div>
              )}
              {Array.isArray(m.memory) && m.memory.length > 0 && m.memory.map((s, j) => (
                <div key={j} style={{ marginTop: 6, display: "inline-block", padding: "3px 8px", borderRadius: 6, fontSize: 11, fontWeight: 600, background: "rgba(165,206,58,0.12)", border: "1px solid rgba(165,206,58,0.35)", color: C.accent }}>
                  🧠 Saved to the brain — applies to {s.applies_to === "_all" ? "every quote" : String(s.applies_to).replace(/_/g, " ")}
                </div>
              ))}
              {Array.isArray(m.references) && m.references.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 11, color: C.muted }}>
                  <div style={{ fontWeight: 700, color: C.accent, marginBottom: 2 }}>Referenced jobs</div>
                  {m.references.map((r, j) => <div key={j}>• {String(r.quote_num || "").replace(/^#+/, "#")} {r.client_name} — {r.relevance}</div>)}
                </div>
              )}
              {Array.isArray(m.assumptions) && m.assumptions.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 11, color: C.muted }}>
                  <div style={{ fontWeight: 700, marginBottom: 2 }}>Assumptions</div>
                  {m.assumptions.map((a, j) => <div key={j}>• {a}</div>)}
                </div>
              )}
              {Array.isArray(m.options) && m.options.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 11, color: C.good }}>↑ Updated above: {m.options.map(o => `${o.label} ${fmt$(o.grand_total)}`).join("  ·  ")}</div>
              )}
              {/* The ladder can fall back a rung when a model 400s — say which one
                  actually answered rather than letting "Max" quietly mean Standard. */}
              {m.model && <div style={{ marginTop: 6, fontSize: 10, color: C.muted }}>{m.model}</div>}
            </div>
          ))}
        </div>
      )}
      {/* Attachment tray — photos / plan PDFs / text staged for the next message.
          The assessment chat's documents are ALWAYS available to the AI's plan
          tools server-side; this is for anything new. */}
      {attachTray.length > 0 && (
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
          {attachTray.map(a => (
            <span key={a.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, border: `1px solid ${a.status === "error" ? "rgba(239,68,68,0.5)" : C.border}`, borderRadius: 999, padding: "3px 10px", color: a.status === "error" ? C.danger : C.text, background: C.surface2 }}
              title={a.status === "error" ? a.error : a.name}>
              {a.kind === "image" ? "🖼" : "📄"} {String(a.name).slice(0, 34)}
              {a.status === "uploading" && " …"}
              {a.status === "error" && " ⚠"}
              <span style={{ cursor: "pointer", color: C.muted }} onClick={() => setAttachTray(t => t.filter(x => x.id !== a.id))}>✕</span>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 8 }}>
        <input ref={fileInputRef} type="file" multiple accept="image/*,.pdf,.txt,.csv,.md" style={{ display: "none" }}
          onChange={e => { attachFiles(e.target.files); e.target.value = ""; }} />
        <button onClick={() => fileInputRef.current?.click()} style={{ ...btn(), padding: "6px 10px", alignSelf: "flex-start" }}
          title="Attach photos, plan PDFs, or text files for this message. Documents already in the assessment chat (the prints) are automatically available to the AI's plan-counting tools — no need to re-attach them.">
          📎
        </button>
        <textarea
          value={chatInput}
          onChange={e => setChatInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Tell it what to change, or ask why something's priced that way… (📎 attaches plans/photos; the assessment's prints are already available to it · Enter to send)"
          rows={2}
          style={{ ...input(), resize: "vertical", flex: 1, minHeight: 44 }}
        />
        {busy
          ? <button onClick={() => abortRef.current?.abort()} style={btn()}>⏹ Stop</button>
          : <button onClick={() => send()} style={btn("primary")} disabled={!chatInput.trim() || attachTray.some(a => a.status === "uploading")}>Refine →</button>}
      </div>
    </div>
  );
}

function DiffLearnModal({ diffs, checked, onToggle, onSkip, onConfirm, saving }) {
  const anyChecked = Object.values(checked || {}).some(Boolean);
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, width: "100%", maxWidth: 720, maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, background: C.surface, zIndex: 1 }}>
          <div style={{ fontSize: 18, fontWeight: 700 }}>I noticed these changes — remember any?</div>
          <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
            Check the boxes for changes that should become permanent rules for this service family.
            Unchecked rows are saved to this quote only.
          </div>
        </div>
        <div style={{ padding: 16, display: "flex", flexDirection: "column", gap: 8 }}>
          {diffs.length === 0 && (
            <div style={{ color: C.muted, padding: 20, textAlign: "center" }}>No diffs detected.</div>
          )}
          {diffs.map((d, i) => (
            <label
              key={i}
              style={{
                display: "flex", gap: 10, padding: 10,
                background: checked[i] ? C.accentDim : C.surface2,
                border: `1px solid ${checked[i] ? C.accent : C.border}`,
                borderRadius: 8, cursor: "pointer",
                alignItems: "flex-start",
              }}
            >
              <input
                type="checkbox"
                checked={!!checked[i]}
                onChange={() => onToggle(i)}
                style={{ marginTop: 4 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{d.summary}</div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  rule type: {d.suggested_rule.rule_type} · kind: {d.kind}
                </div>
              </div>
            </label>
          ))}
        </div>
        <div style={{ padding: 16, borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end", gap: 8, position: "sticky", bottom: 0, background: C.surface }}>
          <button onClick={onSkip} style={btn("ghost")} disabled={saving}>Skip — don't remember</button>
          <button onClick={onConfirm} style={btn("primary")} disabled={saving || !anyChecked}>
            {saving ? "Saving…" : `Remember ${Object.values(checked || {}).filter(Boolean).length} rule(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ElecBidProposalPanel — the "Send via ElecBid" builder. Adds the things Jobber's
// API can't carry (per-option photos, client-viewable attachments, an intro +
// headline hero, reusable content sections) onto a per-company template, then
// publishes the quote to the client portal (Client 360). All calls hit
// /api/proposals (crew-auth) via apiFetch.
// ═══════════════════════════════════════════════════════════════════════════
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1] || "");
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function ElecBidProposalPanel({ quoteId, draft, optionRows, onClose }) {
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [options, setOptions] = useState([]);       // [{key,label,recommended}]
  const [media, setMedia] = useState([]);           // proposal_media rows
  const [framing, setFraming] = useState({ template_id: null, intro_headline: "", intro_body: "", hero_url: null, sections: [], previous_work: [] });
  const [busy, setBusy] = useState(null);           // upload/save/publish label
  const [saveMsg, setSaveMsg] = useState(null);
  const [emailClient, setEmailClient] = useState(false);
  const [published, setPublished] = useState(false);
  const [portalUrl, setPortalUrl] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await apiFetch("/api/proposals", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "get", quote_id: quoteId }) });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      setOptions(j.quote?.options || []);
      setMedia(j.media || []);
      setFraming({
        template_id: j.framing?.template_id ?? null,
        intro_headline: j.framing?.intro_headline || "",
        intro_body: j.framing?.intro_body || "",
        hero_url: j.framing?.hero_url || null,
        sections: Array.isArray(j.framing?.sections) ? j.framing.sections : [],
        previous_work: Array.isArray(j.framing?.previous_work) ? j.framing.previous_work : [],
      });
      setPublished(j.status === "sent");
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, [quoteId]);
  useEffect(() => { load(); }, [load]);

  async function doUpload(file, kind, option_key) {
    if (!file) return;
    setBusy(`Uploading ${file.name}…`); setSaveMsg(null);
    try {
      const data_base64 = await fileToBase64(file);
      const r = await apiFetch("/api/proposals", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "upload", quote_id: quoteId, kind, option_key: option_key || null, filename: file.name, content_type: file.type, data_base64 }) });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      if (kind === "hero") setFraming((f) => ({ ...f, hero_url: j.media.url }));
      else setMedia((m) => [...m, j.media]);
    } catch (e) { setSaveMsg("⚠ " + e.message); }
    finally { setBusy(null); }
  }

  async function removeMedia(id) {
    setBusy("Removing…");
    try {
      await apiFetch("/api/proposals", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "delete-media", id }) });
      setMedia((m) => m.filter((x) => x.id !== id));
    } catch (e) { setSaveMsg("⚠ " + e.message); }
    finally { setBusy(null); }
  }

  async function saveDraftFraming() {
    setBusy("Saving…"); setSaveMsg(null);
    try {
      const r = await apiFetch("/api/proposals", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "save-proposal", quote_id: quoteId, template_id: framing.template_id, intro_headline: framing.intro_headline, intro_body: framing.intro_body, hero_url: framing.hero_url, sections: framing.sections }) });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      setSaveMsg("✓ Saved");
    } catch (e) { setSaveMsg("⚠ " + e.message); }
    finally { setBusy(null); }
  }

  async function saveAsTemplate() {
    const name = window.prompt("Template name (reusable framing for future quotes):", "Standard proposal");
    if (!name) return;
    const makeDefault = window.confirm("Make this your DEFAULT template (used for new quotes automatically)?");
    setBusy("Saving template…"); setSaveMsg(null);
    try {
      const r = await apiFetch("/api/proposals", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "save-template", name, intro_headline: framing.intro_headline, intro_body: framing.intro_body, hero_url: framing.hero_url, sections: framing.sections, previous_work: framing.previous_work, make_default: makeDefault }) });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      setSaveMsg("✓ Template saved");
    } catch (e) { setSaveMsg("⚠ " + e.message); }
    finally { setBusy(null); }
  }

  async function sendToClient() {
    setBusy("Sending…"); setSaveMsg(null);
    try {
      await saveDraftFraming();
      const r = await apiFetch("/api/proposals", { method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "publish", quote_id: quoteId, email_client: emailClient }) });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      setPublished(true);
      setPortalUrl(j.portal_url || null);
      const emailNote = j.email ? (j.email.ok ? ` — link emailed to ${j.email.emailed}` : ` — email skipped: ${j.email.error}`) : "";
      setSaveMsg("✅ Sent to the client's portal" + emailNote);
    } catch (e) { setSaveMsg("⚠ " + e.message); }
    finally { setBusy(null); }
  }

  const editSection = (i, patch) => setFraming((f) => ({ ...f, sections: f.sections.map((s, k) => (k === i ? { ...s, ...patch } : s)) }));
  const addSection = () => setFraming((f) => ({ ...f, sections: [...f.sections, { type: "text", title: "", body: "" }] }));
  const removeSection = (i) => setFraming((f) => ({ ...f, sections: f.sections.filter((_, k) => k !== i) }));
  const photosFor = (key) => media.filter((m) => m.kind === "option_photo" && m.option_key === key);
  const attachments = media.filter((m) => m.kind === "attachment");

  if (loading) return <div style={{ ...cardStyle, background: C.surface2, color: C.muted, fontSize: 13 }}>Loading proposal builder…</div>;
  if (err) return <div style={{ ...cardStyle, background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: C.danger, fontSize: 13 }}>Couldn't load: {err} <button style={{ ...btn(), marginLeft: 8 }} onClick={load}>Retry</button></div>;

  const FileBtn = ({ label, kind, optionKey, accept }) => (
    <label style={{ ...btn(), cursor: "pointer", display: "inline-block" }}>
      {label}
      <input type="file" accept={accept || "image/*"} style={{ display: "none" }}
        onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; doUpload(f, kind, optionKey); }} />
    </label>
  );
  const thumb = (m) => (
    <div key={m.id} style={{ position: "relative" }}>
      {m.content_type?.startsWith("image/")
        ? <img src={m.url} alt={m.caption || ""} style={{ height: 70, width: "auto", maxWidth: 120, objectFit: "cover", borderRadius: 6, border: `1px solid ${C.border}`, display: "block" }} />
        : <a href={m.url} target="_blank" rel="noreferrer" style={{ ...btn("ghost"), fontSize: 11 }}>{m.filename || "file"}</a>}
      <button onClick={() => removeMedia(m.id)} title="Remove" style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", border: "none", background: C.danger, color: "#fff", fontSize: 11, cursor: "pointer", lineHeight: "18px", padding: 0 }}>×</button>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Intro + hero */}
      <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, background: C.surface2 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Intro & headline image</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-start" }}>
          <div style={{ flex: "0 0 auto" }}>
            {framing.hero_url
              ? <img src={framing.hero_url} alt="" style={{ height: 84, width: 140, objectFit: "cover", borderRadius: 8, border: `1px solid ${C.border}`, display: "block" }} />
              : <div style={{ height: 84, width: 140, borderRadius: 8, border: `1px dashed ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", color: C.muted, fontSize: 11 }}>No hero</div>}
            <div style={{ marginTop: 6 }}><FileBtn label={framing.hero_url ? "Change hero" : "Add hero"} kind="hero" /></div>
          </div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <input value={framing.intro_headline} onChange={(e) => setFraming((f) => ({ ...f, intro_headline: e.target.value }))} placeholder="Headline (e.g. Thanks for considering us)" style={{ ...input(), marginBottom: 8 }} />
            <textarea value={framing.intro_body} onChange={(e) => setFraming((f) => ({ ...f, intro_body: e.target.value }))} placeholder="Intro message shown at the top of the proposal…" rows={3} style={{ ...input(), resize: "vertical" }} />
          </div>
        </div>
      </div>

      {/* Per-option photos */}
      {options.length > 0 && (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, background: C.surface2 }}>
          <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Photos per option</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {options.map((o) => (
              <div key={o.key}>
                <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>{o.label}{o.recommended && <span style={{ color: C.good }}> (Recommended)</span>}</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  {photosFor(o.key).map(thumb)}
                  <FileBtn label="+ Photo" kind="option_photo" optionKey={o.key} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Attachments */}
      <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, background: C.surface2 }}>
        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>Attachments the client can open</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {attachments.map(thumb)}
          <FileBtn label="+ Attachment" kind="attachment" accept="*/*" />
        </div>
      </div>

      {/* Content sections */}
      <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, padding: 14, background: C.surface2 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Sections (contract, messages, notes)</div>
          <button style={btn()} onClick={addSection}>+ Section</button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {framing.sections.length === 0 && <div style={{ fontSize: 12, color: C.muted }}>No sections. Add one for contract terms, a message, or notes.</div>}
          {framing.sections.map((s, i) => (
            <div key={i} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, background: C.surface }}>
              <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                <input value={s.title || ""} onChange={(e) => editSection(i, { title: e.target.value })} placeholder="Section title" style={{ ...input(), flex: 1 }} />
                <button style={iconBtn()} title="Remove section" onClick={() => removeSection(i)}>×</button>
              </div>
              <textarea value={s.body || ""} onChange={(e) => editSection(i, { body: e.target.value })} placeholder="Section text…" rows={2} style={{ ...input(), resize: "vertical" }} />
            </div>
          ))}
        </div>
      </div>

      {saveMsg && <div style={{ fontSize: 12, color: saveMsg.startsWith("⚠") ? C.danger : C.good, whiteSpace: "pre-wrap" }}>{saveMsg}</div>}
      {busy && <div style={{ fontSize: 12, color: C.muted }}>{busy}</div>}
      {published && portalUrl && (
        <div style={{ background: "rgba(16,185,129,0.08)", border: `1px solid rgba(16,185,129,0.3)`, padding: 10, borderRadius: 8, fontSize: 12, color: C.text }}>
          The client can view & approve this in their portal. <a href={portalUrl} target="_blank" rel="noreferrer" style={{ color: C.accent }}>{portalUrl}</a>
        </div>
      )}

      {/* Footer */}
      <div style={{ display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", marginTop: 4 }}>
        <label style={{ fontSize: 12, color: C.muted, display: "flex", gap: 6, alignItems: "center" }}>
          <input type="checkbox" checked={emailClient} onChange={(e) => setEmailClient(e.target.checked)} />
          Email the client a sign-in link (test mode gates real sends)
        </label>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={btn("ghost")} onClick={onClose} disabled={!!busy}>Cancel</button>
          <button style={btn()} onClick={saveAsTemplate} disabled={!!busy}>Save as template</button>
          <button style={btn()} onClick={saveDraftFraming} disabled={!!busy}>Save draft</button>
          <button style={btn("primary")} onClick={sendToClient} disabled={!!busy}>{published ? "Re-send / update" : "Send to client →"}</button>
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ReviewPushModal — the single convert → refine → push flow.
//   On open: save if dirty, fetch clientFacing (respecting the warranty
//   toggle) into an EDITABLE textarea (totals lines stripped — Jobber computes
//   them). Controls: warranty toggle (re-fetches), deposit amount + $/%
//   toggle, and a Refine-with-AI row (refineQuote → reload draft + re-fetch
//   clientFacing). A live totals strip reads from the current draft. Footer:
//   optional dry-run Preview + the real Push to Jobber, results shown inline.
// ═══════════════════════════════════════════════════════════════════════════

// Trailing computed-totals lines the server appends to the client-facing text.
// We strip them from the editable body so they aren't double-shown — Jobber
// recomputes them and we display them in the totals strip below.
const TOTALS_LINE_RE = /^\s*(electrical work|electrical permit|permit|subtotal|gst|total|deposit)\b.*$/i;
function stripTotalsLines(text) {
  const lines = String(text || "").split("\n");
  // Only trim from the tail — a "Total: …" mid-body would be unusual, and we
  // don't want to nuke a legit scope bullet that happens to start with a word.
  let end = lines.length;
  while (end > 0) {
    const ln = lines[end - 1];
    if (ln.trim() === "" || TOTALS_LINE_RE.test(ln)) { end--; continue; }
    break;
  }
  return lines.slice(0, end).join("\n").trimEnd();
}

// ═══════════════════════════════════════════════════════════════════════════
// SaveTemplateCard — snapshots THIS quote (its full quote_json) as a named
// saved quote template. Future Smart Assessments pick it at Convert and the
// server adapts it to the new scope. Distinct from the proposal-framing
// saveAsTemplate in the ReviewPush modal and from the playbook Templates tab.
// ═══════════════════════════════════════════════════════════════════════════
function SaveTemplateCard({ quoteId, defaultName, onSaveIfDirty, onClose }) {
  const [name, setName] = useState(defaultName || "");
  const [desc, setDesc] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [done, setDone] = useState(false);

  const inputSt = {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
    color: C.text, fontSize: 13, padding: "8px 10px", fontFamily: "'Roboto', sans-serif",
  };

  async function save() {
    if (busy || done) return; // Enter in the inputs fires this too — no double-submit
    const n = name.trim();
    if (!n) { setMsg("⚠ Give the template a name."); return; }
    setBusy(true); setMsg(null);
    try {
      // Flush unsaved edits first: the server snapshots smart_quotes.quote_json
      // from the DB, and the autosave debounce is 2.5s — without this a
      // just-edited hours/material change silently misses the template.
      await onSaveIfDirty?.();
      const r = await apiFetch("/api/quote-brain", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "saveQuoteTemplate", quote_id: quoteId, name: n, description: desc.trim() }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      setDone(true);
      setMsg(`✓ Saved — “${j.name}” now shows in the template picker when converting an assessment.`);
    } catch (e) { setMsg("⚠ " + e.message); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ ...cardStyle, marginBottom: 12, padding: 12, border: `1.5px solid rgba(165,206,58,.35)` }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: C.accent, marginBottom: 4 }}>💾 Save as Template</div>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 8, lineHeight: 1.45 }}>
        Saves this whole quote (scope, materials, hours, notes) as a reusable base. On a future assessment, hit
        Convert and pick it — the new job's scope swaps in the client wording, cable runs and quantities, and
        prices re-match from the pricebook.
      </div>
      {done ? (
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, color: msg && msg.startsWith("⚠") ? C.warn : C.good }}>{msg}</span>
          <button style={btn("ghost")} onClick={onClose}>Close</button>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <input
              style={{ ...inputSt, flex: "1 1 220px" }}
              placeholder="Template name (e.g. 22kW Generac whole-home)"
              value={name}
              autoFocus
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") save(); }}
            />
            <input
              style={{ ...inputSt, flex: "2 1 280px" }}
              placeholder="Description (optional — when to reach for this one)"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") save(); }}
            />
          </div>
          <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button style={btn("primary")} onClick={save} disabled={busy}>{busy ? "Saving…" : "Save Template"}</button>
            <button style={btn("ghost")} onClick={onClose} disabled={busy}>Cancel</button>
            {msg && <span style={{ fontSize: 12, color: msg.startsWith("⚠") ? C.warn : C.muted }}>{msg}</span>}
          </div>
        </>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// ClientRow — shows the quote's Jobber client (or "no client") + an inline
// picker. A from-scratch quote isn't linked to a Jobber client, so pushing
// fails; this lets the owner search Jobber clients and attach one
// (setQuoteClient → assessment.jobber_client_id + client_name), then reloads.
// ═══════════════════════════════════════════════════════════════════════════
function ClientRow({ quoteId, assessment, onReload }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null); // null = no search yet, [] = searched-empty
  const [searching, setSearching] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignMsg, setAssignMsg] = useState(null);
  const debounceRef = useRef(null);
  const reqIdRef = useRef(0); // guards against out-of-order search responses

  const clientName = assessment?.client_name || "";
  // Directly linked (jobber_client_id) OR seeded from a Jobber request —
  // push resolves the request's client automatically (jb_requests, then a
  // live Jobber lookup), so a request-linked quote never needs manual
  // assignment. The picker stays available to override.
  const linkedDirect = !!assessment?.jobber_client_id;
  const linkedViaRequest = !linkedDirect && !!assessment?.jobber_request_id;
  const linked = linkedDirect || linkedViaRequest;

  // Debounced Jobber client search (min 2 chars).
  function onQueryChange(v) {
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const term = v.trim();
    if (term.length < 2) { setResults(null); setSearching(false); return; }
    setSearching(true);
    const myReq = ++reqIdRef.current;
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await apiFetch("/api/quote-brain", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify({ action: "searchClients", term }),
        });
        const j = await r.json();
        if (myReq !== reqIdRef.current) return; // a newer keystroke superseded this
        if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
        setResults(Array.isArray(j.clients) ? j.clients : []);
      } catch {
        if (myReq === reqIdRef.current) setResults([]);
      } finally {
        if (myReq === reqIdRef.current) setSearching(false);
      }
    }, 300);
  }

  async function assign(client) {
    setAssigning(true);
    setAssignMsg(null);
    try {
      const r = await apiFetch("/api/quote-brain", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ action: "setQuoteClient", quote_id: quoteId, client_id: client.id, client_name: client.name }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      await onReload();
      setOpen(false);
      setQuery("");
      setResults(null);
      setAssignMsg(`Assigned to ${j.client_name || client.name}`);
    } catch (e) {
      setAssignMsg("Assign failed: " + e.message);
    } finally {
      setAssigning(false);
    }
  }

  function closePicker() {
    setOpen(false);
    setQuery("");
    setResults(null);
    setSearching(false);
  }

  return (
    <div style={{ ...cardStyle, marginTop: 12, padding: 12 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <span style={{ fontSize: 14 }}>👤</span>
        <span style={{ fontSize: 14, fontWeight: 600, color: clientName ? C.text : C.muted }}>
          {clientName || "No client assigned"}
        </span>
        {linked ? (
          <span style={{ ...pill(C.good), fontSize: 11 }}>
            {linkedViaRequest ? "✓ via Jobber request" : "✓ linked to Jobber"}
          </span>
        ) : (
          <span style={{ fontSize: 11, color: C.muted }}>not linked — assign to push</span>
        )}
        <div style={{ flex: 1 }} />
        <button style={{ ...btn("ghost"), fontSize: 12, padding: "6px 10px" }} onClick={() => (open ? closePicker() : setOpen(true))}>
          {open ? "✕ Close" : linked ? "Change" : "Assign client"}
        </button>
      </div>

      {open && (
        <div style={{ marginTop: 10, position: "relative" }}>
          <input
            autoFocus
            value={query}
            onChange={e => onQueryChange(e.target.value)}
            onKeyDown={e => { if (e.key === "Escape") closePicker(); }}
            placeholder="Search Jobber clients…"
            style={{ ...input() }}
          />
          {query.trim().length >= 2 && (
            <div style={{ marginTop: 6, border: `1px solid ${C.border}`, borderRadius: 8, background: C.surface2, maxHeight: 260, overflowY: "auto" }}>
              {searching ? (
                <div style={{ padding: "10px 12px", fontSize: 13, color: C.muted }}>Searching…</div>
              ) : results && results.length ? (
                results.slice(0, 10).map((c) => (
                  <button
                    key={c.id}
                    onClick={() => assign(c)}
                    disabled={assigning}
                    style={{
                      display: "block", width: "100%", textAlign: "left", border: "none",
                      borderBottom: `1px solid ${C.border}`, background: "transparent", color: C.text,
                      padding: "9px 12px", fontSize: 13, fontFamily: "inherit",
                      cursor: assigning ? "wait" : "pointer",
                    }}
                  >
                    {c.name}
                  </button>
                ))
              ) : (
                <div style={{ padding: "10px 12px", fontSize: 13, color: C.muted }}>No matches</div>
              )}
            </div>
          )}
        </div>
      )}

      {assignMsg && (
        <div style={{ marginTop: 8, fontSize: 12, color: assignMsg.startsWith("Assign failed") ? C.danger : C.good }}>
          {assignMsg}
        </div>
      )}
    </div>
  );
}

function ReviewPushModal({ quoteId, draft, onSaveIfDirty, onReloadDraft, onClose }) {
  const [includeWarranty, setIncludeWarranty] = useState(draft?.include_warranty ?? false);
  const [body, setBody] = useState("");
  const [bodyTouched, setBodyTouched] = useState(false);
  const [loadingText, setLoadingText] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // Delivery: how this client-facing quote goes out. "jobber" = the existing
  // push-to-Jobber flow (unchanged). "elecbid" = send via our own portal
  // (Client 360) with photos/attachments/intro Jobber's API can't carry.
  const [deliveryMode, setDeliveryMode] = useState("jobber"); // "jobber" | "elecbid"

  // Push target mode: "fill_draft" (DEFAULT) = write the line items into the
  // Jobber draft the owner already converted Request→Quote, so his template's
  // cover photo, intro and deposit survive. "create" = a brand-new Jobber quote,
  // which carries none of that — secondary, and warned about when picked.
  const [pushMode, setPushMode] = useState("fill_draft"); // "fill_draft" | "create"
  const [targetQuoteNum, setTargetQuoteNum] = useState(""); // fill mode: Jobber quote # (auto-filled)
  const fillMode = pushMode === "fill_draft";

  // The title the Jobber quote ends up with — pre-filled from the quote's own
  // title so a filled draft stops carrying the REQUEST's wording ("Request from
  // the website"). Untouched → the server computes it; blank → leave Jobber's
  // title alone.
  const [quoteTitle, setQuoteTitle] = useState("");
  const titleTouchedRef = useRef(false); // a ref, so the lookup effect keeps [quoteId] deps

  // Auto-resolved fill target — the draft # is on screen before he clicks, so
  // the normal path is "look at it, hit Push". null = still looking.
  const [draftLookup, setDraftLookup] = useState(null); // { target, candidates, reason, clientName }
  const [showTargetPicker, setShowTargetPicker] = useState(false);
  // Once he picks a mode himself, the lookup stops steering it.
  const modeTouchedRef = useRef(false);
  function chooseMode(val) {
    modeTouchedRef.current = true;
    setPushMode(val);
    setPushResult(null);
  }

  // Deposit
  const [depositAmount, setDepositAmount] = useState("");
  const [depositType, setDepositType] = useState("Unit"); // "Unit" = $, "Percent" = %

  // Refine
  const [refineInput, setRefineInput] = useState("");
  const [refineBusy, setRefineBusy] = useState(false);
  const [refineNote, setRefineNote] = useState(null);

  // The same conversation, carried into the client-facing stage: assessment
  // scope chat + refine turns, so a follow-up typed here has its context on
  // screen (and the server feeds the same history to the refine model).
  const [convo, setConvo] = useState(null);
  const [convoOpen, setConvoOpen] = useState(false);

  // Auto-tidy of HAND-EDITED client-facing text (spelling/caps/smoothing).
  // Cache keyed by input text: dedupes blur-vs-push races, and outputs are
  // cached to themselves so already-polished text never makes a second trip.
  const polishCacheRef = useRef(new Map()); // text → Promise<polished>
  const [polishBusy, setPolishBusy] = useState(false);
  const [polishNote, setPolishNote] = useState(null);

  // Multi-option: one editable description per package. null = single-option
  // (use the `body` textarea path). Each row: { index, label, recommended,
  // sectioned, subtotal, description (server render), edited (textarea value|null) }.
  const [optionRows, setOptionRows] = useState(null);
  // Quote was built in phases but hand-edited since — sections are stale, so it
  // renders and pushes as ONE line (the server applies the same rule).
  const [sectionsStale, setSectionsStale] = useState(false);
  // "Push anyway" on the unverified-prices gate — sticky for the rest of this
  // modal session, so a follow-up pick_draft round-trip doesn't re-gate.
  const [acceptedUnverified, setAcceptedUnverified] = useState(false);

  // Push / preview
  const [pushBusy, setPushBusy] = useState(false);
  const [pushResult, setPushResult] = useState(null); // null | {dry_run,preview} | {quote} | {error}

  // Totals strip (live from the current draft)
  const t = draft?.totals || {};
  const permitFee = Number(draft?.permit_fee ?? t.permit_fee ?? 0);
  const subtotal  = Number(t.subtotal ?? 0);
  const workTotal = +(subtotal - permitFee).toFixed(2);
  const gst       = +(subtotal * 0.05).toFixed(2);

  // Fetch the client-facing render (respecting the warranty toggle). Called on
  // open, on warranty toggle, and after a refine. On (re)fetch we replace the
  // body — per spec, manual edits are only preserved when untouched.
  // Wording & tone: standard = consistent material wording (default),
  // original = backend text as entered, casual/salesy = AI-reworded tone.
  const [wordingMode, setWordingMode] = useState("standard");

  // AI reword helper — returns restyled blocks (or null to keep originals).
  const restyle = useCallback(async (texts, style) => {
    try {
      const r = await apiFetch("/api/quote-brain", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "restyleClientFacing", style, texts }),
      });
      const j = await r.json();
      return (r.ok && j.ok !== false && Array.isArray(j.texts)) ? j.texts : null;
    } catch { return null; }
  }, []);

  const fetchClientFacing = useCallback(async (warranty, mode = "standard") => {
    setLoadingText(true);
    setLoadError(null);
    try {
      const wording = mode === "original" ? "original" : "standard";
      const r = await apiFetch("/api/quote-brain", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ action: "clientFacing", quote_id: quoteId, include_warranty: warranty, wording }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      const restyling = mode === "casual" || mode === "salesy";
      setSectionsStale(!!j.sections_stale);
      if (Array.isArray(j.options)) {
        // Multi-option OR sectioned: one editable description per Jobber line.
        // Sectioned rows carry sectioned:true — the prices ADD UP (firm phases)
        // instead of competing pick-one, and push as section_descriptions.
        // Re-fetch replaces all rows (same replace-on-refetch as the textarea).
        let rows = j.options.map((o) => ({
          index:       o.index,
          key:         o.key,
          label:       o.label,
          recommended: o.recommended,
          sectioned:   !!j.is_sectioned || !!o.sectioned,
          subtotal:    o.subtotal,
          description: stripTotalsLines(o.description || ""),
          edited:      null,
        }));
        if (restyling) {
          const out = await restyle(rows.map((r2) => r2.description), mode);
          if (out) rows = rows.map((r2, i) => ({ ...r2, description: out[i] ?? r2.description }));
        }
        setOptionRows(rows);
        setBody("");
      } else {
        setOptionRows(null);
        let text = stripTotalsLines(j.text);
        if (restyling) {
          const out = await restyle([text], mode);
          if (out) text = out[0] ?? text;
        }
        setBody(text);
      }
      setBodyTouched(false);
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setLoadingText(false);
    }
  }, [quoteId, restyle]);

  // On open: save if dirty, then fetch the client-facing text.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { await onSaveIfDirty(); } catch { /* surfaced elsewhere */ }
      if (!cancelled) await fetchClientFacing(includeWarranty, "standard");
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // On open (in parallel with the text render): find the Jobber draft this
  // quote should fill and pre-fill its number. No draft to fill → fall back to
  // "Create new quote" so the modal is never stuck pointing at nothing.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiFetch("/api/quote-brain", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "findDraftTarget", quote_id: quoteId }),
        });
        const j = await r.json();
        if (cancelled) return;
        if (!r.ok || j.ok === false) { setDraftLookup({ target: null, candidates: [], reason: "lookup_failed" }); return; }
        setDraftLookup(j);
        if (j.elecbidTitle) setQuoteTitle(cur => (titleTouchedRef.current ? cur : j.elecbidTitle));
        if (j.target?.quoteNumber != null) setTargetQuoteNum(String(j.target.quoteNumber));
        else if (!modeTouchedRef.current) setPushMode("create");
      } catch {
        if (!cancelled) setDraftLookup({ target: null, candidates: [], reason: "lookup_failed" });
      }
    })();
    return () => { cancelled = true; };
  }, [quoteId]);

  // Pick a different draft from the candidate list.
  function chooseTarget(d) {
    setTargetQuoteNum(String(d.quoteNumber));
    setDraftLookup(cur => (cur ? { ...cur, target: d } : cur));
    setShowTargetPicker(false);
    setPushResult(null);
  }

  // Load the conversation history (assessment scope chat + earlier refines).
  const loadConvo = useCallback(async () => {
    try {
      const r = await apiFetch("/api/quote-brain", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "chatHistory", quote_id: quoteId }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) return;
      const turns = [];
      for (const m of (j.scope_chat || [])) {
        const text = m.text || ((m.attachments || []).length ? `📎 ${m.attachments.join(", ")}` : "");
        if (text) turns.push({ who: m.role === "you" ? "Tech" : "AI", text });
      }
      for (const t of (j.refines || [])) {
        if (t.instruction) turns.push({ who: "Tech", text: t.instruction });
        if (t.reply) turns.push({ who: "AI", text: t.reply });
      }
      setConvo(turns);
    } catch { /* section simply doesn't render */ }
  }, [quoteId]);
  useEffect(() => { loadConvo(); }, [loadConvo]);

  // One tidy pass for a hand-edited block. Returns the cleaned text (or the
  // original on any failure — the tech's own words are never a blocker).
  const polishText = useCallback((text) => {
    const t = String(text || "");
    if (!t.trim()) return Promise.resolve(t);
    const cache = polishCacheRef.current;
    if (cache.has(t)) return cache.get(t);
    const p = (async () => {
      try {
        const r = await apiFetch("/api/quote-brain", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "polishClientFacing", texts: [t] }),
        });
        const j = await r.json();
        const out = (r.ok && j.ok !== false && Array.isArray(j.texts) && typeof j.texts[0] === "string" && j.texts[0].trim())
          ? j.texts[0] : t;
        cache.set(out, Promise.resolve(out)); // clean output — never re-polish it
        return out;
      } catch { return t; }
    })();
    cache.set(t, p);
    return p;
  }, []);

  // Blur handlers: tidy the edit in place so the tech SEES the cleaned text
  // before it pushes. Functional setState guards against clobbering text the
  // tech kept typing while the polish was in flight.
  async function polishBody() {
    if (!bodyTouched || !body.trim() || polishCacheRef.current.has(body)) return;
    const input = body;
    setPolishBusy(true);
    try {
      const out = await polishText(input);
      if (out !== input) {
        setBody(cur => (cur === input ? out : cur));
        setPolishNote("✨ Tidied the wording — spelling & caps cleaned up.");
      }
    } finally { setPolishBusy(false); }
  }

  async function polishOption(index) {
    const row = (optionRows || []).find(r => r.index === index);
    if (!row || row.edited == null || row.edited === row.description) return;
    const input = row.edited;
    if (!input.trim() || polishCacheRef.current.has(input)) return;
    setPolishBusy(true);
    try {
      const out = await polishText(input);
      if (out !== input) {
        setOptionRows(rows => (rows || []).map(r => (r.index === index && r.edited === input ? { ...r, edited: out } : r)));
        setPolishNote("✨ Tidied the wording — spelling & caps cleaned up.");
      }
    } finally { setPolishBusy(false); }
  }

  async function toggleWarranty(next) {
    setIncludeWarranty(next);
    await fetchClientFacing(next, wordingMode); // re-fetch + replace (drops manual edits — simplest per spec)
  }

  async function changeWording(mode) {
    setWordingMode(mode);
    await fetchClientFacing(includeWarranty, mode);
  }

  async function handleRefine() {
    const instruction = refineInput.trim();
    if (!instruction || refineBusy) return;
    setRefineBusy(true);
    setRefineNote(null);
    try {
      const r = await apiFetch("/api/quote-brain", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ action: "refineQuote", quote_id: quoteId, instruction }),
      });
      const j = await r.json();
      if (!r.ok || j.ok === false) throw new Error(j.error || `HTTP ${r.status}`);
      setRefineNote(j.reply || "Refined.");
      setRefineInput("");
      await onReloadDraft();               // pull the refined materials/scope into the page
      await fetchClientFacing(includeWarranty, wordingMode); // refresh the description
      loadConvo();                         // the conversation just grew a turn
    } catch (e) {
      setRefineNote("⚠ " + e.message);
    } finally {
      setRefineBusy(false);
    }
  }

  // Core push. In fill mode we send mode:"fill_draft" (+ optional target quote
  // #) and omit the deposit (the Jobber template already set it — server
  // ignores it in fill mode). `targetOverride` lets the pick-draft list re-push
  // straight to a chosen draft's quoteNumber.
  async function doPush({ dry_run, targetOverride, acceptUnverified } = {}) {
    setPushBusy(true);
    if (!dry_run) setPushResult(null);
    try {
      const payload = {
        action: "pushToJobber",
        quote_id: quoteId,
        dry_run: !!dry_run,
        include_warranty: includeWarranty,
        wording: wordingMode === "original" ? "original" : "standard",
        // Set by the "Push anyway" button on the unverified-prices gate (and
        // sticky via acceptedUnverified so a pick_draft round-trip after the
        // tap doesn't re-gate) — a real push with red AI-guess lines needs
        // this explicit tap first.
        ...((acceptUnverified || acceptedUnverified) ? { accept_unverified: true } : {}),
      };
      // Title: only sent once he's typed in the field — untouched, the server
      // derives the same thing it pre-filled. Cleared on purpose = "don't
      // rename the Jobber draft" (create mode still needs one, so it computes).
      if (titleTouchedRef.current) {
        const t = quoteTitle.trim();
        if (t) payload.title_override = t;
        else payload.update_title = false;
      }
      // Multi-option → one description per package (edited value or the server
      // render), index order. Single-option → the single description_override.
      // Both apply in fill mode too, so appended lines carry the edited wording.
      //
      // Hand-edits get one tidy pass (spelling/caps) before they ship — awaiting
      // here also closes the type-then-immediately-push race where the blur
      // polish hasn't landed yet. The cache makes an already-tidied blur free.
      if (optionRows) {
        const snap = optionRows;
        const descs = [];
        for (const r of snap) {
          const edited = r.edited != null && r.edited !== r.description;
          descs.push(edited ? await polishText(r.edited) : (r.edited ?? r.description));
        }
        // Sectioned rows are firm phases → section_descriptions; pick-one
        // packages → option_descriptions. (Server accepts either name.)
        if (snap.some(r => r.sectioned)) payload.section_descriptions = descs;
        else payload.option_descriptions = descs;
        // Reflect the tidied text in the textareas — but never over a newer edit.
        setOptionRows(rows => (rows || []).map(r => {
          const i = snap.findIndex(s => s.index === r.index);
          return (i >= 0 && snap[i].edited != null && r.edited === snap[i].edited) ? { ...r, edited: descs[i] } : r;
        }));
      } else {
        const preBody = body;
        const finalBody = bodyTouched ? await polishText(preBody) : preBody;
        payload.description_override = finalBody;
        if (finalBody !== preBody) setBody(cur => (cur === preBody ? finalBody : cur));
      }
      if (fillMode) {
        payload.mode = "fill_draft";
        const tgt = targetOverride != null
          ? targetOverride
          : (targetQuoteNum.trim() ? parseInt(targetQuoteNum.trim(), 10) : null);
        payload.target_quote_number = Number.isFinite(tgt) ? tgt : null;
        payload.deposit = null; // template owns the deposit in fill mode
      } else {
        const amt = Number(depositAmount);
        payload.deposit = amt > 0 ? { rate: amt, type: depositType } : null;
      }
      const r = await apiFetch("/api/quote-brain", {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify(payload),
      });
      const j = await r.json();
      // Fill-mode "needs a decision" responses aren't hard errors — carry the
      // whole body through so the render can branch on j.need.
      if (!r.ok || j.ok === false) {
        if (j && j.need) { setPushResult(j); return; }
        setPushResult({ error: j.error || `HTTP ${r.status}`, userErrors: j.userErrors });
        return;
      }
      setPushResult(j);
    } catch (e) {
      setPushResult({ error: e.message });
    } finally {
      setPushBusy(false);
    }
  }

  // Clicking a draft in the pick_draft list re-pushes straight into it. The
  // pick persists into the target field so a later retry (e.g. after the
  // unverified-prices confirm) re-derives the same draft instead of ping-
  // ponging back to pick_draft.
  function pickDraft(quoteNumber) {
    setTargetQuoteNum(String(quoteNumber));
    doPush({ dry_run: false, targetOverride: quoteNumber });
  }

  // Edit one option's description textarea (multi-option).
  function editOption(index, value) {
    setOptionRows((rows) => (rows || []).map((r) => (r.index === index ? { ...r, edited: value } : r)));
  }

  const isMulti = Array.isArray(optionRows) && optionRows.length > 0;
  const isSectioned = isMulti && optionRows.some(r => r.sectioned);

  const pushedOk = pushResult && pushResult.quote && !pushResult.dry_run && pushResult.ok !== false;
  const pickDraftNeeded = pushResult && pushResult.need === "pick_draft";
  const infoNeed = pushResult && (pushResult.need === "no_draft" || pushResult.need === "not_found" || pushResult.need === "no_client");
  // Server-side hard check: red AI-guess lines block a real push until the tech
  // re-prices or explicitly taps "Push anyway" (which resends accept_unverified).
  const confirmUnverified = pushResult && pushResult.need === "confirm_unverified";

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 200, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, width: "100%", maxWidth: 760, maxHeight: "92vh", overflowY: "auto" }}>
        <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", position: "sticky", top: 0, background: C.surface, zIndex: 1 }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>{deliveryMode === "elecbid" ? "Send via ElecBid (Client 360)" : "Review & Push to Jobber"}</div>
          <button onClick={onClose} style={btn("ghost")}>✕</button>
        </div>

        <div style={{ padding: 20 }}>
          {/* Delivery choice: Jobber push (as-is) vs send via ElecBid (Client 360) */}
          <div style={{ marginBottom: 16 }}>
            <label style={lbl()}>How to send this to the client</label>
            <div style={{ display: "flex", border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden", width: "fit-content" }}>
              {[["jobber", "Push to Jobber"], ["elecbid", "Send via ElecBid"]].map(([val, label]) => (
                <button key={val} onClick={() => setDeliveryMode(val)}
                  style={{ border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700, padding: "8px 14px",
                    background: deliveryMode === val ? C.accent : C.surface2, color: deliveryMode === val ? C.bg : C.text }}>
                  {label}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
              {deliveryMode === "elecbid"
                ? "Adds photos, attachments, an intro & hero image — the things Jobber's API can't carry — and sends straight to the client's portal."
                : "Pushes to Jobber exactly as before (creates or fills a Jobber quote)."}
            </div>
          </div>

          {deliveryMode === "elecbid" ? (
            <ElecBidProposalPanel quoteId={quoteId} draft={draft} optionRows={optionRows} onClose={onClose} />
          ) : (
          <>
          {/* Push target mode — filling his existing draft is the default path */}
          <div style={{ marginBottom: 16 }}>
            <label style={lbl()}>How to push</label>
            <div style={{ display: "flex", border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden", width: "fit-content" }}>
              {[["fill_draft", "Fill my existing draft"], ["create", "Create new quote"]].map(([val, label]) => (
                <button
                  key={val}
                  onClick={() => chooseMode(val)}
                  style={{
                    border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700, padding: "8px 14px",
                    background: pushMode === val ? C.accent : C.surface2,
                    color: pushMode === val ? C.bg : C.text,
                  }}
                >{label}</button>
              ))}
            </div>

            {/* CREATE mode: spell out what a fresh quote does NOT carry over. */}
            {!fillMode && (
              <div style={{ marginTop: 8, padding: "9px 11px", borderRadius: 8, background: "rgba(245,158,11,0.08)", border: `1px solid rgba(245,158,11,0.3)`, fontSize: 12, color: C.warn, lineHeight: 1.5 }}>
                ⚠ A brand-new Jobber quote starts empty — <strong>no cover photo, no intro or default client message, no template deposit</strong>. Those live on the draft your Request → Quote conversion creates. Only use this when there's no draft to fill.
              </div>
            )}

            {fillMode && (
              <div style={{ marginTop: 10 }}>
                {draftLookup === null ? (
                  <div style={{ fontSize: 12, color: C.muted }}>Looking up this client's Jobber draft…</div>
                ) : draftLookup.target ? (
                  <>
                    <div style={{ padding: "10px 12px", borderRadius: 8, background: C.accentDim, border: `1px solid ${C.accent}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 14, fontWeight: 700, color: C.accent }}>
                          Filling Jobber quote #{draftLookup.target.quoteNumber}
                        </span>
                        {draftLookup.target.source === "pushed" && <span style={{ ...pill(C.muted), fontSize: 10 }}>already pushed here</span>}
                        {draftLookup.target.source === "request" && <span style={{ ...pill(C.good), fontSize: 10 }}>from this request</span>}
                        <div style={{ flex: 1 }} />
                        {(draftLookup.candidates || []).length > 1 && (
                          <button style={{ ...btn("ghost"), fontSize: 11, padding: "4px 9px" }} onClick={() => setShowTargetPicker(s => !s)}>
                            {showTargetPicker ? "✕ Close" : `Change (${draftLookup.candidates.length} drafts)`}
                          </button>
                        )}
                      </div>
                      <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                        {[draftLookup.target.title, draftLookup.clientName, draftLookup.target.total != null ? fmt$(draftLookup.target.total) : null]
                          .filter(Boolean).join(" · ") || "Scope, materials & permit drop into this draft."}
                      </div>
                    </div>

                    {showTargetPicker && (
                      <div style={{ marginTop: 6, border: `1px solid ${C.border}`, borderRadius: 8, background: C.surface2, maxHeight: 220, overflowY: "auto" }}>
                        {draftLookup.candidates.map(d => (
                          <button
                            key={d.id || d.quoteNumber}
                            onClick={() => chooseTarget(d)}
                            style={{
                              display: "block", width: "100%", textAlign: "left", border: "none", borderBottom: `1px solid ${C.border}`,
                              background: String(d.quoteNumber) === String(targetQuoteNum) ? C.accentDim : "transparent",
                              color: C.text, padding: "9px 12px", fontSize: 13, fontFamily: "inherit", cursor: "pointer",
                            }}
                          >
                            <span style={{ fontWeight: 700, color: C.accent }}>#{d.quoteNumber}</span>
                            {d.title ? ` · ${d.title}` : ""}
                            {d.total != null ? ` · ${fmt$(d.total)}` : ""}
                            {d.createdAt ? <span style={{ color: C.muted }}>{` · ${new Date(d.createdAt).toLocaleDateString()}`}</span> : null}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ padding: "9px 11px", borderRadius: 8, background: "rgba(245,158,11,0.08)", border: `1px solid rgba(245,158,11,0.3)`, fontSize: 12, color: C.warn, lineHeight: 1.5 }}>
                    {draftLookup.reason === "no_client"
                      ? "This quote isn't linked to a Jobber client yet, so there's no draft to look up — close this and use “Assign client” on the quote page, or type the quote # below and it'll use that draft's client."
                      : draftLookup.reason === "lookup_failed"
                        ? "Couldn't reach Jobber to find the draft — type the quote # below, or switch to Create new quote."
                        : `No draft quote in Jobber${draftLookup.clientName ? ` for ${draftLookup.clientName}` : ""} yet. Convert the Request → Quote in Jobber (that applies your template), then reopen this — or type the quote # below.`}
                  </div>
                )}

                {/* Manual override — always available, pre-filled with what we found. */}
                <div style={{ marginTop: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <label style={{ ...lbl(), margin: 0 }}>Jobber quote #</label>
                  <input
                    type="number"
                    placeholder="e.g. 1712"
                    value={targetQuoteNum}
                    onChange={e => { setTargetQuoteNum(e.target.value); setPushResult(null); }}
                    style={{ ...input(), width: 130 }}
                  />
                  <span style={{ fontSize: 11, color: C.muted }}>blank = find the draft at push time</span>
                </div>
              </div>
            )}
          </div>

          {/* Quote title — what the Jobber quote gets CALLED. A draft converted
              from the request carries the request's wording, so filling it also
              retitles it to the job we actually priced. */}
          <div style={{ marginBottom: 14 }}>
            <label style={lbl()}>Quote title in Jobber</label>
            <input
              value={quoteTitle}
              maxLength={200}
              placeholder={fillMode ? "blank = keep the draft's current title" : "e.g. 200A Service Upgrade"}
              onChange={e => { titleTouchedRef.current = true; setQuoteTitle(e.target.value); setPushResult(null); }}
              style={{ ...input(), width: "100%" }}
            />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              {!fillMode
                ? "The new Jobber quote is created with this title."
                : quoteTitle.trim()
                  ? `Renames the draft${targetQuoteNum ? ` #${targetQuoteNum}` : ""} to this${draftLookup?.target?.title ? ` — it's “${draftLookup.target.title}” right now` : ""}.`
                  : "Blank — the draft keeps whatever title it already has."}
            </div>
          </div>

          {/* Wording & tone — how the material/scope wording reads to the client */}
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            <span style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>Wording</span>
            {[
              ["standard", "Standard", "Cleans up material wording so it reads the same (e.g. Siemens 2-pole 15A breaker)"],
              ["original", "Original", "Keep the back-end wording exactly as entered"],
              ["casual",   "Casual",   "AI rewords the scope/notes into a friendly, plain tone (facts unchanged)"],
              ["salesy",   "Salesy",   "AI rewords into a polished, benefit-forward sales tone (facts unchanged)"],
            ].map(([val, label, tip]) => (
              <button
                key={val}
                type="button"
                title={tip}
                disabled={loadingText}
                onClick={() => changeWording(val)}
                style={{
                  border: `1px solid ${wordingMode === val ? C.accent : C.border}`, cursor: loadingText ? "wait" : "pointer",
                  fontFamily: "inherit", fontSize: 12, fontWeight: 700, padding: "5px 11px", borderRadius: 999,
                  background: wordingMode === val ? C.accent : "transparent",
                  color: wordingMode === val ? C.bg : C.text,
                }}
              >{label}</button>
            ))}
            {(wordingMode === "casual" || wordingMode === "salesy") && (
              <span style={{ fontSize: 11, color: C.muted }}>AI reword — review before pushing</span>
            )}
          </div>

          {/* Editable client-facing description(s) */}
          <label style={lbl()}>
            {isSectioned ? "Client-facing descriptions — one per phase (phases add up; this is what pushes)"
              : isMulti ? "Client-facing descriptions — one per option (editable, this is what pushes)"
              : "Client-facing description (editable — this is what pushes)"}
          </label>
          {sectionsStale && (
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
              Built in phases originally, but the edited lines lost their phase tags — it pushes as ONE work line (plus the permit line), exactly as shown below.
            </div>
          )}
          {loadingText ? (
            <div style={{ ...cardStyle, background: C.surface2, color: C.muted, fontSize: 13 }}>{(wordingMode === "casual" || wordingMode === "salesy") ? "Rewording…" : "Rendering client-facing text…"}</div>
          ) : loadError ? (
            <div style={{ ...cardStyle, background: "rgba(239,68,68,0.08)", borderColor: "rgba(239,68,68,0.3)", color: C.danger, fontSize: 13 }}>
              Couldn't render: {loadError}
              <div style={{ marginTop: 8 }}><button style={btn()} onClick={() => fetchClientFacing(includeWarranty, wordingMode)}>Retry</button></div>
            </div>
          ) : isMulti ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              {optionRows.map((o) => {
                const sub  = Number(o.subtotal || 0);
                const ogst = +(sub * 0.05).toFixed(2);
                return (
                  <div key={o.index} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, background: C.surface2 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 2 }}>
                      {o.label || `Option ${o.index + 1}`}
                      {o.recommended && <span style={{ color: C.good }}> (Recommended)</span>}
                      <span style={{ color: C.accent }}> — {fmt$(sub)}</span>
                    </div>
                    <textarea
                      value={o.edited ?? o.description}
                      onChange={e => editOption(o.index, e.target.value)}
                      onBlur={() => polishOption(o.index)}
                      rows={9}
                      style={{ ...input(), resize: "vertical", minHeight: 160, lineHeight: 1.5, fontFamily: "inherit", marginTop: 6 }}
                    />
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 6 }}>
                      {o.sectioned
                        ? <>Jobber line price <strong style={{ color: C.text }}>{fmt$(sub)}</strong> — phases add up to the quote total</>
                        : <>Subtotal {fmt$(sub)} · GST 5% {fmt$(ogst)} · Total <strong style={{ color: C.text }}>{fmt$(sub + ogst)}</strong></>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <textarea
              value={body}
              onChange={e => { setBody(e.target.value); setBodyTouched(true); }}
              onBlur={polishBody}
              rows={12}
              style={{ ...input(), resize: "vertical", minHeight: 220, lineHeight: 1.5, fontFamily: "inherit" }}
            />
          )}
          {!isMulti && bodyTouched && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Edited — your changes will push; spelling &amp; caps get tidied automatically. (Toggling warranty or refining re-renders and replaces this.)</div>}
          {isMulti && (
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              {isSectioned
                ? "Each phase pushes as its own firm Jobber line (they add up — plus a separate permit line); edited wording gets tidied automatically. Toggling warranty or refining re-renders and replaces these."
                : "Each option pushes as its own Jobber line; edited wording gets tidied automatically. Toggling warranty or refining re-renders and replaces these."}
            </div>
          )}
          {polishBusy && <div style={{ fontSize: 11, color: C.accent, marginTop: 4 }}>✨ Tidying the wording…</div>}
          {!polishBusy && polishNote && <div style={{ fontSize: 11, color: C.good, marginTop: 4 }}>{polishNote}</div>}

          {/* Warranty */}
          <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={includeWarranty} onChange={e => toggleWarranty(e.target.checked)} disabled={loadingText} />
            Include 3-yr labour warranty line
          </label>

          {/* Deposit — create mode only (fill mode uses the Jobber template's deposit) */}
          {fillMode ? (
            <div style={{ marginTop: 14, fontSize: 12, color: C.muted, fontStyle: "italic" }}>
              Deposit comes from your Jobber template.
            </div>
          ) : (
            <div style={{ marginTop: 14 }}>
              <label style={lbl()}>Deposit — override (optional)</label>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input
                  type="number" step="1" min="0" placeholder="auto"
                  value={depositAmount}
                  onChange={e => setDepositAmount(e.target.value)}
                  style={{ ...input(), width: 120 }}
                />
                <div style={{ display: "flex", border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
                  {[["Unit", "$"], ["Percent", "%"]].map(([val, sym]) => (
                    <button
                      key={val}
                      onClick={() => setDepositType(val)}
                      style={{
                        border: "none", cursor: "pointer", fontFamily: "inherit", fontSize: 13, fontWeight: 700, padding: "8px 14px",
                        background: depositType === val ? C.accent : C.surface2,
                        color: depositType === val ? C.bg : C.text,
                      }}
                    >{sym}</button>
                  ))}
                </div>
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Leave blank for your standard: <b>35%</b> (auto <b>50%</b> for generators, solar, Gemstone). Type a number here only to override this quote.</div>
            </div>
          )}

          {/* Conversation so far — assessment scope chat + earlier refines, so a
              follow-up typed below has its context on screen (the model gets the
              same history server-side). */}
          {Array.isArray(convo) && convo.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <button
                onClick={() => setConvoOpen(o => !o)}
                style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", padding: 0, fontFamily: "inherit" }}
              >
                {convoOpen ? "▾" : "▸"} 💬 Conversation so far ({convo.length}) — from the assessment &amp; refines
              </button>
              {convoOpen && (
                <div style={{ marginTop: 6, maxHeight: 220, overflowY: "auto", border: `1px solid ${C.border}`, borderRadius: 8, padding: 10, display: "flex", flexDirection: "column", gap: 6, background: C.surface2 }}>
                  {convo.slice(-30).map((t, i) => (
                    <div key={i} style={{ fontSize: 12 }}>
                      <span style={{ fontWeight: 700, color: t.who === "Tech" ? C.accent : C.muted }}>{t.who}:</span>{" "}
                      <span style={{ color: C.text, whiteSpace: "pre-wrap" }}>{t.text.length > 400 ? t.text.slice(0, 400) + "…" : t.text}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Refine with AI */}
          <div style={{ marginTop: 14 }}>
            <label style={lbl()}>Refine with AI</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                value={refineInput}
                onChange={e => setRefineInput(e.target.value)}
                onKeyDown={e => { if (e.key === "Enter") handleRefine(); }}
                placeholder="e.g. add a customer-supplied-panel option, drop the trenching line…"
                style={{ ...input(), flex: 1 }}
                disabled={refineBusy}
              />
              <button style={btn()} onClick={handleRefine} disabled={refineBusy || !refineInput.trim()}>
                {refineBusy ? "Refining…" : "Refine"}
              </button>
            </div>
            {refineNote && <div style={{ fontSize: 12, color: refineNote.startsWith("⚠") ? C.danger : C.muted, marginTop: 6, whiteSpace: "pre-wrap" }}>{refineNote}</div>}
          </div>

          {/* Totals strip (single-option only — multi shows per-option totals above) */}
          {!isMulti && (
            <div style={{ marginTop: 16, background: C.surface2, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, display: "grid", gridTemplateColumns: "1fr auto", rowGap: 4, columnGap: 12, fontSize: 13 }}>
              <span style={{ color: C.muted }}>Work (materials + labour)</span><span style={{ textAlign: "right" }}>{fmt$(workTotal)}</span>
              <span style={{ color: C.muted }}>Permit</span><span style={{ textAlign: "right" }}>{fmt$(permitFee)}</span>
              <span style={{ color: C.muted }}>GST 5%</span><span style={{ textAlign: "right" }}>{fmt$(gst)}</span>
              <span style={{ fontWeight: 700, color: C.text, borderTop: `1px solid ${C.border}`, paddingTop: 6 }}>Total</span>
              <span style={{ textAlign: "right", fontWeight: 700, color: C.accent, borderTop: `1px solid ${C.border}`, paddingTop: 6 }}>{fmt$(subtotal + gst)}</span>
            </div>
          )}

          {/* Inline push/preview result */}
          {pushResult && (
            <div style={{ marginTop: 14 }}>
              {pushResult.error ? (
                <div style={{ background: "rgba(239,68,68,0.08)", border: `1px solid rgba(239,68,68,0.3)`, padding: 12, borderRadius: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.danger, marginBottom: 6 }}>Push failed</div>
                  <div style={{ fontSize: 12, color: C.text, fontFamily: "ui-monospace, monospace", whiteSpace: "pre-wrap" }}>{pushResult.error}</div>
                  {Array.isArray(pushResult.userErrors) && pushResult.userErrors.length > 0 && (
                    <details style={{ marginTop: 8, fontSize: 11, color: C.muted }}>
                      <summary style={{ cursor: "pointer" }}>Jobber userErrors ({pushResult.userErrors.length})</summary>
                      <pre style={{ fontSize: 10, marginTop: 6, overflowX: "auto" }}>{JSON.stringify(pushResult.userErrors, null, 2)}</pre>
                    </details>
                  )}
                </div>
              ) : pickDraftNeeded ? (
                <div style={{ background: C.surface2, border: `1px solid ${C.border}`, padding: 12, borderRadius: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>
                    This client has {(pushResult.drafts || []).length} drafts — pick one:
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {(pushResult.drafts || []).map((d) => (
                      <button
                        key={d.id || d.quoteNumber}
                        onClick={() => pickDraft(d.quoteNumber)}
                        disabled={pushBusy}
                        style={{
                          border: `1px solid ${C.border}`, background: C.surface, color: C.text, borderRadius: 8,
                          padding: "8px 12px", cursor: pushBusy ? "wait" : "pointer", fontFamily: "inherit",
                          textAlign: "left", display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center",
                        }}
                      >
                        <span style={{ minWidth: 0 }}>
                          <span style={{ fontWeight: 700, color: C.accent }}>#{d.quoteNumber}</span>
                          {d.title ? <span style={{ color: C.text }}> — {d.title}</span> : null}
                        </span>
                        <span style={{ color: C.muted, fontSize: 12, whiteSpace: "nowrap" }}>
                          {d.total != null ? fmt$(d.total) : ""}{d.createdAt ? ` · ${new Date(d.createdAt).toLocaleDateString()}` : ""}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ) : confirmUnverified ? (
                <div style={{ background: "rgba(239,68,68,0.08)", border: `1px solid rgba(239,68,68,0.35)`, padding: 12, borderRadius: 8, fontSize: 13, color: C.text }}>
                  <div style={{ fontWeight: 700, marginBottom: 6 }}>
                    🔴 {pushResult.unverified?.count || "Some"} material line{(pushResult.unverified?.count || 2) === 1 ? "" : "s"} still carr{(pushResult.unverified?.count || 2) === 1 ? "ies" : "y"} unverified prices
                    {pushResult.unverified?.cost_value ? ` (≈$${pushResult.unverified.cost_value} cost)` : ""}
                  </div>
                  {Array.isArray(pushResult.unverified?.lines) && pushResult.unverified.lines.length > 0 && (
                    <div style={{ fontFamily: "ui-monospace, monospace", fontSize: 11.5, lineHeight: 1.6, marginBottom: 8, color: C.muted }}>
                      {pushResult.unverified.lines.map((l, i) => (
                        <div key={i}>{l.qty} {l.unit} · {l.description}{l.option ? ` — ${l.option}` : ""} · est ${l.our_cost}</div>
                      ))}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>
                    Fix them on the quote (🔍 a line or "Re-price AI-guess lines"), or push with these estimates as-is.
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={onClose} style={btn()}>Go fix the prices</button>
                    <button
                      onClick={() => { setAcceptedUnverified(true); doPush({ dry_run: false, acceptUnverified: true }); }}
                      disabled={pushBusy}
                      style={{ ...btn("ghost"), borderColor: "rgba(239,68,68,0.5)", color: "#ef4444" }}
                    >
                      {pushBusy ? "Pushing…" : "Push anyway with AI estimates"}
                    </button>
                  </div>
                </div>
              ) : infoNeed ? (
                <div style={{ background: "rgba(59,130,246,0.08)", border: `1px solid rgba(59,130,246,0.3)`, padding: 12, borderRadius: 8, fontSize: 13, color: C.text }}>
                  {pushResult.message || (pushResult.need === "no_draft"
                    ? "No draft found — convert the Request → Quote in Jobber first."
                    : pushResult.need === "no_client"
                      ? "No Jobber client on this quote — assign one on the quote page, or enter a quote #."
                      : "That quote number wasn't found.")}
                </div>
              ) : pushedOk ? (
                <div style={{ background: "rgba(16,185,129,0.08)", border: `1px solid rgba(16,185,129,0.3)`, padding: 12, borderRadius: 8 }}>
                  <div style={{ fontSize: 14, fontWeight: 700, color: C.good, marginBottom: 4 }}>
                    ✅ {pushResult.mode === "fill_draft" ? "Filled Jobber quote" : "Quote"} {pushResult.quote.quoteNumber ? `#${pushResult.quote.quoteNumber}` : ""} {pushResult.mode === "fill_draft" ? "" : "created in Jobber"}
                  </div>
                  {pushResult.quote.title && (
                    <div style={{ fontSize: 12, color: C.text }}>
                      {pushResult.quote.title}
                      {pushResult.quote.renamed && (
                        <span style={{ color: C.muted }}>
                          {pushResult.quote.previousTitle ? ` — retitled from “${pushResult.quote.previousTitle}”` : " — title set"}
                        </span>
                      )}
                    </div>
                  )}
                  {pushResult.quote.clientName && <div style={{ fontSize: 12, color: C.text }}>Client: {pushResult.quote.clientName}</div>}
                  {pushResult.quote.total != null && <div style={{ fontSize: 12, color: C.text }}>Total: ${pushResult.quote.total}</div>}
                  {pushResult.site_notes_target && <div style={{ fontSize: 12, color: C.good, marginTop: 2 }}>📝 Site notes added to the Jobber {pushResult.site_notes_target}</div>}
                  {pushResult.quote.jobberUrl && (
                    <a href={pushResult.quote.jobberUrl} target="_blank" rel="noreferrer" style={{ ...btn(), textDecoration: "none", display: "inline-block", marginTop: 8 }}>
                      Open in Jobber ↗
                    </a>
                  )}
                  {pushResult.warning && (
                    <div style={{ marginTop: 8, fontSize: 12, color: C.warn, background: "rgba(245,158,11,0.1)", border: `1px solid rgba(245,158,11,0.3)`, borderRadius: 6, padding: "6px 8px" }}>
                      ⚠ {pushResult.warning}
                    </div>
                  )}
                </div>
              ) : pushResult.dry_run && pushResult.preview && pushResult.preview.mode === "fill_draft" ? (
                <div style={{ background: C.surface2, border: `1px solid ${C.border}`, padding: 12, borderRadius: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Preview — fill draft (nothing sent)</div>
                  <div style={{ fontSize: 12, color: C.text, marginBottom: 4 }}>
                    Target: <strong>#{pushResult.preview.targetQuoteNumber}</strong>{pushResult.preview.targetTitle ? ` — ${pushResult.preview.targetTitle}` : ""}
                  </div>
                  <div style={{ fontSize: 12, color: pushResult.preview.newTitle ? C.accent : C.muted, marginBottom: 4 }}>
                    {pushResult.preview.newTitle
                      ? <>Retitles to: <strong>{pushResult.preview.newTitle}</strong></>
                      : "Title unchanged"}
                  </div>
                  <div style={{ fontSize: 12, color: pushResult.preview.willClearExisting ? C.warn : C.muted, marginBottom: 8 }}>
                    {pushResult.preview.willClearExisting
                      ? `Will clear ${pushResult.preview.existingLineCount} existing line(s)`
                      : `Keeps ${pushResult.preview.existingLineCount || 0} existing line(s)`}
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Line items to append:</div>
                  <div style={{ maxHeight: 160, overflowY: "auto", fontSize: 12 }}>
                    {(pushResult.preview.appendLineItems || []).map((li, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "2px 0" }}>
                        <span>{li.name}</span>
                        <span style={{ color: C.muted }}>{fmt$(li.unitPrice)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : pushResult.dry_run && pushResult.preview ? (
                <div style={{ background: C.surface2, border: `1px solid ${C.border}`, padding: 12, borderRadius: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 6 }}>Preview (nothing sent)</div>
                  <div style={{ fontSize: 12, color: C.text, marginBottom: 8 }}>Expected total: <strong>{fmt$(pushResult.preview.expectedTotal)}</strong></div>
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>Line items:</div>
                  <div style={{ maxHeight: 160, overflowY: "auto", fontSize: 12 }}>
                    {(pushResult.preview.variables?.input?.lineItems || []).map((li, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "2px 0" }}>
                        <span>{li.name}{li.optional ? " (optional)" : ""}</span>
                        <span style={{ color: C.muted }}>{fmt$(li.unitPrice)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          )}

          {/* Footer */}
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 }}>
            {pushedOk ? (
              <button style={btn("primary")} onClick={onClose}>Close</button>
            ) : (
              <>
                <button style={btn("ghost")} onClick={onClose} disabled={pushBusy}>Cancel</button>
                <button style={btn()} onClick={() => doPush({ dry_run: true })} disabled={pushBusy || loadingText} title="Dry-run — see what would push without firing it">
                  {pushBusy ? "…" : "Preview"}
                </button>
                <button style={btn("primary")} onClick={() => doPush({ dry_run: false })} disabled={pushBusy || loadingText}>
                  {pushBusy ? "Pushing…" : fillMode ? `Fill Jobber draft${targetQuoteNum.trim() ? ` #${targetQuoteNum.trim()}` : ""} →` : "Push to Jobber →"}
                </button>
              </>
            )}
          </div>
          </>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Style helpers ────────────────────────────────────────────────────────────

const pageStyle = {
  padding: "20px",
  minHeight: "100vh",
  background: C.bg,
  color: C.text,
  fontFamily: "'Roboto', sans-serif",
};

const cardStyle = {
  background: C.surface,
  border: `1px solid ${C.border}`,
  borderRadius: 12,
  padding: 16,
};

// Sticky options bar. The scroll container is Layout's `.layout-content`
// (overflow-y:auto), which carries padding-top:48px under the FIXED mobile
// header — so a sticky top:0 would park the bar underneath that header. Offset
// by 48 on mobile, 0 from 768px up where the nav moves to the left sidebar
// (same breakpoint Layout uses).
const QDR_CSS = `
.qdr-optbar-pinned{position:sticky;top:48px;z-index:30;box-shadow:0 6px 16px rgba(0,0,0,0.35)}
@media(min-width:768px){.qdr-optbar-pinned{top:0}}
`;

function btn(variant = "default") {
  const base = {
    border: `1px solid ${C.border}`,
    background: C.surface2,
    color: C.text,
    padding: "8px 14px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  };
  if (variant === "primary") return { ...base, background: C.accent, color: C.bg, border: "none" };
  if (variant === "ghost")   return { ...base, background: "transparent" };
  return base;
}

function iconBtn() {
  return {
    background: "transparent",
    border: `1px solid ${C.border}`,
    color: C.muted,
    width: 28, height: 28,
    borderRadius: 6, fontSize: 14, cursor: "pointer",
    fontFamily: "inherit",
  };
}

function input() {
  return {
    width: "100%",
    padding: "8px 10px",
    background: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    color: C.text,
    fontSize: 13,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
  };
}

function inputCell() {
  return {
    width: "100%",
    padding: "6px 8px",
    background: "transparent",
    border: `1px solid transparent`,
    borderRadius: 4,
    color: C.text,
    fontSize: 12,
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
  };
}

function lbl() {
  return {
    fontSize: 11,
    color: C.muted,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    fontWeight: 600,
    marginBottom: 4,
    display: "block",
  };
}

function pill(color) {
  return {
    display: "inline-flex",
    alignItems: "center",
    padding: "3px 8px",
    borderRadius: 999,
    background: `${color}1f`,
    color,
    fontSize: 11,
    fontWeight: 600,
  };
}

// ═══════════════════════════════════════════════════════════════════════════
// MaterialsFooter — what this quote's materials cost US vs what the client
// pays, on the bottom of the table. `sumIdxs` null = every line (the whole
// quote); an array = only those rows, which is how ticking the checkboxes on
// the left narrows the totals to the lines you picked. When it IS narrowed the
// whole-quote figures stay on a second line — a partial total that looks like
// the quote total is exactly how a quote goes out mispriced.
// Cost is PST-in (ourTotalPst), the same basis the quote's margin uses.
// ═══════════════════════════════════════════════════════════════════════════
function MaterialsFooter({ materials, sumIdxs, hiddenLotLines, basis, onBasisChange }) {
  const list = Array.isArray(materials) ? materials : [];
  const b = basis || { pst: false, gst: false };
  // `cost` is the DISPLAYED figure and follows the tax boxes. `costPst` is the
  // quote's real cost basis (PST in, no GST — GST is an input tax credit) and
  // is the ONLY thing profit and margin are ever computed from, so ticking a
  // box can never flatter the margin.
  const sum = (rows) => rows.reduce((a, m) => {
    a.cost += ourTotalShown(m, b);
    a.costPst += ourTotalPst(m);
    a.client += Number(m.client_total) || 0;
    return a;
  }, { cost: 0, costPst: 0, client: 0 });
  const allT = sum(list);
  const narrowed = Array.isArray(sumIdxs);
  const picked = narrowed ? sumIdxs.map(i => list[i]).filter(Boolean) : list;
  const selT = narrowed ? sum(picked) : allT;
  // COUNT EXACTLY WHAT WE SUM. A lot anchor is a real priced line — groupSelected
  // turns an existing row into the anchor and keeps its qty/cost, and
  // recomputeTotals prices it like any other — so the old `!m._lot` filter
  // (borrowed from applyBulkMarkup, where the premise is different) printed
  // "Selected · 0 lines" beside a real dollar total whenever the tech ticked an
  // anchor. Dollars were right; the count was not.
  const lineCount = list.length;
  const pickedCount = picked.length;
  const marginPct = (t) => (t.client > 0 ? ((t.client - t.costPst) / t.client) * 100 : 0);
  // When the displayed basis is not "PST in", the profit figure will not equal
  // client minus the cost on screen — so say what it WAS computed from rather
  // than leaving the tech to find the arithmetic broken.
  const marginBasisNote = (t) => (b.pst && !b.gst ? null : `on cost incl. PST ${money(t.costPst)}`);
  const box = (on, onChange, text, hint) => (
    <label title={hint} style={{ display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer", color: on ? C.accent : C.muted, fontWeight: on ? 700 : 400 }}>
      <input type="checkbox" checked={!!on} onChange={(e) => onChange(e.target.checked)} style={{ accentColor: C.accent, cursor: "pointer" }} />
      {text}
    </label>
  );
  const toggles = (
    <span style={{ display: "inline-flex", gap: 14, alignItems: "center", fontSize: 11.5, fontWeight: 400 }}>
      <span style={{ color: C.muted }}>Our cost shown:</span>
      {box(b.pst, (v) => onBasisChange && onBasisChange({ pst: v }), "+ PST", "Add each line's own PST % to the cost figures. PST is real money we pay, so it is what the quote's margin already uses.")}
      {box(b.gst, (v) => onBasisChange && onBasisChange({ gst: v }), "+ GST 5%", "Add 5% GST so the figure matches what a supplier invoice will total. GST comes back to us as an input tax credit, so it never changes profit or margin.")}
      {!b.pst && !b.gst ? <span style={{ color: C.muted, fontStyle: "italic" }}>ex tax — same basis a supplier quotes</span> : null}
    </span>
  );
  // Round BEFORE formatting, the way recomputeTotals stores its totals
  // (+mat_cost.toFixed(2)). toFixed and toLocaleString disagree on a value
  // sitting exactly on a half-cent, so formatting the raw float made this
  // footer read a cent off the totals card six inches below it
  // (30.5 m @ $1.19 + 7% PST → card $38.73, footer $38.74).
  const money = (n) => fmt$(+Number(n || 0).toFixed(2));
  // `note` sits INSIDE the count phrase, never beside it: "(+N hidden)" next to
  // a count that already included those N read as N more lines than there were.
  const row = (label, t, count, note, primary, withToggles) => (
    <tr style={{ background: primary ? "rgba(165,206,58,0.10)" : "#11141a", borderTop: `1px solid ${C.border}` }}>
      <td colSpan={MAT_COLSPAN} style={{ padding: "8px 10px", fontSize: 12, fontWeight: primary ? 700 : 500, color: C.muted }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", justifyContent: withToggles ? "space-between" : "flex-end" }}>
          {withToggles ? toggles : null}
          <span style={{ textAlign: "right" }}>
            <span style={{ color: primary ? C.accent : C.muted }}>{label}</span>
            {count != null ? <span style={{ color: C.muted, fontWeight: 400 }}> · {count} line{count === 1 ? "" : "s"}{note ? ` (${note})` : ""}</span> : null}
            {` — our cost (${taxBasisLabel(b)}) `}<span style={{ color: C.text, fontWeight: 700 }}>{money(t.cost)}</span>
            {" · client "}<span style={{ color: C.accent, fontWeight: 700 }}>{money(t.client)}</span>
            {" · profit "}<span style={{ color: t.client - t.costPst >= 0 ? C.good : C.danger, fontWeight: 700 }}>{money(t.client - t.costPst)}</span>
            {t.client > 0 ? <span style={{ color: C.muted, fontWeight: 400 }}> ({marginPct(t).toFixed(0)}% margin{marginBasisNote(t) ? `, ${marginBasisNote(t)}` : ""})</span> : null}
          </span>
        </div>
      </td>
    </tr>
  );
  return (
    <tfoot>
      {narrowed && row("✓ Selected", selT, pickedCount, hiddenLotLines ? `${hiddenLotLines} hidden in a lot` : null, true, false)}
      {row(narrowed ? "All materials" : "Materials total", allT, lineCount, null, !narrowed, true)}
    </tfoot>
  );
}

function th(width) {
  return {
    textAlign: "left",
    padding: "6px 8px",
    fontWeight: 600,
    width: width === "auto" ? undefined : width,
    minWidth: width === "auto" ? undefined : width,
  };
}

function td() {
  return {
    padding: "6px 8px",
    verticalAlign: "middle",
  };
}
